// Canonical commodity tree: TACT groups → sub-groups → commodities. Customers see `label`.
// Source of truth: edit this file directly (seeded once from the old commodity list and the airline sheets — see plans/2026-10-commodity-catalog/plan.md).

import { CargoType, CargoTypeSub } from '@prisma/client';

import type { CargoAvailability, CommodityNode } from './types';

/** Seeded from today's EK product visibility; Valuable added to the general profiles. */
export const CARGO_PROFILES = {
  GENERAL: [
    { cargoType: null, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.GeneralCargo, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.DangerousGoods, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.Perishable, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.Valuable },
  ],
  PERISHABLE: [
    { cargoType: CargoType.Perishable, cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA] },
    { cargoType: CargoType.Pharma, cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA] },
  ],
  PHARMA: [
    { cargoType: CargoType.Perishable, cargoTypeSubs: [CargoTypeSub.PHARMA] },
    { cargoType: CargoType.Pharma, cargoTypeSubs: [CargoTypeSub.PHARMA] },
  ],
  GENERAL_AND_PERISHABLE: [
    { cargoType: null, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.GeneralCargo, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.DangerousGoods, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    {
      cargoType: CargoType.Perishable,
      cargoTypeSubs: [CargoTypeSub.GENERAL, CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
    { cargoType: CargoType.Pharma, cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA] },
    { cargoType: CargoType.Valuable },
  ],
  GENERAL_AND_PHARMA: [
    { cargoType: null, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.GeneralCargo, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.DangerousGoods, cargoTypeSubs: [CargoTypeSub.GENERAL] },
    { cargoType: CargoType.Perishable, cargoTypeSubs: [CargoTypeSub.GENERAL, CargoTypeSub.PHARMA] },
    { cargoType: CargoType.Pharma, cargoTypeSubs: [CargoTypeSub.PHARMA] },
    { cargoType: CargoType.Valuable },
  ],
} satisfies Record<string, CargoAvailability[]>;

export const COMMODITY_TREE: CommodityNode[] = [
  {
    id: 'tact_g0',
    level: 'GROUP',
    range: [1, 999],
    label: 'Edible animal and vegetable products',
    description: 'Edible animal and vegetable products',
    isSelectable: false,
  },
  {
    id: 'tact_s0001',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [1, 99],
    label: 'Foodstuff, spices, beverages',
    description: 'Foodstuff, spices, beverages',
    isSelectable: false,
  },
  {
    id: 'cmd_fruit_dry_fresh',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0002',
    label: 'Fruit (Fresh or Dried)',
    description: 'FRUIT  DRY,FRESH',
    aliases: ['FRUIT  EXCLUDING MELONS', 'FRUIT  FRESH'],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_apples', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Apples', description: 'APPLES' }, // added from the TK list
  { id: 'cmd_apricots', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Apricots', description: 'APRICOTS' }, // added from the TK list
  { id: 'cmd_durian', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Durian', description: 'DURIAN' }, // added from the TK list
  { id: 'cmd_figs', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Figs', description: 'FIGS' }, // added from the TK list
  {
    id: 'cmd_frozen_fruit',
    parentId: 'cmd_fruit_dry_fresh',
    level: 'ITEM',
    label: 'Frozen Fruit',
    description: 'FROZEN FRUIT',
  }, // added from the TK list
  {
    id: 'cmd_nectarines',
    parentId: 'cmd_fruit_dry_fresh',
    level: 'ITEM',
    label: 'Nectarines',
    description: 'NECTARINES',
  }, // added from the TK list
  {
    id: 'cmd_passion_fruit',
    parentId: 'cmd_fruit_dry_fresh',
    level: 'ITEM',
    label: 'Passion Fruit',
    description: 'PASSION FRUIT',
  }, // added from the TK list
  { id: 'cmd_peaches', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Peaches', description: 'PEACHES' }, // added from the TK list
  { id: 'cmd_pears', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Pears', description: 'PEARS' }, // added from the TK list
  { id: 'cmd_plums', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Plums', description: 'PLUMS' }, // added from the TK list
  {
    id: 'cmd_pomegranates',
    parentId: 'cmd_fruit_dry_fresh',
    level: 'ITEM',
    label: 'Pomegranates',
    description: 'POMEGRANATES',
  }, // added from the TK list
  { id: 'cmd_quinces', parentId: 'cmd_fruit_dry_fresh', level: 'ITEM', label: 'Quinces', description: 'QUINCES' }, // added from the TK list
  {
    id: 'cmd_foodstuffs_spices_beverages_frozen',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0005',
    label: 'Foodstuffs, Spices, Beverages (Frozen)',
    description: 'FOODSTUFFS, SPICES, BEVERAGES -FROZEN-',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  }, // added from the TK list
  {
    id: 'cmd_foodstuffs_spices_beverages',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0006',
    label: 'Foodstuffs, Spices, Beverages',
    description: 'FOODSTUFFS, SPICES, BEVERAGES',
    aliases: [
      'FOODSTUFF EXCL MEAT,FISH,SEAFOOD SPICES,BEVERAGES',
      'FOODSTUFF EXCL CAVAIR,HATCHING EGGS ,SPICES AND BEVERAGES.',
      'FOODSTUFFS EXCL STRAWBERRIES ,SPICES,BEVERAGES',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_rennet_powder',
    parentId: 'cmd_foodstuffs_spices_beverages',
    level: 'ITEM',
    label: 'Rennet Powder',
    description: 'RENNET POWDER',
  }, // added from the TK list
  { id: 'cmd_salt', parentId: 'cmd_foodstuffs_spices_beverages', level: 'ITEM', label: 'Salt', description: 'SALT' }, // added from the TK list
  { id: 'cmd_sugar', parentId: 'cmd_foodstuffs_spices_beverages', level: 'ITEM', label: 'Sugar', description: 'SUGAR' }, // added from the TK list
  {
    id: 'cmd_fruits_vegetables',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0007',
    label: 'Fruits, Vegetables',
    description: 'FRUITS, VEGETABLES',
    aliases: [
      '****FRUITS AND VEGETABLES EXCL BANANAS',
      'FRUITS EXCL STRAWBERRIES VEGETABLES',
      '****FRUITS,VEGETABLES-FRESH-',
    ],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_infant_food',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0010',
    label: 'Infant Food',
    description: 'INFANT FOOD',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_foodstuff_beverages_destined_to_embassy_counsulate_legation',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0011',
    label: 'Foodstuffs, Beverages for Embassies, Consulates, Legations',
    description: 'FOODSTUFF,BEVERAGES-DESTINED TO EMBASSY/COUNSULATE/LEGATION',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_tubers_earth_nuts',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0017',
    label: 'Tubers, Earth Nuts',
    description: 'TUBERS,EARTH NUTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_potatoes', parentId: 'cmd_tubers_earth_nuts', level: 'ITEM', label: 'Potatoes', description: 'POTATOES' }, // added from the TK list
  {
    id: 'cmd_sweet_potatoes',
    parentId: 'cmd_tubers_earth_nuts',
    level: 'ITEM',
    label: 'Sweet Potatoes',
    description: 'SWEET POTATOES',
  }, // added from the TK list
  {
    id: 'cmd_asparagus_maracuya',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0018',
    label: 'Asparagus, Maracuya',
    description: 'ASPARAGUS,MARACUYA',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_nuts',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0020',
    label: 'Nuts',
    description: 'NUTS',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_hot_peppers_eggplants',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0023',
    label: 'Hot Peppers, Eggplants',
    description: 'HOT PEPPERS,EGGPLANTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_avocados_pineapples_papaya_melons_tamarind_chayote',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0024',
    label: 'Avocados, Pineapples, Papaya, Melons, Tamarind',
    description: 'AVOCADOS,PINEAPPLES,PAPAYA,MELONS,TAMARIND,CHAYOTE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_avocados_pineapples',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0025',
    label: 'Avocados, Pineapples',
    description: 'AVOCADOS,PINEAPPLES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mangoes_melons_pineapples',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0027',
    label: 'Mangoes, Melons, Pineapples',
    description: 'MANGOES, MELONS, PINEAPPLES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_melons_persimmons_citrus_fruits',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0029',
    label: 'Melons, Persimmons, Citrus Fruits',
    description: 'MELONS,PERSIMMONS,CITRUS FRUITS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mandarins',
    parentId: 'cmd_melons_persimmons_citrus_fruits',
    level: 'ITEM',
    label: 'Mandarins',
    description: 'MANDARINS',
  }, // added from the TK list
  {
    id: 'cmd_oranges',
    parentId: 'cmd_melons_persimmons_citrus_fruits',
    level: 'ITEM',
    label: 'Oranges',
    description: 'ORANGES',
  }, // added from the TK list
  {
    id: 'cmd_tangerines',
    parentId: 'cmd_melons_persimmons_citrus_fruits',
    level: 'ITEM',
    label: 'Tangerines',
    description: 'TANGERINES',
  }, // added from the TK list
  {
    id: 'cmd_beans_corn_capsicum',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0030',
    label: 'Beans, Corn, Capsicum',
    description: 'BEANS,CORN,CAPSICUM',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_kola_nuts',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0037',
    label: 'Kola Nuts',
    description: 'KOLA NUTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_candy',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0055',
    label: 'Candy',
    description: 'CANDY',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_chocolates_manufactures_candies_wafers_biscuits',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0059',
    label: 'Chocolates, Candies, Wafers, Biscuits',
    description: 'CHOCOLATES MANUFACTURES,CANDIES,WAFERS,BISCUITS',
    aliases: ['CHOCOLATE MANUFACTURES'],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_confectionary_desserts',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0060',
    label: 'Confectionery, Desserts',
    description: 'CONFECTIONARY,DESSERTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_chewing_gum',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0070',
    label: 'Chewing Gum',
    description: 'CHEWING-GUM',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_honey',
    parentId: 'tact_s0001',
    level: 'ITEM',
    tactCode: '0075',
    label: 'Honey',
    description: 'HONEY',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'tact_s0100',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [100, 199],
    label: 'Beverages, coffee, tea',
    description: 'Beverages, coffee, tea',
    isSelectable: false,
  },
  {
    id: 'cmd_beverages',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0110',
    label: 'Beverages',
    description: 'BEVERAGES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mineral_water',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0113',
    label: 'Mineral Water',
    description: 'MINERAL WATER',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_spirits_liquors_wines_beers',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0129',
    label: 'Spirits, Liquors, Wines, Beers',
    description: 'SPIRITS,LIQUORS,WINES,BEERS',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_wines',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0130',
    label: 'Wines',
    description: 'WINES',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_coffee',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0180',
    label: 'Coffee',
    description: 'COFFEE',
    aliases: ['COFFEE ROASTED,GROUND'],
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_tea',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0185',
    label: 'Tea',
    description: 'TEA',
    aliases: ['TEA INSTANT'],
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_beverages_syrups_flavours',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0190',
    label: 'Beverages, Syrups, Flavors',
    description: 'BEVERAGES,SYRUPS,FLAVOURS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_fruit_juices',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0192',
    label: 'Fruit Juices',
    description: 'FRUIT JUICES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_extracts_flavouring_compound',
    parentId: 'tact_s0100',
    level: 'ITEM',
    tactCode: '0194',
    label: 'Extracts, Flavoring Compound',
    description: 'EXTRACTS, FLAVOURING COMPOUND',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'tact_s0200',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [200, 299],
    label: 'Dairy products, eggs, ice cream',
    description: 'Dairy products, eggs, ice cream',
    isSelectable: false,
  },
  {
    id: 'cmd_dairy_products_eggs',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0200',
    label: 'Dairy Products, Eggs',
    description: 'DAIRY PRODUCTS,EGGS',
    aliases: ['DAIRY PRODUCTS EXCL EGGS'],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_cream', parentId: 'cmd_dairy_products_eggs', level: 'ITEM', label: 'Cream', description: 'CREAM' }, // added from the TK list
  { id: 'cmd_yoghurt', parentId: 'cmd_dairy_products_eggs', level: 'ITEM', label: 'Yogurt', description: 'YOGHURT' }, // added from the TK list
  {
    id: 'cmd_milk_fresh',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0203',
    label: 'Fresh Milk',
    description: 'MILK FRESH',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_butter',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0210',
    label: 'Butter',
    description: 'BUTTER',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_cheese',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0220',
    label: 'Cheese',
    description: 'CHEESE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_eggs',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0230',
    label: 'Eggs',
    description: 'EGGS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_ice_cream_frozen_confection',
    parentId: 'tact_s0200',
    level: 'ITEM',
    tactCode: '0246',
    label: 'Ice Cream, Frozen Confectionery',
    description: 'ICE CREAM,FROZEN CONFECTION',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0300',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [300, 399],
    label: 'Fish and seafood, frogs',
    description: 'Fish and seafood, frogs',
    isSelectable: false,
  },
  {
    id: 'cmd_fish_edible_seafood',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0300',
    label: 'Fish (Edible), Seafood',
    description: 'FISH (EDIBLE), SEAFOOD',
    aliases: [
      'FISH EDIBLE',
      'FISH,CRABS,SHRIMPS-EDIBLE',
      'FISH EDIBLE/EXCL.LOBSTERS,SCALLOPS',
      'FISH  EDIBLE ,SEAFOOD  EXCL CAVIAR',
      'FISH, FISH PRODUCTS',
      '*****SEAFOOD',
    ],
    sortPosition: 5,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_sea_urchin_processed',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0304',
    label: 'Processed Sea Urchin',
    description: 'SEA URCHIN/PROCESSED',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_sea_urchin',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0305',
    label: 'Sea Urchin',
    description: 'SEA-URCHIN',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_fish_processed',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0307',
    label: 'Processed Fish',
    description: 'FISH PROCESSED',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_dried_fish',
    parentId: 'cmd_fish_processed',
    level: 'ITEM',
    label: 'Dried Fish',
    description: 'DRIED FISH',
  }, // added from the TK list
  {
    id: 'cmd_fish_smoked',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0308',
    label: 'Fish, Smoked',
    description: 'FISH SMOKED',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_shellfish',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0309',
    label: 'Shellfish',
    description: 'SHELLFISH',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_clams_oysters_scallops',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0310',
    label: 'Clams, Oysters, Scallops',
    description: 'CLAMS,OYSTERS,SCALLOPS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_cockles',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0311',
    label: 'Cockles',
    description: '*****COCKLES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_abalone',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0314',
    label: 'Abalone',
    description: 'ABALONE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_crabs_crawfish_lobsters',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0315',
    label: 'Crabs, Crawfish, Lobsters',
    description: 'CRABS,CRAWFISH,LOBSTERS',
    sortPosition: 4,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_crabs',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0316',
    label: 'Crabs',
    description: 'CRABS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_tilapia',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0318',
    label: 'Tilapia',
    description: 'TILAPIA',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mahi_mahi',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0319',
    label: 'Mahi-Mahi',
    description: 'MAHI MAHI',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_eel',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0320',
    label: 'Eel',
    description: 'EEL',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_tuna_fresh',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0323',
    label: 'Tuna (Fresh)',
    description: 'TUNA  FRESH',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_fish_roe',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0330',
    label: 'Fish Roe',
    description: 'FISH ROE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_caviar', parentId: 'cmd_fish_roe', level: 'ITEM', label: 'Caviar', description: 'CAVIAR' }, // added from the TK list
  {
    id: 'cmd_fish_gut_dried',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0334',
    label: 'Dried Fish Gut',
    description: 'FISH GUT  DRIED',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_frogs',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0335',
    label: 'Frogs',
    description: 'FROGS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_salmon',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0340',
    label: 'Salmon',
    description: 'SALMON',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_snapper',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0355',
    label: 'Snapper',
    description: 'SNAPPER',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_shark_fins',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0370',
    label: 'Shark Fins',
    description: 'SHARK FINS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_shrimps_prawns',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0380',
    label: 'Shrimps, Prawns',
    description: 'SHRIMPS, PRAWNS',
    aliases: ['*****SHRIMPS AND/OR PRAWNS'],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_shrimps_prawns_lobsters',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0384',
    label: 'Shrimps, Prawns, Lobsters',
    description: 'SHRIMPS, PRAWNS, LOBSTERS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_turtles',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0385',
    label: 'Turtles',
    description: 'TURTLES',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_lobsters',
    parentId: 'tact_s0300',
    level: 'ITEM',
    tactCode: '0386',
    label: 'Lobsters',
    description: 'LOBSTERS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0400',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [400, 499],
    label: 'Fruits, berries, melons',
    description: 'Fruits, berries, melons',
    isSelectable: false,
  },
  {
    id: 'cmd_berries_melons',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0400',
    label: 'Berries, Melons',
    description: 'BERRIES, MELONS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_melons', parentId: 'cmd_berries_melons', level: 'ITEM', label: 'Melons', description: 'MELONS' }, // added from the TK list
  {
    id: 'cmd_watermelons',
    parentId: 'cmd_berries_melons',
    level: 'ITEM',
    label: 'Watermelons',
    description: 'WATERMELONS',
  }, // added from the TK list
  {
    id: 'cmd_berries',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0401',
    label: 'Berries',
    description: 'BERRIES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_bananas',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0420',
    label: 'Bananas',
    description: 'BANANAS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_limes_lemons',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0422',
    label: 'Limes, Lemons',
    description: 'LIMES, LEMONS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_grapes',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0445',
    label: 'Grapes',
    description: 'GRAPES',
    sortPosition: 8,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_kiwifruit',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0456',
    label: 'Kiwifruit',
    description: 'KIWIFRUIT',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mangoes',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0460',
    label: 'Mangoes',
    description: 'MANGOES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_dates',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0461',
    label: 'Dates',
    description: 'DATES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_olives',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0462',
    label: 'Olives',
    description: 'OLIVES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_cherries',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0477',
    label: 'Cherries',
    description: 'CHERRIES',
    sortPosition: 7,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pineapples',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0485',
    label: 'Pineapples',
    description: 'PINEAPPLES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_papayas',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0486',
    label: 'Papayas',
    description: 'PAPAYAS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_papayas_pineapples',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0487',
    label: 'Papayas, Pineapples',
    description: 'PAPAYAS, PINEAPPLES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_raspberries',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0494',
    label: 'Raspberries',
    description: 'RASPBERRIES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_strawberries',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0495',
    label: 'Strawberries',
    description: 'STRAWBERRIES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_indian_sweetmeats',
    parentId: 'tact_s0400',
    level: 'ITEM',
    tactCode: '0497',
    label: 'Indian Sweetmeats',
    description: 'INDIAN SWEETMEATS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0500',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [500, 599],
    label: 'Grains and grain preparations, cereal foods',
    description: 'Grains and grain preparations, cereal foods',
    isSelectable: false,
  },
  {
    id: 'cmd_flour',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0509',
    label: 'Flour',
    description: 'FLOUR',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_bakery_products',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0510',
    label: 'Bakery Products',
    description: 'BAKERY PRODUCTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pastry_dough',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0514',
    label: 'Pastry Dough',
    description: 'PASTRY DOUGH',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_bread',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0515',
    label: 'Bread',
    description: 'BREAD',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_fruit_cakes',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0521',
    label: 'Fruit Cakes',
    description: 'FRUIT CAKES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_cereal_foods_ready_to_eat',
    parentId: 'tact_s0500',
    level: 'ITEM',
    tactCode: '0552',
    label: 'Cereal Foods Ready to Eat',
    description: 'CEREAL FOODS  READY TO EAT',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0600',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [600, 699],
    label: 'Meat, slaughtered poultry and game, sausages, meat pies',
    description: 'Meat, slaughtered poultry and game, sausages, meat pies',
    isSelectable: false,
  },
  {
    id: 'cmd_meat_including_slaughtered_poultry',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0600',
    label: 'Meat (Fresh or Frozen, incl. Poultry)',
    description: 'MEAT INCLUDING SLAUGHTERED POULTRY',
    aliases: [
      'MEAT  EXCL SLAUGHTERED POULTRY, GAME',
      '*****MEAT FRESH-FROZEDN INCL HORSE MEAT AND POULTRY',
      'MEAT  FRESH OR FROZEN',
    ],
    sortPosition: 9,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_meat_sausages_bologna_frankfurters',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0602',
    label: 'Meat, Sausages, Bologna, Frankfurters',
    description: 'MEAT, SAUSAGES, BOLOGNA, FRANKFURTERS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_meat_seafood',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0603',
    label: 'Meat, Seafood',
    description: 'MEAT, SEAFOOD',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pate_de_foie_gras',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0606',
    label: 'Pâté de Foie Gras',
    description: 'PATE DE FOIE GRAS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_lamb_mutton',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0630',
    label: 'Lamb, Mutton',
    description: 'LAMB, MUTTON',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_poultry_game',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0640',
    label: 'Poultry, Game',
    description: 'POULTRY, GAME',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_liver_raw',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0645',
    label: 'Raw Liver',
    description: 'LIVER  RAW',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pork_chicken_chilled_or_frozen',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0651',
    label: 'Pork, Chicken (Chilled or Frozen)',
    description: 'PORK, CHICKEN - CHILLED OR FROZEN',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_bacon',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0655',
    label: 'Bacon',
    description: 'BACON',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_horse_meat',
    parentId: 'tact_s0600',
    level: 'ITEM',
    tactCode: '0670',
    label: 'Horse Meat',
    description: 'HORSE MEAT',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0700',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [700, 799],
    label: 'Roots and spices',
    description: 'Roots and spices',
    isSelectable: false,
  },
  {
    id: 'cmd_roots_and_spices',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0700',
    label: 'Roots and Spices',
    description: 'ROOTS AND SPICES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_turmeric', parentId: 'cmd_roots_and_spices', level: 'ITEM', label: 'Turmeric', description: 'TURMERIC' }, // added from the TK list
  {
    id: 'cmd_spices_spice_extracts_excluding_saffron',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0702',
    label: 'Spices, Spice Extracts (excl. Saffron)',
    description: 'SPICES, SPICE EXTRACTS EXCLUDING SAFFRON',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_vanilla',
    parentId: 'cmd_spices_spice_extracts_excluding_saffron',
    level: 'ITEM',
    label: 'Vanilla',
    description: 'VANILLA',
  }, // added from the TK list
  {
    id: 'cmd_cardamom',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0728',
    label: 'Cardamom',
    description: 'CARDAMOM',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_asafoetida',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0737',
    label: 'Asafoetida',
    description: 'ASAFOETIDA',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_birdeye_chillies_dried',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0750',
    label: "Bird's Eye Chilies (Dried)",
    description: 'BIRDEYE CHILLIES  DRIED',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_ginger',
    parentId: 'tact_s0700',
    level: 'ITEM',
    tactCode: '0751',
    label: 'Ginger',
    description: 'GINGER',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0800',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [800, 899],
    label: 'Vegetables, soup, yeast',
    description: 'Vegetables, soup, yeast',
    isSelectable: false,
  },
  {
    id: 'cmd_vegetables',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0800',
    label: 'Vegetables',
    description: 'VEGETABLES',
    sortPosition: 1,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_beets', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Beets', description: 'BEETS' }, // added from the TK list
  {
    id: 'cmd_brussels_sprouts',
    parentId: 'cmd_vegetables',
    level: 'ITEM',
    label: 'Brussels Sprouts',
    description: 'BRUSSELS SPROUTS',
  }, // added from the TK list
  { id: 'cmd_cabbage', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Cabbage', description: 'CABBAGE' }, // added from the TK list
  { id: 'cmd_carrots', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Carrots', description: 'CARROTS' }, // added from the TK list
  {
    id: 'cmd_cauliflower',
    parentId: 'cmd_vegetables',
    level: 'ITEM',
    label: 'Cauliflower',
    description: 'CAULIFLOWER',
  }, // added from the TK list
  { id: 'cmd_cress', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Cress', description: 'CRESS' }, // added from the TK list
  { id: 'cmd_cucumbers', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Cucumbers', description: 'CUCUMBERS' }, // added from the TK list
  { id: 'cmd_garlic', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Garlic', description: 'GARLIC' }, // added from the TK list
  { id: 'cmd_okra', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Okra', description: 'OKRA' }, // added from the TK list
  { id: 'cmd_onions', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Onions', description: 'ONIONS' }, // added from the TK list
  { id: 'cmd_pumpkins', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Pumpkins', description: 'PUMPKINS' }, // added from the TK list
  { id: 'cmd_radishes', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Radishes', description: 'RADISHES' }, // added from the TK list
  { id: 'cmd_spinach', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Spinach', description: 'SPINACH' }, // added from the TK list
  { id: 'cmd_turnips', parentId: 'cmd_vegetables', level: 'ITEM', label: 'Turnips', description: 'TURNIPS' }, // added from the TK list
  {
    id: 'cmd_artichokes',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0801',
    label: 'Artichokes',
    description: 'ARTICHOKES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_broccoli',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0802',
    label: 'Broccoli',
    description: 'BROCCOLI',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_lettuce',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0803',
    label: 'Lettuce',
    description: 'LETTUCE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_asparagus',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0805',
    label: 'Asparagus',
    description: 'ASPARAGUS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_yams_cocoyams_plaintains',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0807',
    label: 'Yams, Cocoyams',
    description: 'YAMS, COCOYAMS, PLAINTAINS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_beans',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0810',
    label: 'Beans',
    description: 'BEANS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_endives_celery',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0838',
    label: 'Endives, Celery',
    description: 'ENDIVES, CELERY',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_mushrooms_mushroom_spawn',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0850',
    label: 'Mushrooms, Mushroom Products',
    description: 'MUSHROOMS, MUSHROOM SPAWN',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_taros',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0866',
    label: 'Taro',
    description: 'TAROS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_tomatoes',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0880',
    label: 'Tomatoes',
    description: 'TOMATOES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_soups',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0885',
    label: 'Soups',
    description: 'SOUPS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_yeast_inactive',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0898',
    label: 'Yeast (Inactive)',
    description: '*****YEAST - INACTIVE',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_yeast',
    parentId: 'tact_s0800',
    level: 'ITEM',
    tactCode: '0899',
    label: 'Yeast',
    description: 'YEAST',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s0900',
    parentId: 'tact_g0',
    level: 'SUB_GROUP',
    range: [900, 999],
    label: 'Miscellaneous edible animal and vegetable products which could not be classified between 0001-0899',
    description: 'Miscellaneous edible animal and vegetable products which could not be classified between 0001-0899',
    isSelectable: false,
  },
  {
    id: 'cmd_cooking_fat',
    parentId: 'tact_s0900',
    level: 'ITEM',
    tactCode: '0901',
    label: 'Cooking Fat',
    description: 'COOKING FAT',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pet_food',
    parentId: 'tact_s0900',
    level: 'ITEM',
    tactCode: '0902',
    label: 'Pet Food',
    description: 'PET FOOD',
    sortPosition: 15,
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_vitamins_for_animal_food',
    parentId: 'tact_s0900',
    level: 'ITEM',
    tactCode: '0986',
    label: 'Vitamins for Animal Food',
    description: 'VITAMINS FOR ANIMAL FOOD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g1',
    level: 'GROUP',
    range: [1000, 1999],
    label: 'Live animals and inedible animal and vegetable products',
    description: 'Live animals and inedible animal and vegetable products',
    isSelectable: false,
  },
  {
    id: 'tact_s1000',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1000, 1099],
    label: 'Animals, live',
    description: 'Animals, live',
    isSelectable: false,
  },
  {
    id: 'cmd_cats',
    parentId: 'tact_s1000',
    level: 'ITEM',
    label: 'Cats',
    description: 'CATS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_hairless_cats',
    parentId: 'cmd_cats',
    level: 'ITEM',
    label: 'Hairless Cats',
    description: 'HAIRLESS CATS',
  }, // added from the TK list
  {
    id: 'cmd_snub_nosed_cats',
    parentId: 'cmd_cats',
    level: 'ITEM',
    label: 'Snub-Nosed Cats',
    description: 'SNUB-NOSED CATS',
  }, // added from the TK list
  {
    id: 'cmd_dogs',
    parentId: 'tact_s1000',
    level: 'ITEM',
    label: 'Dogs',
    description: 'DOGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_hairless_dogs',
    parentId: 'cmd_dogs',
    level: 'ITEM',
    label: 'Hairless Dogs',
    description: 'HAIRLESS DOGS',
  }, // added from the TK list
  {
    id: 'cmd_k9_certified_dogs',
    parentId: 'cmd_dogs',
    level: 'ITEM',
    label: 'K9 Certified Dogs',
    description: 'K9 CERTIFIED DOGS',
  }, // added from the TK list
  {
    id: 'cmd_snub_nosed_dogs',
    parentId: 'cmd_dogs',
    level: 'ITEM',
    label: 'Snub-Nosed Dogs',
    description: 'SNUB-NOSED DOGS',
  }, // added from the TK list
  {
    id: 'cmd_live_birds',
    parentId: 'tact_s1000',
    level: 'ITEM',
    label: 'Live Birds',
    description: 'LIVE BIRDS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  { id: 'cmd_parrots', parentId: 'cmd_live_birds', level: 'ITEM', label: 'Parrots', description: 'PARROTS' }, // added from the TK list
  { id: 'cmd_pigeons', parentId: 'cmd_live_birds', level: 'ITEM', label: 'Pigeons', description: 'PIGEONS' }, // added from the TK list
  {
    id: 'cmd_livestock',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1000',
    label: 'Livestock',
    description: 'LIVESTOCK',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_live_animals_warm_blooded',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1001',
    label: 'Live Animals, Warm-Blooded',
    description: 'LIVE ANIMALS, WARM-BLOODED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_armadillos',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Armadillos',
    description: 'ARMADILLOS',
  }, // added from the TK list
  {
    id: 'cmd_beavers',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Beavers',
    description: 'BEAVERS',
  }, // added from the TK list
  {
    id: 'cmd_chimpanzees',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Chimpanzees',
    description: 'CHIMPANZEES',
  }, // added from the TK list
  {
    id: 'cmd_chinchillas',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Chinchillas',
    description: 'CHINCHILLAS',
  }, // added from the TK list
  {
    id: 'cmd_dolphins',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Dolphins',
    description: 'DOLPHINS',
  }, // added from the TK list
  {
    id: 'cmd_ferrets',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Ferrets',
    description: 'FERRETS',
  }, // added from the TK list
  {
    id: 'cmd_gorillas',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Gorillas',
    description: 'GORILLAS',
  }, // added from the TK list
  {
    id: 'cmd_guinea_pigs',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Guinea Pigs',
    description: 'GUINEA PIGS',
  }, // added from the TK list
  {
    id: 'cmd_hamsters',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Hamsters',
    description: 'HAMSTERS',
  }, // added from the TK list
  {
    id: 'cmd_laboratory_animals',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Laboratory Animals',
    description: 'LABORATORY ANIMALS',
  }, // added from the TK list
  { id: 'cmd_mice', parentId: 'cmd_live_animals_warm_blooded', level: 'ITEM', label: 'Mice', description: 'MICE' }, // added from the TK list
  { id: 'cmd_mink', parentId: 'cmd_live_animals_warm_blooded', level: 'ITEM', label: 'Mink', description: 'MINK' }, // added from the TK list
  {
    id: 'cmd_monkeys',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Monkeys',
    description: 'MONKEYS',
  }, // added from the TK list
  {
    id: 'cmd_rabbits',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Rabbits',
    description: 'RABBITS',
  }, // added from the TK list
  { id: 'cmd_rats', parentId: 'cmd_live_animals_warm_blooded', level: 'ITEM', label: 'Rats', description: 'RATS' }, // added from the TK list
  {
    id: 'cmd_squirrels',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Squirrels',
    description: 'SQUIRRELS',
  }, // added from the TK list
  {
    id: 'cmd_whales',
    parentId: 'cmd_live_animals_warm_blooded',
    level: 'ITEM',
    label: 'Whales',
    description: 'WHALES',
  }, // added from the TK list
  {
    id: 'cmd_live_animals_cold_blooded',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1002',
    label: 'Live Animals, Cold-Blooded',
    description: 'LIVE ANIMALS, COLD-BLOODED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_amphibians',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Amphibians',
    description: 'AMPHIBIANS',
  }, // added from the TK list
  {
    id: 'cmd_insects',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Insects',
    description: 'INSECTS',
  }, // added from the TK list
  {
    id: 'cmd_ladybirds',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Ladybirds',
    description: 'LADYBIRDS',
  }, // added from the TK list
  {
    id: 'cmd_larvae',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Larvae',
    description: 'LARVAE',
  }, // added from the TK list
  {
    id: 'cmd_leeches',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Leeches',
    description: 'LEECHES',
  }, // added from the TK list
  {
    id: 'cmd_scorpions',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Scorpions',
    description: 'SCORPIONS',
  }, // added from the TK list
  {
    id: 'cmd_water_snakes',
    parentId: 'cmd_live_animals_cold_blooded',
    level: 'ITEM',
    label: 'Water Snakes',
    description: 'WATER SNAKES',
  }, // added from the TK list
  {
    id: 'cmd_deer',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1022',
    label: 'Deer',
    description: 'DEER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_live_aquatic_plants_fish_live_inedible_coral_weed_fish_food',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1024',
    label: 'Live Aquatic Plants, Live Inedible Fish, Coral, Fish Food',
    description: 'LIVE AQUATIC PLANTS, FISH (LIVE, INEDIBLE), CORAL, WEED, FISH FOOD',
    aliases: ['LIVE AQUATIC PLANTS, CORAL', 'LIVE AQUATIC PLANTS, FISH (INEDIBLE), CORAL, WEED, FISH FOOD'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fish_live_inedible',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1026',
    label: 'Fish, Live, Inedible',
    description: '***FISH, LIVE, INEDIBLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hares',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1040',
    label: 'Hares',
    description: 'HARES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_horses',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1050',
    label: 'Horses',
    description: 'HORSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_equine_equipment',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1051',
    label: 'Equine Equipment',
    description: 'Equine Equipment',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sheep',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1052',
    label: 'Sheep',
    description: 'SHEEP',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cattle',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1053',
    label: 'Cattle',
    description: 'CATTLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_goats',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1054',
    label: 'Goats',
    description: 'GOATS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tubifex_worms',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1059',
    label: 'Tubifex Worms',
    description: 'TUBIFEX WORMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bees',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1061',
    label: 'Bees',
    description: 'BEES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_turtles_live',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1072',
    label: 'Turtles, Live',
    description: 'TURTLES, LIVE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_pigs',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1074',
    label: 'Pigs',
    description: 'PIGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_poultry_live',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1080',
    label: 'Poultry, Live',
    description: 'POULTRY, LIVE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_day_old_chicks',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1081',
    label: 'Day-Old Chicks',
    description: 'DAY-OLD CHICKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_hatching_eggs',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1082',
    label: 'Hatching Eggs',
    description: 'HATCHING EGGS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_turkey_hatching_eggs',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1084',
    label: 'Turkey Hatching Eggs',
    description: 'TURKEY HATCHING EGGS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_snails',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1091',
    label: 'Snails',
    description: 'SNAILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_worms',
    parentId: 'tact_s1000',
    level: 'ITEM',
    tactCode: '1093',
    label: 'Worms',
    description: 'WORMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s1100',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1100, 1199],
    label: 'Furs, hides, pelts, skins, excluding wearing apparel',
    description: 'Furs, hides, pelts, skins, excluding wearing apparel',
    isSelectable: false,
  },
  {
    id: 'cmd_furs_hides_skins_excluding_wearing_apparel',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1100',
    label: 'Furs, Hides, Skins (excl. Wearing Apparel)',
    description: 'FURS, HIDES, SKINS EXCLUDING WEARING APPAREL',
    aliases: [
      'FURS,HIDES,SKINS-UNDRESED/INCL SEMI-FINISHED MFRS',
      'FURS, HIDES, SKINS EXCLUDING MINK, WEARING APPAREL',
      'HIDES,SKINS - INCLUDING PRODUCTS THEREOF',
      'HIDES,SKINS- UNDRESSED- FURS EXCL MFRS NUTRIA/REPTILE SKINS',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fur_plates_made_from_sewn_scraps',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1102',
    label: 'Fur Plates Made from Sewn Scraps',
    description: 'FUR PLATES  MADE FROM SEWN SCRAPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_furs_fur_wearing_apparel_skins',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1103',
    label: 'Furs, Fur Wearing Apparel, Skins',
    description: 'FURS, FUR WEARING APPAREL, SKINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_lamb_kid_sheep_goat_skins_excl_wearing_apparel',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1125',
    label: 'Lamb, Kid, Sheep and Goat Skins (excl. Wearing Apparel)',
    description: 'LAMB/KID/SHEEP/GOAT SKINS EXCL WEARING APPAREL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fur_scraps',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1150',
    label: 'Fur Scraps',
    description: 'FUR SCRAPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cattle_hides_sheep_skins_goat_skins',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1164',
    label: 'Cattle Hides, Sheep Skins, Goat Skins',
    description: 'CATTLE HIDES, SHEEP SKINS, GOAT SKINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_caracul_astrakhan_skins',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1188',
    label: 'Caracul, Astrakhan Skins',
    description: 'CARACUL, ASTRAKHAN SKINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_reptile_skins',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1190',
    label: 'Reptile Skins',
    description: 'REPTILE SKINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_crocodile_skins_wet_salted',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1194',
    label: 'Crocodile Skins, Wet/Salted',
    description: 'CROCODILE SKINS  WET/SALTED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_nutria_skins',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1196',
    label: 'Nutria Skins',
    description: 'NUTRIA SKINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_leather_hides_and_skins_excl_furs_and_pelts',
    parentId: 'tact_s1100',
    level: 'ITEM',
    tactCode: '1198',
    label: 'Leather, Hides, Skins (excl. Furs)',
    description: 'LEATHER,HIDES AND SKINS - EXCL. FURS AND PELTS',
    aliases: [
      'LEATHER TANNED,DYED,FINISHED,SEMI-FINISHED,CUT TO SHAPE',
      'LEATHER  EXCL NUTRIA/REPTILE SKIN MANUACTURES',
      'LEATHERHIDESANDSKINS EXCL FURSPELTSREPTILENUTRIAL SK',
      'LEATHER,HIDES,SKINS -EXCL.WEARING APPAREL,FURS',
      'LEATHER,HIDES,SKINS - TANNED, PROCESSED',
      'LEATHER  UNMANUFACTURED',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s1200',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1200, 1299],
    label: 'Leather, leather goods, skin rugs, artificial leather, excluding wearing apparel',
    description: 'Leather, leather goods, skin rugs, artificial leather, excluding wearing apparel',
    isSelectable: false,
  },
  {
    id: 'cmd_leather_leather_garments',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1200',
    label: 'Leather, Leather Garments',
    description: 'LEATHER, LEATHER GARMENTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_leather_leather_products',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1201',
    label: 'Leather, Leather Products',
    description: 'LEATHER, LEATHER PRODUCTS',
    aliases: ['LEATHER GOODS', 'LEATHER,LEATHER PRODUCTS EXCL. SPORTING GOODS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shoe_leather_alone_or_together_with_other_shoe_mfg_mtrls',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1206',
    label: 'Shoe Leather and Shoe Manufacturing Materials',
    description: 'SHOE LEATHER ALONE OR TOGETHER WITH OTHER SHOE MFG MTRLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tanned_and_or_processed_leather_synthetic_leather_cut_to',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1214',
    label: 'Tanned and/or Processed Leather, Synthetic Leather Cut to Shape',
    description: '*****TANNED AND/OR PROCESSED LEATHER, SYNTHETIC LEATHER CUT TO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_merino_lamb_skins',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1216',
    label: 'Merino Lamb Skins',
    description: 'MERINO LAMB SKINS',
    aliases: ['MERINO LAMB SKINS TANNEDDYED'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_artificial_leather',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1220',
    label: 'Artificial Leather',
    description: 'ARTIFICIAL LEATHER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_leather_bag_ball_belt_billfold_suitcase_harness_saddlery_poc',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1238',
    label: 'Leather Bags, Balls, Belts, Billfolds, Suitcases, Harnesses',
    description: 'LEATHER-BAG,BALL,BELT,BILLFOLD,SUITCASE,HARNESS,SADDLERY,POC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_car_seat_covers_rugs_made_of_hide_or_sheepskin',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1251',
    label: 'Car Seat Covers, Rugs (Hide or Sheepskin)',
    description: 'CAR SEAT COVERS,RUGS-MADE OF HIDE OR SHEEPSKIN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_leatherleather_goods_artificial_excl_wearing_apparel',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1272',
    label: 'Leather and Artificial Leather Goods (excl. Wearing Apparel)',
    description: 'LEATHERLEATHER GOODS ARTIFICIAL EXCL WEARING APPAREL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bags_belts_billfolds_brushes_clasps_sewing_kits_shaving_kits',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1295',
    label: 'Bags, Belts, Billfolds, Brushes, Clasps, Sewing Kits, Shaving Kits',
    description: 'BAGS,BELTS,BILLFOLDS,BRUSHES,CLASPS,SEWING KITS,SHAVING KITS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_manicure_cases',
    parentId: 'tact_s1200',
    level: 'ITEM',
    tactCode: '1296',
    label: 'Manicure Cases',
    description: 'MANICURE CASES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s1300',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1300, 1399],
    label: 'Naval stores, gums, resins',
    description: 'Naval stores, gums, resins',
    isSelectable: false,
  },
  {
    id: 'cmd_gum_arabic',
    parentId: 'tact_s1300',
    level: 'ITEM',
    tactCode: '1352',
    label: 'Gum Arabic',
    description: 'GUM ARABIC',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_frankincese_and_gums',
    parentId: 'tact_s1300',
    level: 'ITEM',
    tactCode: '1353',
    label: 'Frankincense and Gums',
    description: '*****FRANKINCESE AND GUMS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s1400',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1400, 1499],
    label: 'Floral and/or nursery stock and bulbs, flowers, seeds, tubers, plants, cuttings',
    description: 'Floral and/or nursery stock and bulbs, flowers, seeds, tubers, plants, cuttings',
    isSelectable: false,
  },
  {
    id: 'cmd_floral_nursery_stock_bulbs_flowers_seeds_tubers',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1400',
    label: 'Floral and Nursery Stock, Bulbs, Flowers, Seeds',
    description: 'FLORAL/NURSERY STOCK,BULBS,FLOWERS,SEEDS,TUBERS',
    aliases: [
      'FLORAL/NURSERY STOCK,BULBS,SEEDS,PLANTS,FLOWERS',
      'FLORAL,NURSERY STOCK,BULBS,SEEDS,PLANTS,FLOWERS',
      'FLORAL/NURSERY STOCK EXCL CUT FLOWERS ,BULBS,SEEDS,TUBERS',
      'PLANTS-FLWS,BLBS,SDS,FLORAL/NURSERY STKS,DPLY MATS EXPO USE',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_cut_flowers_foliage_cuttings',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1403',
    label: 'Cut Flowers, Foliage, Cuttings',
    description: 'CUT FLOWERS, FOLIAGE, CUTTINGS',
    aliases: ['CUT FLOWERS  EXCL ORCHIDS', '*****CUT FLOWERS,FOLIAGE, INCL MIMOSA'],
    sortPosition: 6,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_bulbs_tubers',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1409',
    label: 'Bulbs',
    description: 'BULBS, TUBERS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_rose_petals',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1411',
    label: 'Rose Petals',
    description: 'ROSE PETALS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_flowers',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1420',
    label: 'Flowers',
    description: 'FLOWERS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  { id: 'cmd_wreaths', parentId: 'cmd_flowers', level: 'ITEM', label: 'Wreaths', description: 'WREATHS' }, // added from the TK list
  {
    id: 'cmd_orchids',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1429',
    label: 'Orchids',
    description: 'ORCHIDS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_carnation_cuttings_chrysanthemum_cuttings_gladioli',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1436',
    label: 'Carnation Cuttings, Chrysanthemum Cuttings, Gladioli Cuttings, and Other Live Plants',
    description: 'CARNATION CUTTINGS, CHRYSANTHEMUM CUTTINGS, GLADIOLI',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_fern',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1471',
    label: 'Fern',
    description: 'FERN',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_yuca_cuttings',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1472',
    label: 'Yuca Cuttings',
    description: 'YUCA CUTTINGS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_plants',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1475',
    label: 'Plants',
    description: 'PLANTS',
    aliases: ['PLANTS  EXCL. ORCHIDS', 'TROPICAL PLANTS  EXCL CUT FLOWERS'],
    sortPosition: 3,
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_seeds_plants',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1479',
    label: 'Seeds, Plants',
    description: 'SEEDS, PLANTS',
    aliases: ['TROPICAL PLANTS, SEEDS'],
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_seeds',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1485',
    label: 'Seeds',
    description: 'SEEDS',
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_flowers_plants',
    parentId: 'tact_s1400',
    level: 'ITEM',
    tactCode: '1499',
    label: 'Flowers, Plants',
    description: 'FLOWERS, PLANTS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'tact_s1500',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1500, 1599],
    label: 'Herbs, leaves and products thereof, betel, tobacco, tobacco products',
    description: 'Herbs, leaves and products thereof, betel, tobacco, tobacco products',
    isSelectable: false,
  },
  {
    id: 'cmd_herbs_leaves_including_products_thereof',
    parentId: 'tact_s1500',
    level: 'ITEM',
    tactCode: '1500',
    label: 'Herbs, Leaves and Their Products',
    description: 'HERBS, LEAVES - INCLUDING PRODUCTS THEREOF -',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_khat',
    parentId: 'cmd_herbs_leaves_including_products_thereof',
    level: 'ITEM',
    label: 'Khat',
    description: 'KHAT',
  }, // added from the TK list
  {
    id: 'cmd_henna_dried',
    parentId: 'tact_s1500',
    level: 'ITEM',
    tactCode: '1503',
    label: 'Henna, Dried',
    description: 'HENNA  DRIED',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_pepper_stems',
    parentId: 'tact_s1500',
    level: 'ITEM',
    tactCode: '1526',
    label: 'Pepper Stems',
    description: '*****PEPPER STEMS',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_betel_leaves',
    parentId: 'tact_s1500',
    level: 'ITEM',
    tactCode: '1535',
    label: 'Betel Leaves',
    description: 'BETEL LEAVES',
    allowedCargo: CARGO_PROFILES.PERISHABLE,
  },
  {
    id: 'cmd_cigarettes_cigars_and_or_tobacco',
    parentId: 'tact_s1500',
    level: 'ITEM',
    tactCode: '1563',
    label: 'Tobacco, Cigars, Cigarettes',
    description: '*****CIGARETTES, CIGARS AND/OR TOBACCO',
    aliases: ['TOBACCO, CIGARS, CIGARETTES', 'TOBACCO, TOBACCO PRODUCTS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_e_cigarettes',
    parentId: 'cmd_cigarettes_cigars_and_or_tobacco',
    level: 'ITEM',
    label: 'E-Cigarettes',
    description: 'E-CIGARETTES',
  }, // added from the TK list
  {
    id: 'tact_s1700',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1700, 1799],
    label: 'Oils, animal and/or vegetable, but not mineral',
    description: 'Oils, animal and/or vegetable, but not mineral',
    isSelectable: false,
  },
  {
    id: 'cmd_oils_animal_vegetable',
    parentId: 'tact_s1700',
    level: 'ITEM',
    tactCode: '1700',
    label: 'Animal and Vegetable Oils',
    description: 'OILS  ANIMAL, VEGETABLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_olive_oil',
    parentId: 'tact_s1700',
    level: 'ITEM',
    tactCode: '1701',
    label: 'Olive Oil',
    description: 'OLIVE OIL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_essential_oils',
    parentId: 'tact_s1700',
    level: 'ITEM',
    tactCode: '1716',
    label: 'Essential Oils',
    description: 'ESSENTIAL OILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_oil_compounds_for_paint',
    parentId: 'tact_s1700',
    level: 'ITEM',
    tactCode: '1728',
    label: 'Oil Compounds for Paint',
    description: 'OIL COMPOUNDS  FOR PAINT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s1900',
    parentId: 'tact_g1',
    level: 'SUB_GROUP',
    range: [1900, 1999],
    label: 'Miscellaneous inedible animal and vegetable products which could not be classified between 1000-1799',
    description: 'Miscellaneous inedible animal and vegetable products which could not be classified between 1000-1799',
    isSelectable: false,
  },
  {
    id: 'cmd_beeswas',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1904',
    label: 'Beeswax',
    description: 'BEESWAS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bristles',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1915',
    label: 'Bristles',
    description: 'BRISTLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_stomach_dried',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1923',
    label: 'Stomach, Dried',
    description: 'STOMACH  DRIED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_casings_made_of_animal_intestines_gut',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1925',
    label: 'Casings (Animal Intestines, Gut)',
    description: 'CASINGS  MADE OF ANIMAL INTESTINES, GUT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rabbit_hair_unmanufactured',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1930',
    label: 'Rabbit Hair (Unmanufactured)',
    description: 'RABBIT HAIR UNMANUFACTURED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_glands_animal',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1931',
    label: 'Animal Glands',
    description: 'GLANDS  ANIMAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_feathers_feather_trimmings',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1950',
    label: 'Feathers, Feather Trimmings',
    description: 'FEATHERS, FEATHER TRIMMINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_feathers_ostrich',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1951',
    label: 'Feathers, Ostrich',
    description: 'FEATHERS  OSTRICH',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sea_flora_shells',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1970',
    label: 'Sea Flora, Shells',
    description: 'SEA-FLORA, SHELLS',
    aliases: ['SEA SHELLS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sponges_natural',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1980',
    label: 'Sponges, Natural',
    description: 'SPONGES  NATURAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sponges_artificial',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1981',
    label: 'Sponges, Artificial',
    description: 'SPONGES  ARTIFICIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ivory_goods',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1990',
    label: 'Ivory Goods',
    description: 'IVORY GOODS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_animal_hunting_trophies_excluding_mory',
    parentId: 'tact_s1900',
    level: 'ITEM',
    tactCode: '1993',
    label: 'Animal Hunting Trophies (excl. Ivory)',
    description: 'ANIMAL HUNTING TROPHIES  EXCLUDING MORY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g2',
    level: 'GROUP',
    range: [2000, 2999],
    label: 'Textiles; fibres and manufactures',
    description: 'Textiles; fibres and manufactures',
    isSelectable: false,
  },
  {
    id: 'tact_s2000',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2000, 2499],
    label: 'Yarn, thread, fibres, felt, cloth, fabrics, clothing, footwear',
    description: 'Yarn, thread, fibres, felt, cloth, fabrics, clothing, footwear',
    isSelectable: false,
  },
  {
    id: 'cmd_yarn_thread_fibres_textile_manufactures',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2001',
    label: 'Yarn, Thread, Fibers, Textile Manufactures',
    description: 'YARN,THREAD,FIBRES, TEXTILE MANUFACTURES',
    aliases: ['YARN,THREAD,FIBRE,TEXTILE,EXCL.WEARING A', 'YARN, THREAD, FIBRES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_polyester',
    parentId: 'cmd_yarn_thread_fibres_textile_manufactures',
    level: 'ITEM',
    label: 'Polyester',
    description: 'POLYESTER',
  }, // added from the TK list
  {
    id: 'cmd_cotton_yarn_thread',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2020',
    label: 'Cotton, Yarn, Thread',
    description: 'COTTON, YARN, THREAD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_thread_cord_made_of_rubber_textile_covered',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2041',
    label: 'Rubber Thread, Cord (Textile Covered)',
    description: 'THREAD CORD MADE OF RUBBER TEXTILE COVERED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_silk_yarn',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2075',
    label: 'Silk Yarn',
    description: 'SILK YARN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wool_yarn_thread',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2090',
    label: 'Wool, Yarn, Thread',
    description: 'WOOL, YARN, THREAD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cloth_not_further_processsed_or_manufactured_exclus_bales_b',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2102',
    label: 'Cloth, Not Further Processed (in Bales, Bolts, Pieces)',
    description: 'CLOTH NOT FURTHER PROCESSSED OR MANUFACTURED/EXCLUS. BALES/B',
    aliases: ['CLOTH EXCLSVLY IN BALESBOLTS OR PIECES NOT FRTH PRCSSD'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_viscose_cloth_not_frthr_prcssd_mfd_exclsvly_in_bales_bolts_p',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2104',
    label: 'Viscose Cloth (Not Further Processed, in Bales, Bolts, Pieces)',
    description: 'VISCOSE CLOTH NOT FRTHR PRCSSD/MFD/EXCLSVLY IN BALES,BOLTS,P',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_coated_fabrics_not_frthr_prcssd_mfd_exclsvly_in_bale',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2144',
    label: 'Plastic-Coated Fabrics (Not Further Processed)',
    description: 'PLASTIC COATED FABRICS NOT FRTHR PRCSSD/MFD/EXCLSVLY IN BALE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_piece_goods_made_of_nylon',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2162',
    label: 'Nylon Piece Goods',
    description: 'PIECE GOODS  MADE OF NYLON',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_silk_artsilk_manufactures',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2175',
    label: 'Silk and Artificial Silk Manufactures',
    description: 'SILK ARTSILK MANUFACTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_batik_textiles',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2183',
    label: 'Batik Textiles',
    description: '*****BATIK TEXTILES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_felt_paper_makers',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2185',
    label: "Paper Makers' Felt",
    description: 'FELT PAPER MAKERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_felt_cloth',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2186',
    label: 'Felt Cloth',
    description: 'FELT CLOTH',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sheep_skin_coats_overcoats_hats_slippers_handmade',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2189',
    label: 'Sheepskin Coats, Overcoats, Hats, Slippers',
    description: 'SHEEP SKIN COATS,OVERCOATS,HATS,SLIPPERS-HANDMADE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_a_yarn_thread_fibres_cloth_not_further_processed_or_manufact',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2195',
    label: 'Yarn, Thread, Fibers, Cloth (Not Further Processed)',
    description: 'A: YARN, THREAD, FIBRES, CLOTH - NOT FURTHER PROCESSED OR MANUFACTURED/EXCLUSIVELY IN BALES, BOLTS,',
    aliases: [
      'YARN,THREAD,FIBERS IN BALES/BOLTS/PCS AND BEDSHEETS ETC MADE O',
      'YARN,THREAD,FIBRES,CLOTH-NOT FURTHER PROCESSED OR MANUFACTUR',
      'YARNTHREADFIBRES IN BALES EXCL CLOTHINGFOOTWEAROUTW',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_a_yarn_thread_fibres_textiles_b_textile_manufactures_c_garme',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2199',
    label: 'Yarn, Thread, Fibers, Textiles, Garments',
    description: 'A: YARN, THREAD, FIBRES, TEXTILES, B: TEXTILE MANUFACTURES, C: GARMENTS, WEARING APPAREL',
    aliases: ['GARMENTS, YARN, THREAD, TEXTILE MANUFACTURES, WEARING APPAREL EXCLUDING FOOTWEAR, LEATHER MANUFACTUR'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_wearing_apparel',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2200',
    label: 'Garments, Wearing Apparel',
    description: 'GARMENTS, WEARING APPAREL',
    aliases: [
      'GARMENTS, CLOTHING AND WEARING APPAREL EXCLUDING LEATHER MANUFACTURES',
      'CLOTHING AND WEARING APPAREL',
      'GARMENTS',
      'GARMENTS, WEARING APPAREL (EXCLUDING UMBRELLAS, BILLFOLDS, PURSES, CARRYING CASES, HANDBAGS, COSTUME',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  { id: 'cmd_shawls', parentId: 'cmd_garments_wearing_apparel', level: 'ITEM', label: 'Shawls', description: 'SHAWLS' }, // added from the TK list
  {
    id: 'cmd_eyelets_press_fasteners_rivets_electric_attaching_machine',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2205',
    label: 'Eyelets, Press Fasteners, Rivets, Electric Attaching Machine',
    description: 'EYELETS,PRESS FASTENERS,RIVETS, ELECTRIC ATTACHING MACHINE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_wearing_apparel_shoe_laces_hooks_snaps_buttons_buck',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2206',
    label: 'Garments, Shoe Laces, Hooks, Snaps, Buttons, Buckles, Zip Fasteners',
    description: 'GARMENTS, WEARING APPAREL, SHOE LACES, HOOKS, SNAPS, BUTTONS, BUCKLES, ZIP-FASTENERS EXCLUDING FURS,',
    aliases: ['GARMENTS, WEARING APPAREL, SHOE LACES, HOOKS, SNAPS, BUTTONS,BUCKLES, ZIP-FASTENERS EXCLUDING FOOTWE'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_yarn_thread_fibres_not_further_processed_or_manufac',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2211',
    label: 'Garments, Yarn, Thread, Fibers (Not Further Processed, in Bales)',
    description: 'GARMENTS, YARN, THREAD, FIBRES - NOT FURTHER PROCESSED OR MANUFACTURED/EXCLUSIVELY IN BALES, BOLTS,',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_trousers_shirts',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2214',
    label: 'Trousers, Shirts',
    description: 'TROUSERS, SHIRTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cloth_ladies_cut_dresses_semi_finished_clothin_incl_dressmak',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2215',
    label: "Cloth, Ladies' Cut Dresses, Semi-Finished Clothing",
    description: 'CLOTH,LADIES CUT DRESSES,SEMI-FINISHED CLOTHIN INCL DRESSMAK',
    aliases: ['SEMI FINISHED CLOTHING'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dresses_ladies_finished',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2218',
    label: "Ladies' Finished Dresses",
    description: 'DRESSES  LADIES, FINISHED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_terry_cloth_terry_articles',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2222',
    label: 'Terry Cloth, Terry Articles',
    description: 'TERRY CLOTH, TERRY ARTICLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_wearing_apparel_fur_leather',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2234',
    label: 'Garments, Wearing Apparel (Fur)',
    description: 'GARMENTS, WEARING APPAREL (FUR/LEATHER)',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_overcoats_jackets_skirts_shirts_waistcoats_trousers_leather',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2243',
    label: 'Overcoats, Jackets, Skirts, Shirts, Waistcoats, Trousers',
    description: 'OVERCOATS,JACKETS,SKIRTS,SHIRTS,WAISTCOATS,TROUSERS-LEATHER',
    aliases: ['JACKETS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elastic_manufactures',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2244',
    label: 'Elastic Manufactures',
    description: 'ELASTIC MANUFACTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wearing_apparel_made_of_leath_excl_footwear',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2245',
    label: 'Leather Wearing Apparel (excl. Footwear)',
    description: 'WEARING APPAREL MADE OF LEATH EXCL FOOTWEAR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_blouses_jackets_jean_skirt_trouser_waistcoat_made_of_cotton',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2292',
    label: 'Blouses, Jackets, Jeans, Skirts, Trousers, Waistcoats',
    description: 'BLOUSES,JACKETS,JEAN,SKIRT,TROUSER,WAISTCOAT-MADE OF COTTON',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_corsets_girdles_brassieres',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2306',
    label: 'Corsets, Girdles, Brassieres',
    description: 'CORSETS, GIRDLES, BRASSIERES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_childrens_wearing_apparel',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2309',
    label: "Garments, Children's Wearing Apparel",
    description: 'GARMENTS, CHILDRENS WEARING APPAREL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dresses_skirts_blouses',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2310',
    label: 'Dresses, Skirts, Blouses',
    description: 'DRESSES SKIRTS BLOUSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_wearing_apparel_textile_manufactures_on_hangers',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2311',
    label: 'Garments, Wearing Apparel, Textile Manufactures (on Hangers)',
    description: 'GARMENTS, WEARING APPAREL, TEXTILE MANUFACTURES - ON HANGERS -',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_gloves_mittens',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2332',
    label: 'Gloves, Mittens',
    description: 'GLOVES, MITTENS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hats',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2350',
    label: 'Hats',
    description: 'HATS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_stockings_anklets_peds_socks',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2360',
    label: 'Stockings, Anklets, Peds, Socks',
    description: 'STOCKINGS, ANKLETS, PEDS, SOCKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hose_pantyhose',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2367',
    label: 'Hose, Pantyhose',
    description: 'HOSE, PANTYHOSE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shirts_blouses_underwear',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2408',
    label: 'Shirts, Blouses, Underwear',
    description: 'SHIRTS, BLOUSES, UNDERWEAR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shoe_uppers',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2417',
    label: 'Shoe Uppers',
    description: 'SHOE UPPERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shoes_slippers',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2420',
    label: 'Shoes, Slippers',
    description: 'SHOES, SLIPPERS',
    aliases: ['SHOES', 'SHOES ANDSLIPPERS FINISHEDSOLESHEELS ANDUPPERS EXCL ANY OTHER', 'SHOES AND SLIPPERS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_soles_for_shoes_slippers',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2426',
    label: 'Soles for Shoes, Slippers',
    description: 'SOLES  FOR SHOES, SLIPPERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_woollen_knitted_sweaters_and_dresses',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2451',
    label: 'Woolen Knitted Sweaters and Dresses',
    description: '*****WOOLLEN KNITTED SWEATERS AND DRESSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pullovers_cardigans',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2452',
    label: 'Pullovers, Cardigans',
    description: 'PULLOVERS, CARDIGANS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_wearing_apparel_knitted',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2455',
    label: 'Garments, Wearing Apparel (Knitted)',
    description: 'GARMENTS, WEARING APPAREL (KNITTED)',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_underwear_swimwear_body_supporting_garments',
    parentId: 'tact_s2000',
    level: 'ITEM',
    tactCode: '2461',
    label: 'Underwear, Swimwear, Body Supporting Garments',
    description: 'UNDERWEAR, SWIMWEAR, BODY SUPPORTING GARMENTS',
    aliases: ['BODY SUPPORTING GARMENTSSWIMWEAR', 'UNDERWEAR'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s2500',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2500, 2599],
    label: 'Drawnwork, needlework, embroideries, lace',
    description: 'Drawnwork, needlework, embroideries, lace',
    isSelectable: false,
  },
  {
    id: 'cmd_drawnwork_needlework_embroideries_lace',
    parentId: 'tact_s2500',
    level: 'ITEM',
    tactCode: '2500',
    label: 'Drawnwork, Needlework, Embroideries',
    description: 'DRAWNWORK, NEEDLEWORK, EMBROIDERIES, LACE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s2600',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2600, 2699],
    label: 'Textiles and finished textile articles',
    description: 'Textiles and finished textile articles',
    isSelectable: false,
  },
  {
    id: 'cmd_textile_articles_fnshd_excl_wearing_apparel',
    parentId: 'tact_s2600',
    level: 'ITEM',
    tactCode: '2600',
    label: 'Finished Textile Articles (excl. Wearing Apparel)',
    description: 'TEXTILE ARTICLES  FNSHD/EXCL WEARING APPAREL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_gas_mantles',
    parentId: 'tact_s2600',
    level: 'ITEM',
    tactCode: '2627',
    label: 'Gas Mantles',
    description: 'GAS MANTLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s2700',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2700, 2799],
    label: 'Hair, hair manufactures',
    description: 'Hair, hair manufactures',
    isSelectable: false,
  },
  {
    id: 'cmd_hair_hair_manufactures',
    parentId: 'tact_s2700',
    level: 'ITEM',
    tactCode: '2700',
    label: 'Hair, Hair Manufactures',
    description: 'HAIR, HAIR MANUFACTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_toupees_wigs_switches_false_eyelashes',
    parentId: 'tact_s2700',
    level: 'ITEM',
    tactCode: '2741',
    label: 'Toupees, Wigs, Switches, False Eyelashes',
    description: 'TOUPEES,WIGS,SWITCHES,FALSE EYELASHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s2800',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2800, 2899],
    label: 'Textile furnishings, curtains, floor coverings, bed linen, blankets',
    description: 'Textile furnishings, curtains, floor coverings, bed linen, blankets',
    isSelectable: false,
  },
  {
    id: 'cmd_textile_furnishings',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2800',
    label: 'Textile Furnishings',
    description: 'TEXTILE FURNISHINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_blankets_bedsheets_spreads_pillow_cases_mosquito_nets_towels',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2811',
    label: 'Blankets, Bedsheets, Pillow Cases, Towels, Mosquito Nets',
    description: 'BLANKETS,BEDSHEETS/SPREADS,PILLOW CASES,MOSQUITO NETS,TOWELS',
    aliases: ['BEDSHEETS PILLOW CASES AND TOWELS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_upholsterycurtainsdrapers',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2830',
    label: 'Upholstery, Curtains, Drapery',
    description: 'UPHOLSTERYCURTAINSDRAPERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shades_and_blinds',
    parentId: 'cmd_upholsterycurtainsdrapers',
    level: 'ITEM',
    label: 'Shades and Blinds',
    description: 'SHADES AND BLINDS',
  }, // added from the TK list
  {
    id: 'cmd_towels',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2846',
    label: 'Towels',
    description: 'TOWELS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_carpets_rugs',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2865',
    label: 'Carpets, Rugs',
    description: 'CARPETS, RUGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  { id: 'cmd_mats', parentId: 'cmd_carpets_rugs', level: 'ITEM', label: 'Mats', description: 'MATS' }, // added from the TK list
  {
    id: 'cmd_felt_base_floor_covering',
    parentId: 'tact_s2800',
    level: 'ITEM',
    tactCode: '2870',
    label: 'Felt Base Floor Covering',
    description: 'FELT BASE FLOOR COVERING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s2900',
    parentId: 'tact_g2',
    level: 'SUB_GROUP',
    range: [2900, 2999],
    label: 'Miscellaneous textile manufactures and raw materials which could not be classified between 2000-2899',
    description: 'Miscellaneous textile manufactures and raw materials which could not be classified between 2000-2899',
    isSelectable: false,
  },
  {
    id: 'cmd_canvas_articles',
    parentId: 'tact_s2900',
    level: 'ITEM',
    tactCode: '2920',
    label: 'Canvas Articles',
    description: 'CANVAS ARTICLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_big_bags_fibc',
    parentId: 'cmd_canvas_articles',
    level: 'ITEM',
    label: 'Big Bags (FIBC)',
    description: 'BIG BAGS (FIBC)',
  }, // added from the TK list
  {
    id: 'cmd_cloth_blankets_cloth_sleeping_bags',
    parentId: 'tact_s2900',
    level: 'ITEM',
    tactCode: '2949',
    label: 'Cloth Blankets, Cloth Sleeping Bags',
    description: '***** CLOTH BLANKETS,CLOTH SLEEPING BAGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g3',
    level: 'GROUP',
    range: [3000, 3999],
    label: 'Metals and manufactures, excluding machinery, vehicles and electrical equipment',
    description: 'Metals and manufactures, excluding machinery, vehicles and electrical equipment',
    isSelectable: false,
  },
  {
    id: 'tact_s3000',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3000, 3099],
    label: 'Metals (gold, silver, platinum, copper, brass, aluminium, steel)',
    description: 'Metals (gold, silver, platinum, copper, brass, aluminium, steel)',
    isSelectable: false,
  },
  {
    id: 'cmd_precious_metals',
    parentId: 'tact_s3000',
    level: 'ITEM',
    label: 'Precious Metals',
    description: 'PRECIOUS METALS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_dor_bullion',
    parentId: 'cmd_precious_metals',
    level: 'ITEM',
    label: 'Doré Bullion',
    description: 'DORÉ BULLION',
  }, // added from the TK list
  { id: 'cmd_gold', parentId: 'cmd_precious_metals', level: 'ITEM', label: 'Gold', description: 'GOLD' }, // added from the TK list
  {
    id: 'cmd_gold_bullion',
    parentId: 'cmd_precious_metals',
    level: 'ITEM',
    label: 'Gold Bullion',
    description: 'GOLD BULLION',
  }, // added from the TK list
  { id: 'cmd_platinum', parentId: 'cmd_precious_metals', level: 'ITEM', label: 'Platinum', description: 'PLATINUM' }, // added from the TK list
  {
    id: 'cmd_silver_silver_products_silver_jewellery',
    parentId: 'tact_s3000',
    level: 'ITEM',
    label: 'Silver, Silver Products, Silver Jewelry',
    description: 'SILVER/SILVER PRODUCTS/SILVER JEWELLERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_silver_accessories',
    parentId: 'cmd_silver_silver_products_silver_jewellery',
    level: 'ITEM',
    label: 'Silver Accessories',
    description: 'SILVER ACCESSORIES',
  }, // added from the TK list
  {
    id: 'cmd_silver_ornaments',
    parentId: 'cmd_silver_silver_products_silver_jewellery',
    level: 'ITEM',
    label: 'Silver Ornaments',
    description: 'SILVER ORNAMENTS',
  }, // added from the TK list
  {
    id: 'cmd_metallized_plasticpaper_foils',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3001',
    label: 'Metallized Plastic and Paper Foils',
    description: 'METALLIZED PLASTICPAPER FOILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metal_ingots_grain_semi_finished_sheet_foil_powder_spo',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3002',
    label: 'Metal Ingots, Grain, Semi-Finished Sheet, Foil, Plates',
    description: 'METAL INGOTS/GRAIN  SEMI-FINISHED  SHEET, FOIL, POWDER, SPO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_raw_metal',
    parentId: 'cmd_metal_ingots_grain_semi_finished_sheet_foil_powder_spo',
    level: 'ITEM',
    label: 'Raw Metal',
    description: 'RAW METAL',
  }, // added from the TK list
  {
    id: 'cmd_wire_cloth',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3004',
    label: 'Wire Cloth',
    description: 'WIRE CLOTH',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_platinoid_mineral_concentrades_matte',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3011',
    label: 'Platinoid Mineral Concentrates, Matte',
    description: 'PLATINOID MINERAL CONCENTRADES, MATTE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metal_die_cast_alloys_excl_gold_platinum_alloys',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3013',
    label: 'Metal Die-Cast Alloys (excl. Gold, Platinum)',
    description: 'METAL DIE CAST ALLOYS EXCL GOLD PLATINUM ALLOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_silver_bullion_ingots_specie_grain_foil',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3015',
    label: 'Silver Bullion, Ingots, Specie, Grain',
    description: 'SILVER-BULLION,INGOTS,SPECIE,GRAIN,FOIL.',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metal_wares_excl_gold_platinum_platinum_mtrls',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3025',
    label: 'Metal Wares (excl. Gold, Silver, Platinum)',
    description: 'METAL WARES  EXCL GOLD, PLATINUM, PLATINUM MTRLS',
    aliases: ['METAL WARES EXCL SILVER GOLD PLATINUM MTRLS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_copper_manufacturs',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3030',
    label: 'Copper Manufactures',
    description: 'COPPER MANUFACTURS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_copper_in_slabs_semi_finishd_prods_plates_sheets_wire_rod',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3032',
    label: 'Copper in Slabs, Semi-Finished Products, Plates, Sheets, Wire',
    description: 'COPPER IN SLABS, SEMI-FINISHD PRODS,PLATES,SHEETS,WIRE,ROD,',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_copper_ore',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3034',
    label: 'Copper Ore',
    description: 'COPPER ORE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_copper_wire_copper_cable',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3038',
    label: 'Copper Wire, Copper Cable',
    description: 'COPPER WIRE, COPPER CABLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_brass_manufacturs',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3050',
    label: 'Brass Manufactures',
    description: 'BRASS MANUFACTURS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_aluminium_tubings_plastic_tubings',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3051',
    label: 'Aluminum and Plastic Tubing',
    description: 'ALUMINIUM TUBINGS PLASTIC TUBINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tube_fittngs_thermoplastic_tubes_tubing_tools_tube_valves',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3052',
    label: 'Tube Fittings, Thermoplastic Tubes, Tube Valves',
    description: 'TUBE FITTNGS THERMOPLASTIC TUBES TUBING TOOLS TUBE VALVES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_aluminium_manufactures',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3060',
    label: 'Aluminum Manufactures',
    description: 'ALUMINIUM MANUFACTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_extrusions_honeycomb_sheets_sheets_foil_plates_tubes_rods',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3075',
    label: 'Extrusions, Honeycomb Sheets, Foil, Plates',
    description: 'EXTRUSIONS,HONEYCOMB SHEETS,SHEETS,FOIL,PLATES,TUBES,RODS,',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tungstentungstn_carbide_powdertngstn_concntratstantalum',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3094',
    label: 'Tungsten, Tungsten Carbide Powder, Tantalum',
    description: 'TUNGSTENTUNGSTN CARBIDE POWDERTNGSTN CONCNTRATSTANTALUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sheetstubingwire_made_of_tungstentantalumolybdenum',
    parentId: 'tact_s3000',
    level: 'ITEM',
    tactCode: '3096',
    label: 'Tungsten, Tantalum, Molybdenum Sheets, Tubing, Wire',
    description: 'SHEETSTUBINGWIRE MADE OF TUNGSTENTANTALUMOLYBDENUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3100',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3100, 3199],
    label: 'Cutlery, tableware, flatware, razor blades, scissors, trophies',
    description: 'Cutlery, tableware, flatware, razor blades, scissors, trophies',
    isSelectable: false,
  },
  {
    id: 'cmd_cutlery',
    parentId: 'tact_s3100',
    level: 'ITEM',
    tactCode: '3100',
    label: 'Cutlery',
    description: 'CUTLERY',
    aliases: ['CUTLER, FORKS, SPOONS EXCLDG SILVERWARE'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_razors_razor_blades',
    parentId: 'tact_s3100',
    level: 'ITEM',
    tactCode: '3127',
    label: 'Razors, Razor Blades',
    description: 'RAZORS, RAZOR BLADES',
    aliases: ['RAZOR BLADES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_table_flatware_bronzeware',
    parentId: 'tact_s3100',
    level: 'ITEM',
    tactCode: '3150',
    label: 'Table Flatware, Bronzeware',
    description: 'TABLE FLATWARE, BRONZEWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electroplated_nickel_silverware_tableware_trophies_cups_meda',
    parentId: 'tact_s3100',
    level: 'ITEM',
    tactCode: '3153',
    label: 'Electroplated Nickel Silverware, Tableware, Trophies, Cups',
    description: 'ELECTROPLATED NICKEL SILVERWARE,TABLEWARE,TROPHIES,CUPS,MEDA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pewter_products',
    parentId: 'tact_s3100',
    level: 'ITEM',
    tactCode: '3161',
    label: 'Pewter Products',
    description: 'PEWTER PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3200',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3200, 3299],
    label: 'Hand tools',
    description: 'Hand tools',
    isSelectable: false,
  },
  {
    id: 'cmd_lawn_sprinklers_pistol_nozzle_connectors_bases_usd_for_sprin',
    parentId: 'tact_s3200',
    level: 'ITEM',
    tactCode: '3206',
    label: 'Lawn Sprinklers, Pistol Nozzles, Connectors, Sprinkler Bases',
    description: 'LAWN SPRINKLERS,PISTOL NOZZLE/CONNECTORS,BASES USD FOR SPRIN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_drill_bits',
    parentId: 'tact_s3200',
    level: 'ITEM',
    tactCode: '3236',
    label: 'Drill Bits',
    description: 'DRILL BITS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_riveting_tools_rivets',
    parentId: 'tact_s3200',
    level: 'ITEM',
    tactCode: '3271',
    label: 'Riveting Tools, Rivets',
    description: 'RIVETING TOOLS RIVETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3300',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3300, 3399],
    label: 'Hardware',
    description: 'Hardware',
    isSelectable: false,
  },
  {
    id: 'cmd_hardware',
    parentId: 'tact_s3300',
    level: 'ITEM',
    tactCode: '3300',
    label: 'Hardware',
    description: 'HARDWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_building_mtrls_fixturs_fittngs_excldg_tools',
    parentId: 'tact_s3300',
    level: 'ITEM',
    tactCode: '3321',
    label: 'Building Materials, Fixtures, Fittings',
    description: 'BUILDING MTRLS, FIXTURS, FITTNGS, EXCLDG TOOLS',
    aliases: ['BUILDING MATERIAL/TOOLS/FIXTURES/FITTINGS', 'BUILDING FIXTURES AND FITTINGS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_scaffolding',
    parentId: 'tact_s3300',
    level: 'ITEM',
    tactCode: '3323',
    label: 'Scaffolding',
    description: 'SCAFFOLDING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bathroom_shower_sets',
    parentId: 'tact_s3300',
    level: 'ITEM',
    tactCode: '3341',
    label: 'Bathroom Shower Sets',
    description: 'BATHROOM SHOWER SETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3400',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3400, 3499],
    label: 'Utensils for household, kitchen and hospital',
    description: 'Utensils for household, kitchen and hospital',
    isSelectable: false,
  },
  {
    id: 'cmd_household_table_kitchen_hospital_utensils',
    parentId: 'tact_s3400',
    level: 'ITEM',
    tactCode: '3400',
    label: 'Household, Table, Kitchen and Hospital Utensils',
    description: 'HOUSEHOLD/TABLE/KITCHEN /HOSPITAL UTENSILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_catering_equipment',
    parentId: 'cmd_household_table_kitchen_hospital_utensils',
    level: 'ITEM',
    label: 'Catering Equipment',
    description: 'CATERING EQUIPMENT',
  }, // added from the TK list
  {
    id: 'cmd_table_kitchen_utensls_of_stnless_steel_excl_machi_elec_eqpt',
    parentId: 'tact_s3400',
    level: 'ITEM',
    tactCode: '3404',
    label: 'Stainless Steel Table and Kitchen Utensils',
    description: 'TABLE/KITCHEN UTENSLS OF STNLESS STEEL EXCL MACHI,ELEC EQPT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_kitchen_table_utensls_vases_excl_electric_equipment',
    parentId: 'tact_s3400',
    level: 'ITEM',
    tactCode: '3409',
    label: 'Kitchen and Table Utensils, Vases (excl. Electric Equipment)',
    description: 'KITCHEN/TABLE UTENSLS,VASES EXCL ELECTRIC EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metal_kitchenware',
    parentId: 'tact_s3400',
    level: 'ITEM',
    tactCode: '3411',
    label: 'Metal Kitchenware',
    description: 'METAL KITCHENWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3500',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3500, 3599],
    label: 'Furniture and fixtures',
    description: 'Furniture and fixtures',
    isSelectable: false,
  },
  {
    id: 'cmd_metal_furniture_metal_fixtures',
    parentId: 'tact_s3500',
    level: 'ITEM',
    tactCode: '3500',
    label: 'Metal Furniture, Metal Fixtures',
    description: 'METAL FURNITURE METAL FIXTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_door_locks_handles',
    parentId: 'tact_s3500',
    level: 'ITEM',
    tactCode: '3501',
    label: 'Door Locks, Handles',
    description: 'DOOR LOCKS, HANDLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pumping_fixtures_pipe_fixtures',
    parentId: 'tact_s3500',
    level: 'ITEM',
    tactCode: '3503',
    label: 'Pumping Fixtures, Pipe Fixtures',
    description: 'PUMPING FIXTURES, PIPE FIXTURES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hardware_for_furniture',
    parentId: 'tact_s3500',
    level: 'ITEM',
    tactCode: '3504',
    label: 'Hardware for Furniture',
    description: 'HARDWARE FOR FURNITURE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_for_refrigerators_cookers',
    parentId: 'tact_s3500',
    level: 'ITEM',
    tactCode: '3574',
    label: 'Parts for Refrigerators and Cookers',
    description: 'PARTS FOR REFRIGERATORS/COOKERS',
    aliases: ['PARTS FOR ELECTRIC REFRIGERATORS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3890',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3890, 3899],
    label: 'Ore',
    description: 'Ore',
    isSelectable: false,
  },
  {
    id: 'cmd_cobalt',
    parentId: 'tact_s3890',
    level: 'ITEM',
    tactCode: '3890',
    label: 'Cobalt',
    description: 'COBALT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ore_excluding_gold_silver_platinum',
    parentId: 'tact_s3890',
    level: 'ITEM',
    tactCode: '3895',
    label: 'Ore (excl. Gold, Silver, Platinum)',
    description: 'ORE  EXCLUDING GOLD, SILVER, PLATINUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_silver_ore',
    parentId: 'tact_s3890',
    level: 'ITEM',
    tactCode: '3896',
    label: 'Silver Ore',
    description: 'SILVER ORE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s3900',
    parentId: 'tact_g3',
    level: 'SUB_GROUP',
    range: [3900, 3999],
    label: 'Miscellaneous metal which could not be classified between 3000-3899',
    description: 'Miscellaneous metal which could not be classified between 3000-3899',
    isSelectable: false,
  },
  {
    id: 'cmd_metal_products_miscellaneous',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3900',
    label: 'Metal Products (Miscellaneous)',
    description: 'METAL PRODUCTS  MISCELLANEOUS',
    sortPosition: 8,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_welding_supplies',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3909',
    label: 'Welding Supplies',
    description: 'WELDING SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_moulds_dies',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3963',
    label: 'Molds, Dies',
    description: 'MOULDS, DIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_locks',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3969',
    label: 'Locks',
    description: 'LOCKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dressmakes_supplies_excl_textilesclothings',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3973',
    label: "Dressmakers' Supplies (excl. Textiles, Clothing)",
    description: 'DRESSMAKES SUPPLIES EXCL TEXTILESCLOTHINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_steel_staples',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3976',
    label: 'Steel Staples',
    description: 'STEEL STAPLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cables_mesh_rivet_sheets_tubngs_wire_excl_gold_silver_platin',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3991',
    label: 'Cables, Mesh, Rivets, Sheets, Tubing, Wire (excl. Gold, Silver, Platinum)',
    description: 'CABLES,MESH,RIVET,SHEETS,TUBNGS,WIRE EXCL GOLD,SILVER,PLATIN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_titanium_alloys_nickel_alloys',
    parentId: 'tact_s3900',
    level: 'ITEM',
    tactCode: '3993',
    label: 'Titanium and Nickel Alloys',
    description: 'TITANIUM ALLOYS NICKEL ALLOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g4',
    level: 'GROUP',
    range: [4000, 4999],
    label: 'Machinery, vehicles and electrical equipment',
    description: 'Machinery, vehicles and electrical equipment',
    isSelectable: false,
  },
  {
    id: 'tact_s4000',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4000, 4099],
    label:
      'Business and office machines, machinery, tools, surface vehicles, excluding steamship and motorship machinery spare parts',
    description:
      'Business and office machines, machinery, tools, surface vehicles, excluding steamship and motorship machinery spare parts',
    isSelectable: false,
  },
  {
    id: 'cmd_busi_ofc_machi_incl_tools_surfc_vhcls_excl_steam_motorship_m',
    parentId: 'tact_s4000',
    level: 'ITEM',
    tactCode: '4010',
    label: 'Business and Office Machines (incl. Tools)',
    description: 'BUSI/OFC MACHI INCL TOOLS,SURFC VHCLS EXCL STEAM/MOTORSHIP M',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4100',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4100, 4199],
    label: 'Aircraft, aircraft engines, aircraft parts, outboard motors for boats',
    description: 'Aircraft, aircraft engines, aircraft parts, outboard motors for boats',
    isSelectable: false,
  },
  {
    id: 'cmd_aircraft',
    parentId: 'tact_s4100',
    level: 'ITEM',
    tactCode: '4105',
    label: 'Aircraft',
    description: 'AIRCRAFT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_helicopter_spares',
    parentId: 'tact_s4100',
    level: 'ITEM',
    tactCode: '4107',
    label: 'Helicopter Spares',
    description: 'HELICOPTER SPARES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_a_craft_engns_parts_incl_accessrs_excl_ovrhaul_maint_tools_s',
    parentId: 'tact_s4100',
    level: 'ITEM',
    tactCode: '4112',
    label: 'Aircraft Engines, Aircraft Parts (incl. Accessories)',
    description: 'A/CRAFT ENGNS/PARTS INCL ACCESSRS EXCL OVRHAUL/MAINT TOOLS,S',
    aliases: [
      'AIRCRAFT ENGINES,AIRCRAFT PARTS EXCL.FUSELAGES, WINGS',
      'AIRCRAFT ENGINES,AIRCRAFT,PARTS EXCL FUSELAGES,WINGS,A/CRAFT',
      'A/CRAFT ENGNS,PARTS EXCL TAIL ASSMBLS,STABLISRS,WINGS,FUSELA',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_aircraft_engines',
    parentId: 'cmd_a_craft_engns_parts_incl_accessrs_excl_ovrhaul_maint_tools_s',
    level: 'ITEM',
    label: 'Aircraft Engines',
    description: 'AIRCRAFT ENGINES',
  }, // added from the TK list
  {
    id: 'cmd_aircraft_parts',
    parentId: 'tact_s4100',
    level: 'ITEM',
    tactCode: '4119',
    label: 'Aircraft Parts',
    description: 'AIRCRAFT PARTS',
    aliases: [
      'AIRCRAFT PARTS  INCLDG ACCESSORIES',
      'AIRCRAFT PARTS  EXCL AIRCRAFT ENGINES',
      'AIRCRAFT PARTS  INCL ACCESSORIES',
    ],
    sortPosition: 6,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_outboard_motors_incl_accessories',
    parentId: 'tact_s4100',
    level: 'ITEM',
    tactCode: '4181',
    label: 'Outboard Motors (incl. Accessories)',
    description: 'OUTBOARD MOTORS  INCL ACCESSORIES',
    aliases: ['OUTBOARD MOTORS FOR BOATS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4200',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4200, 4299],
    label: 'Surface vehicles, parts, accessories',
    description: 'Surface vehicles, parts, accessories',
    isSelectable: false,
  },
  {
    id: 'cmd_ship_spares',
    parentId: 'tact_s4200',
    level: 'ITEM',
    label: 'Ship Spares',
    description: 'SHIP SPARES',
    aliases: ['INVENTORY MATERIALS FOR SHIPS'],
    sortPosition: 5,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_surface_vehicles_parts_accessories',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4200',
    label: 'Surface Vehicles, Parts, Accessories',
    description: 'SURFACE VEHICLES,PARTS,ACCESSORIES',
    aliases: [
      'PARTS OF SURFACE VEHICLESAGRI MACHNRY EXCL STEAMMOTORSHIP',
      'SURFACE VEHICLES(INCL ACCESSORIES) EXCH STEAM/MOTORSHP PARTS',
      'SURFAC VEHICLS EXCL STEAMSHIP/MOTORSHIP PARTS',
      'SURFACE VEHICLS INCL ACCES MACHNS EXCL STEAM/MOTORSHP MACHN',
      'SURFACE VEHICLES INCL ACCESSORIES ,MACHINERY,TOOLS,CUTLERY',
      'VEHICLES AND ACCESSORIES EXCL STEAM/MOTOR/RAIL/ARGI.MACH.PARTS',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_of_automobilesmotorscooterscycles_andagri_machines',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4204',
    label: 'Parts of Automobiles, Motor Scooters, Cycles, Agricultural Machines',
    description: 'PARTS OF AUTOMOBILESMOTORSCOOTERSCYCLES ANDAGRI MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_surfac_vehicls_self_propelled_agri_mach_incl_accesrs_excl_st',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4206',
    label: 'Self-Propelled Agricultural Machinery, Surface Vehicles',
    description: 'SURFAC VEHICLS,SELF-PROPELLED AGRI MACH-INCL ACCESRS-EXCL ST',
    aliases: ['SURFACE VHCLS,SELF-PPLD AGRO MACH-INCL ACCSR-EXCL STEAM/MOTO'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_of_automobile_incl_accessories_repair_equipt',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4211',
    label: 'Automobile Parts, Accessories, Repair Equipment',
    description: 'PARTS OF AUTOMOBILE INCL ACCESSORIES /REPAIR EQUIPT',
    sortPosition: 15,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_construction_parts_for_cable_cars_pylons',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4215',
    label: 'Construction Parts for Cable Cars, Pylons',
    description: 'CONSTRUCTION PARTS FOR CABLE CARS PYLONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_automobile_transmissions',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4216',
    label: 'Automobile Transmissions',
    description: 'AUTOMOBILE TRANSMISSIONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_motorcar_cushions_backrests',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4221',
    label: 'Motorcar Cushions, Backrests',
    description: 'MOTORCAR CUSHIONS BACKRESTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_automobl_parts_incl_accesrs_motorscootrs_mtr_bicycles_agri_m',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4232',
    label: 'Automobile, Motor Scooter and Bicycle Parts (incl. Accessories)',
    description: 'AUTOMOBL PARTS INCL ACCESRS MOTORSCOOTRS,MTR/BICYCLES,AGRI M',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_motorscooters_motorcycles_cycles_part_of_surface_vehicles_in',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4235',
    label: 'Motorcycles, Motor Scooters, Bicycles, Parts',
    description: 'MOTORSCOOTERS,MOTORCYCLES,CYCLES,PART OF SURFACE VEHICLES,IN',
    aliases: ['MOTOR ASSISTED BITRIMOTORCYCLESMOTORSCOOTERS', 'MOTORCYCLESMOTORSCOOTERSBICYCLES BOXED'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_automobile_temperature_gauges',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4242',
    label: 'Automobile Gauges',
    description: 'AUTOMOBILE TEMPERATURE GAUGES',
    aliases: ['AUTOMOBILE GAUGES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tachographs_for_surface_vehicles',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4243',
    label: 'Tachographs for Vehicles',
    description: 'TACHOGRAPHS FOR SURFACE VEHICLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_brake_linings',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4246',
    label: 'Brake Linings',
    description: 'BRAKE LININGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fanbelts',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4248',
    label: 'Fan Belts',
    description: 'FANBELTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_for_sporting_boats_incl_accessories',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4266',
    label: 'Parts for Sporting Boats (incl. Accessories)',
    description: 'PARTS FOR SPORTING BOATS  INCL ACCESSORIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_filters_for_agricultural_machines',
    parentId: 'tact_s4200',
    level: 'ITEM',
    tactCode: '4291',
    label: 'Filters for Agricultural Machines',
    description: 'FILTERS FOR AGRICULTURAL MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4300',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4300, 4399],
    label: 'Business and office machinery, parts, accessories',
    description: 'Business and office machinery, parts, accessories',
    isSelectable: false,
  },
  {
    id: 'cmd_office_machinery',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4300',
    label: 'Office Machinery',
    description: 'OFFICE MACHINERY',
    aliases: ['BUSINESS AND OFFICE MACHINERY, PARTS, ACCESSORIES', 'OFFICE MACHINES INCLUDING SUPPLIES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_photo_typesetting_machines',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4304',
    label: 'Photo-Typesetting Machines',
    description: 'PHOTO-TYPESETTING MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_portable_electronic_calculators',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4305',
    label: 'Portable Electronic Calculators',
    description: 'PORTABLE ELECTRONIC CALCULATORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_computers',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4306',
    label: 'Computers',
    description: 'COMPUTERS',
    aliases: [
      'DIGTL/ANALOG COMPUTRS INCL PUNCH CARDS,DISC PACKS,TAPES-ELEC',
      'ELEC DATA PROCSSG MACHNS,DIGTL/ANALOGUE COMPTRS-INDIVIDL UNI',
      'ELECTRNC DATA STORAGE PROCSG MACHINES,DIGITAL COMPUTRS,ANALO',
    ],
    sortPosition: 9,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_adding_calculatng_machns_cash_regstr_duplctg_docmts_typewrtr',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4309',
    label: 'Adding and Calculating Machines, Cash Registers, Typewriters, Duplicators',
    description: 'ADDING/CALCULATNG MACHNS,CASH REGSTR,DUPLCTG DOCMTS,TYPEWRTR',
    aliases: [
      'ADDG/CALCULATG MACHNS,CASH RGSTERS,MACHNSFOR DUPLCTNG DOCS',
      'CASH REGISTERS',
      'DUPLICATING MACHINES',
      'ADDING MACHINES COMPUTING MACHINES',
      'ADDING/COMPUTING/TYPEWRTG MACHNS,DOCUMT/LTR DUPLCTG MACHNS',
      'CALCULATING MACHINES',
      'ADDG/CALCULATG MACHNS,CASH REGSTERS,MACHN FR DUPL DOCS TAPE,',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_computer_punch_cards_computer_data_sheets',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4313',
    label: 'Computer Punch Cards, Computer Data Sheets',
    description: 'COMPUTER PUNCH CARDS, COMPUTER DATA SHEETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elctrnc_cmponnts_fabricatd_fr_elec_comptrs_auxiliary_machns',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4314',
    label: 'Electronic Components for Computers and Auxiliary Machines',
    description: 'ELCTRNC CMPONNTS FABRICATD FR ELEC COMPTRS,AUXILIARY MACHNS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_magnetic_tapes_for_data_storage',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4315',
    label: 'Magnetic Tapes for Data Storage',
    description: 'MAGNETIC TAPES FOR DATA STORAGE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_punch_cars_tape_data_sheets_discs_fr_elctrnc_data_storage_pr',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4317',
    label: 'Punch Cards, Tape, Data Sheets/Discs from Electronic Data Processing Machines',
    description: 'PUNCH CARS,TAPE,DATA SHEETS/DISCS FR ELCTRNC DATA STORAGE-PR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_typewriters',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4321',
    label: 'Typewriters',
    description: 'TYPEWRITERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_photocopying_machins_incl_accessories_supplies',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4322',
    label: 'Photocopying Machines (incl. Accessories, Supplies)',
    description: 'PHOTOCOPYING MACHINS  INCL ACCESSORIES, SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_printer_for_personal_computer',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4324',
    label: 'Computer Printers',
    description: 'PRINTER FOR PERSONAL COMPUTER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electronic_products',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4329',
    label: 'Electronic Products',
    description: 'ELECTRONIC PRODUCTS',
    aliases: ['ELECTRONIC PRODUCTS  INCL COMPONENTS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wire_processors',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4330',
    label: 'Wire Processors',
    description: 'WIRE PROCESSORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_recording_machines_using_tape_wire_disc',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4336',
    label: 'Recording Machines (Tape, Wire, Disc)',
    description: 'RECORDING MACHINES USING TAPE WIRE DISC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pari_mutual_totalizators_incl_ticket_rolls',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4375',
    label: 'Pari-Mutuel Totalizators (incl. Ticket Rolls)',
    description: 'PARI MUTUAL TOTALIZATORS INCL TICKET ROLLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_typewriter_ribbons',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4382',
    label: 'Typewriter Ribbons',
    description: 'TYPEWRITER RIBBONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_appliances_gas_appliances',
    parentId: 'tact_s4300',
    level: 'ITEM',
    tactCode: '4397',
    label: 'Electric and Gas Appliances',
    description: 'ELECTRIC APPLIANCES, GAS APPLIANCES',
    aliases: ['ELECTRICAL APPLIANCES', 'HOUSEHOLD APPLIANCES/UTENSILS - ELECTRIC -'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4400',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4400, 4499],
    label: 'Electrical and electronic appliances',
    description: 'Electrical and electronic appliances',
    isSelectable: false,
  },
  {
    id: 'cmd_electric_equipment',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4401',
    label: 'Electrical Equipment',
    description: 'ELECTRIC EQUIPMENT',
    aliases: ['ELECTRIC EQUIPMENT  EXCLUDING MACHINERY'],
    sortPosition: 4,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_power_supplies',
    parentId: 'cmd_electric_equipment',
    level: 'ITEM',
    label: 'Power Supplies',
    description: 'POWER SUPPLIES',
  }, // added from the TK list
  {
    id: 'cmd_electric_eqpt_appl_excl_business_office_machinery',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4402',
    label: 'Electric Equipment and Appliances (excl. Office Machinery)',
    description: 'ELECTRIC EQPT./APPL. EXCL. BUSINESS/OFFICE MACHINERY',
    aliases: ['ELEC EQPT, ELEC APPLIANCES EXCL COMPUTERS,OFFICE MACHINERY'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elec_control_panels_for_radar_station_powerplant_heatg_coolg',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4403',
    label: 'Electrical Control Panels (Radar Stations, Power Plants, Heating and Cooling)',
    description: 'ELEC CONTROL PANELS FOR RADAR STATION,POWERPLANT,HEATG/COOLG',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_batteries',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4405',
    label: 'Batteries',
    description: 'BATTERIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_phonograph_pick_ups',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4407',
    label: 'Phonograph Pick-Ups',
    description: 'PHONOGRAPH PICK UPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cables_for_electrcity_power_plantstelegraphtel_installns',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4408',
    label: 'Cables for Power Plants, Telegraph and Telephone Installations',
    description: 'CABLES FOR ELECTRCITY POWER PLANTSTELEGRAPHTEL INSTALLNS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electrodes',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4409',
    label: 'Electrodes',
    description: 'ELECTRODES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_communication_devices_signal_devices',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4410',
    label: 'Communication Devices, Signal Devices',
    description: 'COMMUNICATION DEVICES, SIGNAL DEVICES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_receivers',
    parentId: 'cmd_communication_devices_signal_devices',
    level: 'ITEM',
    label: 'Receivers',
    description: 'RECEIVERS',
  }, // added from the TK list
  {
    id: 'cmd_transmitters',
    parentId: 'cmd_communication_devices_signal_devices',
    level: 'ITEM',
    label: 'Transmitters',
    description: 'TRANSMITTERS',
  }, // added from the TK list
  {
    id: 'cmd_loud_spkrs_tape_recordrs_rcrd_players_radios_amplifiers',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4414',
    label: 'Radios, TVs, Record Players, Tape Recorders, Loudspeakers, Amplifiers',
    description: 'LOUD SPKRS,TAPE RECORDRS,RCRD PLAYERS,RADIOS,AMPLIFIERS',
    aliases: [
      'AMPLIFIERS,SPEAKERS,RHYTHM BOXES,RADIOS',
      'LOUDSPEAKERS',
      'RAIODS, TAPE RECORDS, RECORD PLAYERS, AERIALS',
      'RADIOS, TV, RADIO-TV SETS, PHONOGRAPH SETS',
      'RADIOS,TELEVISIONS,RECORD PLAYERS,TAPE RECORDERS',
      'TAPE RECORDERS',
      'RECORD PLAYERS TAPE RECORDERS',
      'RAIODS,TVS,RCRD PLAYRS,TAPE RCRDERS,LOUDSPKRS,AMPLIFIERS',
      'TRANSISTOR RADIOS',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mobile_phones_mobile_phones_automobile_radios_dictation_mach',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4416',
    label: 'Mobile Phones, Automobile Radios, Dictation Machines, Hearing Aids, Recordings',
    description: 'MOBILE PHONES, MOBILE PHONES,AUTOMOBILE RADIOS, DICTATION MACHINES, HEARING AIDS, RECORDS, RECORDING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_radios_tv_radio_tv_sets_radio_phonograph_sets_elec_hshld_app',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4417',
    label: 'Radios, Televisions, Radio-Phonograph Sets, Household Electrical Appliances',
    description: 'RADIOS,TV,RADIO-TV SETS,RADIO-PHONOGRAPH SETS,ELEC HSHLD APP',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mobile_phones_telephone_equipment_radio_telephone_equipment',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4419',
    label: 'Mobile Phones, Telephone and Radio-Telephone Equipment (incl. Testing Equipment)',
    description: 'MOBILE PHONES, TELEPHONE EQUIPMENT, RADIO-TELEPHONE EQUIPMENT - INCLUDING TESTING EQUIPMENT -',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mobile_phone_accessories_parts_components_for_the_manufactur',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4422',
    label: 'Mobile Phone Accessories, Parts and Components for the Manufacture of Telecommunication Equipment',
    description: 'MOBILE PHONE ACCESSORIES, PARTS/COMPONENTS FOR THE MANUFACTURE OF TELECOMMUNICATION EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sim_cards',
    parentId: 'cmd_mobile_phone_accessories_parts_components_for_the_manufactur',
    level: 'ITEM',
    label: 'SIM Cards',
    description: 'SIM CARDS',
  }, // added from the TK list
  {
    id: 'cmd_condensers',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4423',
    label: 'Condensers',
    description: 'CONDENSERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_autombl_radios_dictatn_machns_hearing_aids_records_rcrdg_tap',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4426',
    label: 'Automobile Radios, Dictation Machines, Hearing Aids, Recordings',
    description: 'AUTOMBL RADIOS,DICTATN MACHNS,HEARING AIDS,RECORDS,RCRDG TAP',
    aliases: ['AUTOMBL-RADIOS, DICTATN MACHNS,HEARG AIDS,RECORDS,RCRDG TAPE'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elec_tubes_transistors_solid_state_semi_conductor_devices_ra',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4427',
    label: 'Electronic Tubes, Transistors, Semiconductor Devices',
    description: 'ELEC TUBES,TRANSISTORS,SOLID STATE SEMI-CONDUCTOR DEVICES,RA',
    aliases: ['ELEC TUBES,TRANSISTORS,SOLID STATE SEMI-CONDUCTOR DEVICES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_relays',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4428',
    label: 'Relays',
    description: 'RELAYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_condensers_resistors_rheostats',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4430',
    label: 'Electric Condensers, Resistors, Rheostats',
    description: 'ELECTRIC CONDENSERS,RESISTORS,RHEOSTATS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_resistors_rheostats_electric_switches',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4431',
    label: 'Resistors, Rheostats, Electric Switches',
    description: 'RESISTORS,RHEOSTATS,ELECTRIC SWITCHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tubes_valves_for_radios_televisions',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4433',
    label: 'Tubes, Valves for Radios, Televisions',
    description: 'TUBES, VALVES - FOR RADIOS, TELEVISIONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_switches',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4434',
    label: 'Switches',
    description: 'SWITCHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_transistors_electric_condensers',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4436',
    label: 'Transistors, Electric Condensers',
    description: 'TRANSISTORS, ELECTRIC CONDENSERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_insulators_electric_fittings',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4437',
    label: 'Electric Insulators, Electric Fittings',
    description: 'ELECTRIC INSULATORS ELECTRIC FITTINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_resistors_transistors_capacitors_regulators_tuners',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4439',
    label: 'Resistors, Transistors, Capacitors, Regulators, Tubes',
    description: 'RESISTORS,TRANSISTORS,CAPACITORS,REGULATORS,TUNERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_radios_electronic_tubes_transistors_solid_state_semi_conduct',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4440',
    label: 'Radios, Electronic Tubes, Transistors, Solid-State Semiconductors',
    description: 'RADIOS,ELECTRONIC TUBES,TRANSISTORS,SOLID-STATE SEMI-CONDUCT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_televisions',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4442',
    label: 'Televisions',
    description: 'TELEVISIONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_telegraph_telephone_teletype_apparatus',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4445',
    label: 'Telegraph/Telephone/Teletype Apparatus',
    description: 'TELEGRAPH/TELEPHONE/TELETYPE APPARATUS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_integratd_circuits_condensors_relays_resistors_transistors',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4446',
    label: 'Integrated Circuits, Capacitors, Relays, Resistors, Transistors',
    description: 'INTEGRATD CIRCUITS,CONDENSORS,RELAYS,RESISTORS,TRANSISTORS',
    aliases: ['INTEGRATED CIRCUITS,RELAYS,CONDENSOR,CAPACITORS,TRANSISTORS,'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electron_gun_which_is_component_of_tv_rcvg_tube_mount',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4447',
    label: 'Electron Guns for TV Picture Tubes',
    description: 'ELECTRON GUN WHICH IS COMPONENT OF TV RCVG TUBE MOUNT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_walkie_talkies_including_batteries',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4448',
    label: 'Walkie-Talkies (incl. Batteries)',
    description: 'WALKIE TALKIES INCLUDING BATTERIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_smoke_detectors',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4449',
    label: 'Smoke Detectors',
    description: 'SMOKE DETECTORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_audio_video_apparatus_computers_for_domestic_use_only',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4451',
    label: 'Audio/Video Apparatus, Computers (Domestic Use Only)',
    description: 'AUDIO/VIDEO APPARATUS,COMPUTERS-FOR DOMESTIC USE ONLY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electro_erosion_machinery',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4452',
    label: 'Electro-Erosion Machinery',
    description: 'ELECTRO EROSION MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_heating_elements',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4453',
    label: 'Electric Heating Elements',
    description: 'ELECTRIC HEATING ELEMENTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_pumps_for_washing_machines',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4454',
    label: 'Electric Pumps for Washing Machines',
    description: 'ELECTRIC PUMPS FOR WASHING MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_implments_fr_measuring_chkg_analysg_auto_contrllg_condensors',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4455',
    label: 'Instruments for Measuring, Checking, Analyzing, Automatic Control',
    description: 'IMPLMENTS FR MEASURING,CHKG,ANALYSG,AUTO CONTRLLG,CONDENSORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_autombl_radios_speed_control_amplifiers_graphic_eqlizers_pre',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4457',
    label: 'Automobile Radios, Speed Control Amplifiers, Graphic Equalizers',
    description: 'AUTOMBL RADIOS,SPEED CONTROL AMPLIFIERS,GRAPHIC EQLIZERS,PRE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_for_radios_record_players_televisions',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4458',
    label: 'Parts for Radios, Record Players, Televisions',
    description: 'PARTS FOR RADIOS,RECORD PLAYERS,TELEVISIONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electronic_parts',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4459',
    label: 'Electronic Parts',
    description: 'ELECTRONIC PARTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_razors_mechanical_razors',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4461',
    label: 'Electric Razors, Mechanical Razors',
    description: 'ELECTRIC RAZORS, MECHANICAL RAZORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fridge_freezers_clothes_driers_washing_machines_stoves',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4465',
    label: 'Refrigerators, Freezers, Clothes Dryers, Washing Machines',
    description: 'FRIDGE,FREEZERS,CLOTHES DRIERS,WASHING MACHINES,STOVES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_thermostats_selector_switches_switch_doors',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4467',
    label: 'Thermostats, Selector Switches, Switch Doors',
    description: 'THERMOSTATS,SELECTOR SWITCHES,SWITCH DOORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_lamps_bulbs_tubes_incandescent_fluorescent',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4470',
    label: 'Incandescent and Fluorescent Lamps, Bulbs, Tubes',
    description: 'LAMPS,BULBS,TUBES-INCANDESCENT,FLUORESCENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_air_coolers',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4471',
    label: 'Air Coolers',
    description: 'AIR COOLERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_studio_lighting_equipment',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4472',
    label: 'Studio Lighting Equipment',
    description: 'STUDIO LIGHTING EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cooling_units_refrigeration_units_industrial',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4473',
    label: 'Industrial Cooling and Refrigeration Units',
    description: 'COOLING UNITS REFRIGERATION UNITS INDUSTRIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fans_heaters_electric',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4474',
    label: 'Electric Fans, Heaters',
    description: 'FANS, HEATERS - ELECTRIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_lighting_fixtures_lighting_fitting',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4475',
    label: 'Lighting Fixtures, Fittings',
    description: 'LIGHTING FIXTURES, LIGHTING FITTING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_lamps_lamp_shades_chndeliers',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4476',
    label: 'Electric Lamps, Lamp Shades, Chandeliers',
    description: 'ELECTRIC LAMPS, LAMP SHADES, CHNDELIERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wires_cables_electric',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4477',
    label: 'Electrical Wires, Cables',
    description: 'WIRES,CABLES - ELECTRIC -',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_tubing_plastic_granules_electric_wring',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4478',
    label: 'Plastic Tubing, Granules, Electric Wiring',
    description: 'PLASTIC TUBING PLASTIC GRANULES ELECTRIC WRING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_air_conditioners',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4483',
    label: 'Air Conditioners',
    description: 'AIR CONDITIONERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_auxlry_unitx_cable_forms_conn_forms_packs_relays_selectors_s',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4490',
    label: 'Auxiliary Units, Cable Forms, Connectors, Packs, Relays, Selectors',
    description: 'AUXLRY UNITX,CABLE-FORMS/CONN,FORMS,PACKS,RELAYS,SELECTORS,S',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elec_eqpt_fr_teletyp_telegraph_tel_radio_tel_tv_radio_broadc',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4492',
    label: 'Electrical Equipment for Teletype, Telegraph, Telephone, Radio-Telephone, Television',
    description: 'ELEC EQPT FR TELETYP,TELEGRAPH,TEL,RADIO-TEL,TV/RADIO BROADC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_switchesrelaysfusesexcess_voltage_conductorsplugssocket',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4493',
    label: 'Switches, Relays, Fuses, Plugs, Sockets',
    description: 'SWITCHESRELAYSFUSESEXCESS VOLTAGE CONDUCTORSPLUGSSOCKET',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_radar_equipment',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4498',
    label: 'Radar Equipment',
    description: 'RADAR EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ele_appl_machinery_machnry_tools_excl_steam_motor_ship_mach',
    parentId: 'tact_s4400',
    level: 'ITEM',
    tactCode: '4499',
    label: 'Electrical Appliances, Machinery, Machine Tools (excl. Ship Machinery)',
    description: 'ELE APPL./MACHINERY,MACHNRY TOOLS EXCL STEAM/MOTOR SHIP MACH',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4500',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4500, 4599],
    label: 'Electrical machinery',
    description: 'Electrical machinery',
    isSelectable: false,
  },
  {
    id: 'cmd_electric_machinery',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4500',
    label: 'Electric Machinery',
    description: 'ELECTRIC MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_drilling_machines_electric',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4502',
    label: 'Electric Drilling Machines',
    description: 'DRILLING MACHINES ELECTRIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_semi_conductors',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4504',
    label: 'Semiconductors',
    description: 'SEMI-CONDUCTORS',
    aliases: ['SEMI CONDUCTOR DEVICES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_printed_circuits',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4505',
    label: 'Printed Circuits',
    description: 'PRINTED CIRCUITS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_television_tuners',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4506',
    label: 'Television Tuners',
    description: 'TELEVISION TUNERS',
    aliases: ['TV TUNER PARTS NES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_light_bulbs',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4512',
    label: 'Electric Light Bulbs',
    description: 'ELECTRIC LIGHT BULBS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_valves_elec_mech_machinry_fr_nuclear_hydro_carbon_power_plan',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4515',
    label: 'Valves, Electrical or Mechanical Machinery for Nuclear and Hydrocarbon Power',
    description: 'VALVES,ELEC/MECH MACHINRY FR NUCLEAR,HYDRO-CARBON POWER PLAN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_copper_wire_relays_circuit_boards_coils',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4521',
    label: 'Copper Wire, Relays, Circuit Boards, Coils',
    description: 'COPPER WIRE RELAYS CIRCUIT BOARDS COILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_motors_generators_transformers_electric',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4560',
    label: 'Electric Motors, Generators, Transformers',
    description: 'MOTORS, GENERATORS, TRANSFORMERS - ELECTRIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_power_amplifiers_motors_electric',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4561',
    label: 'Power Amplifiers, Electric Motors',
    description: 'POWER AMPLIFIERS MOTORS ELECTRIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_starter_switches_for_flourescent_lamps',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4570',
    label: 'Starter Switches for Fluorescent Lamps',
    description: 'STARTER SWITCHES FOR FLOURESCENT LAMPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_door_control_devices',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4581',
    label: 'Electric Door Control Devices',
    description: 'ELECTRIC DOOR CONTROL DEVICES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_switchgearsmetersfusestransformersrelays_electric',
    parentId: 'tact_s4500',
    level: 'ITEM',
    tactCode: '4583',
    label: 'Switchgear, Meters, Fuses, Transformers, Relays',
    description: 'SWITCHGEARSMETERSFUSESTRANSFORMERSRELAYS ELECTRIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4600',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4600, 4699],
    label: 'Engines and turbines',
    description: 'Engines and turbines',
    isSelectable: false,
  },
  {
    id: 'cmd_engines_turbines',
    parentId: 'tact_s4600',
    level: 'ITEM',
    tactCode: '4600',
    label: 'Engines, Turbines',
    description: 'ENGINES,TURBINES',
    aliases: ['INTERNAL COMBUSTION ENGINES,TURBINES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_diesel_engines_pumpsets',
    parentId: 'tact_s4600',
    level: 'ITEM',
    tactCode: '4616',
    label: 'Diesel Engines, Pumpsets',
    description: 'DIESEL ENGINES, PUMPSETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4700',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4700, 4799],
    label: 'Machinery and tools',
    description: 'Machinery and tools',
    isSelectable: false,
  },
  {
    id: 'cmd_machinery_and_tools',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4703',
    label: 'Machinery and Tools',
    description: 'MACHINERY AND TOOLS .......',
    aliases: [
      'MACHINERY,TOOLS EXCL MACHNS FOR A/CRAFT ARRESTING,CATAPULTIN',
      'MACHINERY TOOLS EXCL.STEAMSHIP/MOTORSHIP SPARE PARTS',
      'MACHINERY, ELECTRIC APPARATUS, TOOLS',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  { id: 'cmd_valves', parentId: 'cmd_machinery_and_tools', level: 'ITEM', label: 'Valves', description: 'VALVES' }, // added from the TK list
  {
    id: 'cmd_agricultural_equipment',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4704',
    label: 'Agricultural Equipment',
    description: 'AGRICULTURAL EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_food_processing_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4705',
    label: 'Food Processing Machinery',
    description: 'FOOD PROCESSING MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_milking_machines_including_accessories',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4706',
    label: 'Milking Machines (incl. Accessories)',
    description: 'MILKING MACHINES INCLUDING ACCESSORIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dairy_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4707',
    label: 'Dairy Machinery',
    description: 'DAIRY MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machines_for_mfg_bottles_washing_bottles',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4708',
    label: 'Machines for Manufacturing Bottles, Washing Bottles',
    description: 'MACHINES  FOR MFG BOTTLES, WASHING BOTTLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machine_parts',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4711',
    label: 'Machine Parts',
    description: 'MACHINE PARTS',
    sortPosition: 3,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_spare_parts',
    parentId: 'cmd_machine_parts',
    level: 'ITEM',
    label: 'Spare Parts',
    description: 'SPARE PARTS',
  }, // added from the TK list
  {
    id: 'cmd_spare_parts_high_value',
    parentId: 'cmd_spare_parts',
    level: 'ITEM',
    label: 'Spare Parts (High Value)',
    description: 'SPARE PARTS (HIGH VALUE)',
  }, // added from the TK list
  {
    id: 'cmd_lwanmowers',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4716',
    label: 'Lawnmowers',
    description: 'LWANMOWERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_moulding_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4719',
    label: 'Molding Machinery',
    description: 'MOULDING MACHINERY',
    aliases: ['MOULDING DEVICES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_insulation_macinery_including_supplies',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4727',
    label: 'Insulation Machinery (incl. Supplies)',
    description: 'INSULATION MACINERY INCLUDING SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machinery_excluding_tools_computers',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4728',
    label: 'Machinery (excl. Tools, Computers)',
    description: 'MACHINERY EXCLUDING TOOLS COMPUTERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_steel_circlips',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4734',
    label: 'Steel Circlips',
    description: 'STEEL CIRCLIPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_extrusion_dies_for_working_aluminium',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4735',
    label: 'Extrusion Dies for Aluminum',
    description: 'EXTRUSION DIES FOR WORKING ALUMINIUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_fr_road_construction_machinery_parts_for_generators',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4741',
    label: 'Parts for Road Construction Machinery and Generators',
    description: 'PARTS FR ROAD CONSTRUCTION MACHINERY,PARTS FOR GENERATORS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machinery_for_oil_drilling_oil_field_servicing_dredging',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4742',
    label: 'Oil Drilling and Oil Field Equipment',
    description: 'MACHINERY FOR OIL DRILLING,OIL FIELD SERVICING,DREDGING',
    aliases: ['***OIL RESEARCHANDDRILLING EQPT, SUPPLIES AND ACCESSORIES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machinery_fr_constructg_earth_movg_conveyg_excavatg_mining',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4747',
    label: 'Machinery for Constructing, Earth Moving, Conveying, Excavating',
    description: 'MACHINERY FR CONSTRUCTG,EARTH MOVG,CONVEYG,EXCAVATG,MINING',
    aliases: ['MACHINERY  FOR CONSTRUCTING, EXCAVATING, CONVEYING'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_drilling_equipment',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4748',
    label: 'Drilling Equipment',
    description: 'DRILLING EQUIPMENT',
    aliases: ['EARTH DRILLING EQUIPMENT'],
    sortPosition: 10,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mining_equipment',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4750',
    label: 'Mining Equipment',
    description: 'MINING EQUIPMENT',
    aliases: ['MINING MACHINERYWELL MACHINERYPUMPING MACHINERY'],
    sortPosition: 14,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pipes_pipe_fittings_sprinkler_systems',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4753',
    label: 'Pipes, Pipe Fittings, Sprinkler Systems',
    description: 'PIPES,PIPE FITTINGS,SPRINKLER SYSTEMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_filters_pumps',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4754',
    label: 'Filters, Pumps',
    description: 'FILTERS, PUMPS',
    aliases: ['PUMPS'],
    sortPosition: 13,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_parts_for_dye_filters',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4755',
    label: 'Parts for Dye Filters',
    description: 'PARTS FOR DYE FILTERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_injection_moulding_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4757',
    label: 'Plastic Injection Molding Machinery',
    description: 'PLASTIC INJECTION MOULDING MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_packing_machinery_wrapping_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4758',
    label: 'Packing and Wrapping Machinery',
    description: 'PACKING MACHINERY WRAPPING MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machines_fr_cuppouch_fillinginjection_mouldingprinting',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4759',
    label: 'Filling, Injection Molding and Printing Machines',
    description: 'MACHINES FR CUPPOUCH FILLINGINJECTION MOULDINGPRINTING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_activating_anodes_for_paper_making_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4761',
    label: 'Anodes for Paper-Making Machinery',
    description: 'ACTIVATING ANODES FOR PAPER MAKING MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machinery_for_sugar_refining',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4769',
    label: 'Sugar Refining Machinery',
    description: 'MACHINERY FOR SUGAR REFINING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_textile_sewing_shoe_machinery',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4780',
    label: 'Textile, Sewing, and Shoe Machinery',
    description: 'TEXTILE/SEWING/SHOE MACHINERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machinery_for_paint_mixing',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4784',
    label: 'Paint Mixing Machinery',
    description: 'MACHINERY FOR PAINT MIXING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_knitting_machines',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4787',
    label: 'Knitting Machines',
    description: 'KNITTING MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sewing_machines_incl_accessories_supplies',
    parentId: 'tact_s4700',
    level: 'ITEM',
    tactCode: '4789',
    label: 'Sewing Machines (incl. Accessories, Supplies)',
    description: 'SEWING MACHINES  INCL ACCESSORIES, SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4800',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4800, 4899],
    label: 'Electrical and mechanical tools',
    description: 'Electrical and mechanical tools',
    isSelectable: false,
  },
  {
    id: 'cmd_tools',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4800',
    label: 'Tools',
    description: 'TOOLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_taps_and_dies',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4803',
    label: 'Taps and Dies',
    description: 'TAPS AND DIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machine_tools',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4811',
    label: 'Machine Tools',
    description: 'MACHINE TOOLS',
    sortPosition: 2,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_abrasive_tools_cutting_tools',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4813',
    label: 'Abrasive Tools, Cutting Tools',
    description: 'ABRASIVE TOOLS, CUTTING TOOLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_twist_masonry_drills_milling_cutters_reamers_tool_bits_hacks',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4816',
    label: 'Twist and Masonry Drills, Milling Cutters, Reamers',
    description: 'TWIST/MASONRY DRILLS,MILLING CUTTERS,REAMERS,TOOL BITS,HACKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cutting_tools_drills',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4818',
    label: 'Cutting Tools, Drills',
    description: 'CUTTING TOOLS DRILLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_universal_filling_guides_grease_guns_wood_files_fixedsaws_ha',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4819',
    label: 'Universal Filling Guides, Grease Guns, Wood Files, Fixtures',
    description: 'UNIVERSAL FILLING GUIDES,GREASE GUNS,WOOD FILES,FIXEDSAWS HA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_hand_tools',
    parentId: 'tact_s4800',
    level: 'ITEM',
    tactCode: '4826',
    label: 'Electric Hand Tools',
    description: 'ELECTRIC HAND TOOLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s4900',
    parentId: 'tact_g4',
    level: 'SUB_GROUP',
    range: [4900, 4999],
    label: 'Miscellaneous mechanical products',
    description: 'Miscellaneous mechanical products',
    isSelectable: false,
  },
  {
    id: 'cmd_pipes_pipe_fittings',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4902',
    label: 'Pipes, Pipe Fittings',
    description: 'PIPES, PIPE FITTINGS',
    aliases: ['PIPES PIPE FITTINGS PIPE FLANGES HOSES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_chain_locks',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4903',
    label: 'Chain Locks',
    description: 'CHAIN LOCKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ball_bearings_roller_bearings',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4911',
    label: 'Ball Bearings, Roller Bearings',
    description: 'BALL BEARINGS, ROLLER BEARINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pneumatic_equipment_hydraulic_equipment_tools',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4954',
    label: 'Pneumatic Equipment, Hydraulic Equipment, Tools',
    description: 'PNEUMATIC EQUIPMENT, HYDRAULIC EQUIPMENT, TOOLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_automatic_door_closers',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4955',
    label: 'Automatic Door Closers',
    description: 'AUTOMATIC DOOR CLOSERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pistons_piston_rings',
    parentId: 'tact_s4900',
    level: 'ITEM',
    tactCode: '4969',
    label: 'Pistons, Piston Rings',
    description: 'PISTONS, PISTON RINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g5',
    level: 'GROUP',
    range: [5000, 5999],
    label: 'Non-metallic minerals and manufactures',
    description: 'Non-metallic minerals and manufactures',
    isSelectable: false,
  },
  {
    id: 'tact_s5000',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5000, 5099],
    label: 'Abrasives and abrasive products',
    description: 'Abrasives and abrasive products',
    isSelectable: false,
  },
  {
    id: 'cmd_abrasives_abrasive_products_grinding_wheels',
    parentId: 'tact_s5000',
    level: 'ITEM',
    tactCode: '5050',
    label: 'Abrasives, Abrasive Products, Grinding Wheels',
    description: 'ABRASIVES,ABRASIVE PRODUCTS,GRINDING WHEELS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5100',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5100, 5149],
    label: 'Asbestos products',
    description: 'Asbestos products',
    isSelectable: false,
  },
  {
    id: 'cmd_asbestos_products',
    parentId: 'tact_s5100',
    level: 'ITEM',
    tactCode: '5100',
    label: 'Asbestos Products',
    description: 'ASBESTOS PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5150',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5150, 5199],
    label: 'Carbon and graphite products',
    description: 'Carbon and graphite products',
    isSelectable: false,
  },
  {
    id: 'cmd_graphite',
    parentId: 'tact_s5150',
    level: 'ITEM',
    tactCode: '5160',
    label: 'Graphite',
    description: 'GRAPHITE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5200',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5200, 5299],
    label:
      'Ceramics, pottery, chinaware, earthenware, stoneware, crystalware, porcelain, glassware, excluding synthetic stones and optical goods',
    description:
      'Ceramics, pottery, chinaware, earthenware, stoneware, crystalware, porcelain, glassware, excluding synthetic stones and optical goods',
    isSelectable: false,
  },
  {
    id: 'cmd_clay_products',
    parentId: 'tact_s5200',
    level: 'ITEM',
    tactCode: '5200',
    label: 'Clay Products',
    description: 'CLAY PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sanitary_articles_made_of_pottery_porcelain',
    parentId: 'tact_s5200',
    level: 'ITEM',
    tactCode: '5235',
    label: 'Sanitary Articles of Pottery, Porcelain',
    description: 'SANITARY ARTICLES  MADE OF POTTERY,PORCELAIN',
    aliases: ['COMMODESSINKSTILES MADE OF POTTERY PORCELAIN'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_telephone_marble_bases',
    parentId: 'tact_s5200',
    level: 'ITEM',
    tactCode: '5293',
    label: 'Marble Telephone Bases',
    description: 'TELEPHONE MARBLE BASES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pottery_ceramics',
    parentId: 'tact_s5200',
    level: 'ITEM',
    tactCode: '5295',
    label: 'Pottery, Ceramics, China, Porcelain',
    description: 'POTTERY, CERAMICS',
    aliases: [
      'CERAMICS,POTTERY,CHINA/EARTHEN/STONE/CRYSTALWARE,PORCELAIN C',
      'CERAMICS,POTTERY,CHINA/EARTHEN/STONE/GLASS/CRYSTALWARE,PORCE',
      'CHINA PORCELAIN',
      'CERAMICS',
      'POTTERY,CHINA/EARTHEN/CRYSTALWARE,PORCELAIN WHOLLY IRISH MFR',
      'POTTERY,CHINA/EARTHEN/STONE/CRYSTAL/GLASSWARE EXCL ARTIFCL S',
      'POTTERY',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5300',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5300, 5399],
    label: 'Glass and glass products',
    description: 'Glass and glass products',
    isSelectable: false,
  },
  {
    id: 'cmd_glass_glass_products',
    parentId: 'tact_s5300',
    level: 'ITEM',
    tactCode: '5300',
    label: 'Glass, Glass Products',
    description: 'GLASS, GLASS PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_glass_bottles_excluding_crystalware',
    parentId: 'tact_s5300',
    level: 'ITEM',
    tactCode: '5321',
    label: 'Glass Bottles',
    description: 'GLASS BOTTLES EXCLUDING CRYSTALWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_glassware_incl_glass_lampshades_associatd_elec_fxtrs_ftngs',
    parentId: 'tact_s5300',
    level: 'ITEM',
    tactCode: '5351',
    label: 'Glassware (incl. Glass Lampshades, Electrical Fittings)',
    description: 'GLASSWARE  INCL GLASS LAMPSHADES, ASSOCIATD ELEC FXTRS/FTNGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5400',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5400, 5449],
    label: 'Marble',
    description: 'Marble',
    isSelectable: false,
  },
  {
    id: 'cmd_marble',
    parentId: 'tact_s5400',
    level: 'ITEM',
    tactCode: '5400',
    label: 'Marble',
    description: 'MARBLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fireplaces_marble',
    parentId: 'tact_s5400',
    level: 'ITEM',
    tactCode: '5406',
    label: 'Fireplaces, Marble',
    description: 'FIREPLACES  MARBLE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5500',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5500, 5599],
    label: 'Mica and mica products',
    description: 'Mica and mica products',
    isSelectable: false,
  },
  {
    id: 'cmd_mica_mica_products',
    parentId: 'tact_s5500',
    level: 'ITEM',
    tactCode: '5500',
    label: 'Mica, Mica Products',
    description: 'MICA, MICA PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mica_capacitorscondenser_filmsblocks_musctoviteandfabricated',
    parentId: 'tact_s5500',
    level: 'ITEM',
    tactCode: '5501',
    label: 'Mica Capacitors, Condenser Films, Blocks',
    description: 'MICA CAPACITORSCONDENSER FILMSBLOCKS MUSCTOVITEANDFABRICATED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5600',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5600, 5699],
    label: 'Petroleum and petroleum products',
    description: 'Petroleum and petroleum products',
    isSelectable: false,
  },
  {
    id: 'cmd_oils_refined',
    parentId: 'tact_s5600',
    level: 'ITEM',
    tactCode: '5620',
    label: 'Refined Oils',
    description: 'OILS  REFINED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5700',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5700, 5799],
    label: 'Quartz and quartz manufactures',
    description: 'Quartz and quartz manufactures',
    isSelectable: false,
  },
  {
    id: 'cmd_quartz',
    parentId: 'tact_s5700',
    level: 'ITEM',
    tactCode: '5700',
    label: 'Quartz',
    description: 'QUARTZ',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_amethyst_malchite_mica_silica_quartz_semi_precious_stons_raw',
    parentId: 'tact_s5700',
    level: 'ITEM',
    tactCode: '5710',
    label: 'Amethyst, Malachite, Mica, Silica, Quartz, Semi-Precious Stones (Raw)',
    description: 'AMETHYST,MALCHITE,MICA,SILICA,QUARTZ,SEMI-PRECIOUS STONS-RAW',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_quartz_manufactures',
    parentId: 'tact_s5700',
    level: 'ITEM',
    tactCode: '5750',
    label: 'Quartz Manufactures, Silica Products',
    description: 'QUARTZ MANUFACTURES',
    aliases: ['QUARTZ SILICA PRODUCTS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5800',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5800, 5899],
    label: 'Stones',
    description: 'Stones',
    isSelectable: false,
  },
  {
    id: 'cmd_stones_for_building_construction',
    parentId: 'tact_s5800',
    level: 'ITEM',
    tactCode: '5819',
    label: 'Stones for Building Construction',
    description: 'STONES FOR BUILDING CONSTRUCTION',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_semi_precious_stones',
    parentId: 'tact_s5800',
    level: 'ITEM',
    tactCode: '5851',
    label: 'Semi-Precious Stones',
    description: 'SEMI-PRECIOUS STONES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_artificial_glass_stones_glass_plastic_pears_unset_unmounted',
    parentId: 'tact_s5800',
    level: 'ITEM',
    tactCode: '5858',
    label: 'Artificial and Glass Stones, Glass and Plastic Pearls (Unset)',
    description: 'ARTIFICIAL/GLASS STONES, GLASS/PLASTIC PEARS,UNSET,UNMOUNTED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s5900',
    parentId: 'tact_g5',
    level: 'SUB_GROUP',
    range: [5900, 5999],
    label: 'Miscellaneous non-metallic minerals and manufactures',
    description: 'Miscellaneous non-metallic minerals and manufactures',
    isSelectable: false,
  },
  {
    id: 'cmd_granite_slabs',
    parentId: 'tact_s5900',
    level: 'ITEM',
    tactCode: '5900',
    label: 'Granite Slabs',
    description: 'GRANITE SLABS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_geological_samples_excluding_ore',
    parentId: 'tact_s5900',
    level: 'ITEM',
    tactCode: '5901',
    label: 'Geological Samples (excl. Ore)',
    description: 'GEOLOGICAL SAMPLES  EXCLUDING ORE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mineral_specimen',
    parentId: 'tact_s5900',
    level: 'ITEM',
    tactCode: '5902',
    label: 'Mineral Specimens',
    description: 'MINERAL SPECIMEN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g6',
    level: 'GROUP',
    range: [6000, 6999],
    label: 'Chemicals and related products',
    description: 'Chemicals and related products',
    isSelectable: false,
  },
  {
    id: 'tact_s6000',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6000, 6099],
    label: 'Chemicals, pharmaceuticals',
    description: 'Chemicals, pharmaceuticals',
    isSelectable: false,
  },
  {
    id: 'cmd_chemicals_pharmaceuticals',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6000',
    label: 'Chemicals, Drugs, Pharmaceuticals',
    description: 'CHEMICALS, PHARMACEUTICALS',
    aliases: [
      'CHEM,CHEM PRODS,DRUGS,COSMETICS,PARMA,OIL EXCL PLASTIC/TOILE',
      'CHEM/CHEM PDTS/DRUGS/COSMETCS/PHARMA/ESSENTL  OILS/PLASTIC',
      'CHEM,CHEM PRODS,DRUGS,COSMETCS,PHARMA,ESSENTIAL OILS,PLASTIC',
      'CHMCLS,DRUGS PARMA,MEDICINE EXCL COSMETICS,TOILET PREPS/ARTI',
      'CHEM/DRUGS/PHARMA/MEDICINES/SURGICAL GAUZE/BANDAGES/PAINTS/V',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PHARMA,
  },
  {
    id: 'cmd_insulin_chemicals_drugs_pharmaceuticals_medicines',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6001',
    label: 'Insulin, Chemicals, Drugs, Pharmaceuticals, Medicines',
    description: 'INSULIN, CHEMICALS, DRUGS, PHARMACEUTICALS, MEDICINES',
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_dyes_chem_fert_insecticides_paints_oils_plastics',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6005',
    label: 'Chemicals, Dyes, Fertilizers, Insecticides, Paints, Oils',
    description: 'DYES/CHEM/FERT/INSECTICIDES/PAINTS/OILS/PLASTICS',
    aliases: [
      'CHEM/DYES/FERT/INSECTICIDES/PAINTS/DRUGS/COSMETICS/PLASTICS',
      'DYES/FERT/INSECTICIDES/PAINTS/PIGMTS/COSMETICS/PLASTICS/OILS',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_foll_prods_incl_brand_nms_prods_of_ici_subs_asso_co_atromid',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6006',
    label: 'Pharmaceutical Products (ICI Brands)',
    description: 'FOLL.PRODS.INCL.BRAND NMS,PRODS.OF ICI/SUBS/ASSO.CO.:ATROMID',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_surgical_gauzes_bandages_cotton_wool_sanitary_towels',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6007',
    label: 'Surgical Gauze, Bandages, Cotton Wool, Sanitary Products',
    description: 'SURGICAL GAUZES/BANDAGES/COTTON WOOL/SANITARY TOWELS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_healthcare_products',
    parentId: 'cmd_surgical_gauzes_bandages_cotton_wool_sanitary_towels',
    level: 'ITEM',
    label: 'Healthcare Products',
    description: 'HEALTHCARE PRODUCTS',
  }, // added from the TK list
  {
    id: 'cmd_chemical_reagents',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6010',
    label: 'Chemical Reagents',
    description: 'CHEMICAL REAGENTS',
    sortPosition: 5,
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_lab_reagents_test_kits',
    parentId: 'cmd_chemical_reagents',
    level: 'ITEM',
    label: 'Lab Reagents, Test Kits',
    description: 'LAB REAGENTS, TEST KITS',
  }, // added from the TK list
  {
    id: 'cmd_radioactive_mtrls_for_medical_laboratory_use',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6012',
    label: 'Radioactive Materials for Medical or Laboratory Use',
    description: 'RADIOACTIVE MTRLS  FOR MEDICAL/LABORATORY USE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_polypropylene_film_metalised_granules',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6030',
    label: 'Metallized Polypropylene Film, Granules',
    description: 'POLYPROPYLENE FILM METALISED /GRANULES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_essentail_oils_perfumes_toilet_preparation_soap_toilet_water',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6050',
    label: 'Essential Oils, Perfumes, Toilet Preparations, Soap, Toiletries',
    description: 'ESSENTAIL OILS,PERFUMES,TOILET PREPARATION,SOAP,TOILET WATER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_vaccines',
    parentId: 'tact_s6000',
    level: 'ITEM',
    tactCode: '6099',
    label: 'Vaccines',
    description: 'VACCINES',
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'tact_s6100',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6100, 6199],
    label: 'Biologicals, human dried blood protein',
    description: 'Biologicals, human dried blood protein',
    isSelectable: false,
  },
  {
    id: 'cmd_biologicals_semen_samples_urine_samples_bacterial_cultures',
    parentId: 'tact_s6100',
    level: 'ITEM',
    tactCode: '6100',
    label: 'Biologicals, Semen Samples, Urine Samples, Bacterial Cultures',
    description: 'BIOLOGICALS, SEMEN SAMPLES, URINE SAMPLES, BACTERIAL CULTURES',
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_bone_marrow',
    parentId: 'cmd_biologicals_semen_samples_urine_samples_bacterial_cultures',
    level: 'ITEM',
    label: 'Bone Marrow',
    description: 'BONE MARROW',
  }, // added from the TK list
  {
    id: 'cmd_diagnostic_specimens_not_restricted',
    parentId: 'cmd_biologicals_semen_samples_urine_samples_bacterial_cultures',
    level: 'ITEM',
    label: 'Diagnostic Specimens (Not Restricted)',
    description: 'DIAGNOSTIC SPECIMENS (NOT RESTRICTED)',
  }, // added from the TK list
  {
    id: 'cmd_vitamins_antibiotics',
    parentId: 'tact_s6100',
    level: 'ITEM',
    tactCode: '6103',
    label: 'Vitamins, Antibiotics',
    description: 'VITAMINS,ANTIBIOTICS',
    sortPosition: 2,
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_blood_serum_haemoderivatives_bloodplasma',
    parentId: 'tact_s6100',
    level: 'ITEM',
    tactCode: '6107',
    label: 'Blood Serum, Hemoderivatives, Blood Plasma',
    description: 'BLOOD SERUM, HAEMODERIVATIVES, BLOODPLASMA',
    aliases: ['BLOODSERUM'],
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_human_blood',
    parentId: 'tact_s6100',
    level: 'ITEM',
    tactCode: '6109',
    label: 'Human Blood',
    description: 'HUMAN BLOOD',
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_plastic_surgical_equipment_human_plasma',
    parentId: 'tact_s6100',
    level: 'ITEM',
    tactCode: '6110',
    label: 'Plastic Surgical Equipment, Human Plasma',
    description: 'PLASTIC SURGICAL EQUIPMENT, HUMAN PLASMA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6200',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6200, 6299],
    label: 'Chemicals',
    description: 'Chemicals',
    isSelectable: false,
  },
  {
    id: 'cmd_chemicals',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6200',
    label: 'Chemicals',
    description: 'CHEMICALS',
    sortPosition: 11,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_mosquito_coils',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6202',
    label: 'Mosquito Coils',
    description: 'MOSQUITO COILS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dyestuffs',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6206',
    label: 'Dyestuffs',
    description: 'DYESTUFFS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_xanthophyll_concentrate',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6208',
    label: 'Xanthophyll Concentrate',
    description: 'XANTHOPHYLL CONCENTRATE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_datura_extract',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6211',
    label: 'Datura Extract',
    description: 'DATURA EXTRACT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dry_cochineals',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6212',
    label: 'Dry Cochineals',
    description: 'DRY COCHINEALS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dry_ice',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6223',
    label: 'Dry Ice',
    description: 'DRY ICE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_indl_chemicals_testing_eqpt_made_of_glass',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6224',
    label: 'Industrial Chemicals, Glass Testing Equipment',
    description: 'INDL CHEMICALS, TESTING EQPT  MADE OF GLASS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pyrethrum_extract',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6227',
    label: 'Pyrethrum Extract',
    description: 'PYRETHRUM EXTRACT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ethabutol_powder',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6234',
    label: 'Ethambutol Powder',
    description: 'ETHABUTOL POWDER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_insecticide',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6240',
    label: 'Insecticide',
    description: 'INSECTICIDE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_veterinary_medicinal_dietary_additive',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6251',
    label: 'Veterinary Medicinal Dietary Additive',
    description: 'VETERINARY MEDICINAL DIETARY ADDITIVE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_quinine_alkaloid',
    parentId: 'tact_s6200',
    level: 'ITEM',
    tactCode: '6271',
    label: 'Quinine Alkaloid',
    description: 'QUININE  ALKALOID',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6400',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6400, 6499],
    label: 'Drugs',
    description: 'Drugs',
    isSelectable: false,
  },
  {
    id: 'cmd_ginseng_root',
    parentId: 'tact_s6400',
    level: 'ITEM',
    tactCode: '6425',
    label: 'Ginseng Root',
    description: 'GINSENG ROOT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_opium',
    parentId: 'tact_s6400',
    level: 'ITEM',
    tactCode: '6435',
    label: 'Opium',
    description: 'OPIUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_drugs_medicine_pharma_insecticide_air_freshner_aerosol_can',
    parentId: 'tact_s6400',
    level: 'ITEM',
    tactCode: '6495',
    label: 'Drugs, Medicine, Pharmaceuticals, Insecticide, Air Freshener Aerosol',
    description: 'DRUGS,MEDICINE,PHARMA.,INSECTICIDE,AIR FRESHNER AEROSOL CAN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_narcotics_controlled_drugs',
    parentId: 'cmd_drugs_medicine_pharma_insecticide_air_freshner_aerosol_can',
    level: 'ITEM',
    label: 'Narcotics (Controlled Drugs)',
    description: 'NARCOTICS (CONTROLLED DRUGS)',
  }, // added from the TK list
  {
    id: 'tact_s6500',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6500, 6599],
    label: 'Pharmaceuticals',
    description: 'Pharmaceuticals',
    isSelectable: false,
  },
  {
    id: 'cmd_general_pharma',
    parentId: 'tact_s6500',
    level: 'ITEM',
    label: 'General Pharma',
    description: 'General Pharma',
    sortPosition: 1,
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_pharmaceuticals',
    parentId: 'tact_s6500',
    level: 'ITEM',
    tactCode: '6500',
    label: 'Pharmaceuticals, Medicines',
    description: 'PHARMACEUTICALS',
    aliases: ['MEDICINES, PHARMACEUTICALS'],
    sortPosition: 3,
    allowedCargo: CARGO_PROFILES.PHARMA,
  },
  {
    id: 'cmd_pharmaceuticals_high_value',
    parentId: 'cmd_pharmaceuticals',
    level: 'ITEM',
    label: 'Pharmaceuticals (High Value)',
    description: 'PHARMACEUTICALS (HIGH VALUE)',
  }, // added from the TK list
  {
    id: 'cmd_veterinary_pharmaceuticals',
    parentId: 'tact_s6500',
    level: 'ITEM',
    tactCode: '6502',
    label: 'Veterinary Pharmaceuticals',
    description: 'VETERINARY PHARMACEUTICALS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_gelatine_capsules_for_medicines',
    parentId: 'tact_s6500',
    level: 'ITEM',
    tactCode: '6505',
    label: 'Empty Gelatine Capsules for Medicines',
    description: 'EMPTY GELATINE CAPSULES FOR MEDICINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bismuth_cadmium',
    parentId: 'tact_s6500',
    level: 'ITEM',
    tactCode: '6511',
    label: 'Bismuth, Cadmium',
    description: 'BISMUTH, CADMIUM',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6600',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6600, 6699],
    label: 'Cosmetics, soaps, toilet preparations',
    description: 'Cosmetics, soaps, toilet preparations',
    isSelectable: false,
  },
  {
    id: 'cmd_cosmetics_soaps_toilet_preparations',
    parentId: 'tact_s6600',
    level: 'ITEM',
    tactCode: '6600',
    label: 'Cosmetics, Soaps, Toilet Preparations',
    description: 'COSMETICS,SOAPS,TOILET PREPARATIONS',
    aliases: [
      'COSMETICS,TOILET PREPARATIONS/ARTICLES, PERFUMES',
      'TOILET ARTICLES/TOILET WATER/PERFUMES/COSMETICS-ALONE OR TOG',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_perfumes',
    parentId: 'tact_s6600',
    level: 'ITEM',
    tactCode: '6630',
    label: 'Perfumes, Toilet Water',
    description: 'PERFUMES',
    aliases: ['PERFUME, TOILET WATER'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_agarbathis',
    parentId: 'tact_s6600',
    level: 'ITEM',
    tactCode: '6682',
    label: 'Agarbatti (Incense Sticks)',
    description: 'AGARBATHIS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6750',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6750, 6799],
    label: 'Paints, pigments, varnishes',
    description: 'Paints, pigments, varnishes',
    isSelectable: false,
  },
  {
    id: 'cmd_paints_pigments_varnishes',
    parentId: 'tact_s6750',
    level: 'ITEM',
    tactCode: '6750',
    label: 'Paints, Pigments, Varnishes',
    description: 'PAINTS/PIGMENTS/VARNISHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_printing_ink_lacquers_rubber_stamps_ink_pad',
    parentId: 'tact_s6750',
    level: 'ITEM',
    tactCode: '6751',
    label: 'Printing Ink, Lacquers, Rubber Stamps',
    description: 'PRINTING INK/LACQUERS, RUBBER STAMPS, INK PAD',
    aliases: ['PRINTING INK'],
    sortPosition: 17,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6800',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6800, 6899],
    label: 'Plastic materials and articles; synthetic gums and resins',
    description: 'Plastic materials and articles; synthetic gums and resins',
    isSelectable: false,
  },
  {
    id: 'cmd_fibreglass_fibreglass_reinforcing_material',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6801',
    label: 'Fiberglass, Fiberglass Reinforcing Material',
    description: 'FIBREGLASS, FIBREGLASS REINFORCING MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_artificial_gums_resins_plastic_manufs_in_sheets_slabs_rod',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6805',
    label: 'Artificial Gums, Resins, Plastic Sheets, Slabs, Rods',
    description: 'ARTIFICIAL GUMS/RESINS, PLASTIC MANUFS. IN SHEETS, SLABS,ROD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_natural_resins',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6806',
    label: 'Natural Resins',
    description: 'NATURAL RESINS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_foils_plastic_sheets',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6807',
    label: 'Plastic Foils, Plastic Sheets',
    description: 'PLASTIC FOILS PLASTIC SHEETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_articles',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6810',
    label: 'Plastic Articles',
    description: 'PLASTIC ARTICLES',
    sortPosition: 16,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_insulating_tubings',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6812',
    label: 'Insulating Tubing',
    description: 'INSULATING TUBINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_doilies',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6817',
    label: 'Plastic Doilies',
    description: 'PLASTIC DOILIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_kitchen_table_decorative_ware_toys',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6824',
    label: 'Plastic Kitchen/Table/Decorative Ware, Toys',
    description: 'PLASTIC KITCHEN/TABLE/DECORATIVE WARE, TOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_articles_for_household_use',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6827',
    label: 'Plastic Articles for Household Use',
    description: 'PLASTIC ARTICLES (FOR HOUSEHOLD USE)',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_packaging_material',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6831',
    label: 'Plastic Packaging Material',
    description: 'PLASTIC PACKAGING MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_blood_bags_cellophane_polyethylene_bags_rolls_for_wr',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6847',
    label: 'Plastic Blood Bags, Cellophane, Polyethylene Bags, Rolls (for Wrapping)',
    description: 'PLASTIC BLOOD BAGS, CELLOPHANE, POLYETHYLENE BAGS, ROLLS (FOR WRAPPING)',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_containers',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6855',
    label: 'Plastic Containers',
    description: 'PLASTIC CONTAINERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_articles_used_in_animal_husbandry',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6857',
    label: 'Plastic Articles Used in Animal Husbandry',
    description: 'PLASTIC ARTICLES  USED IN ANIMAL HUSBANDRY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_machine_partsautomobile_weather_shields_made_of_plastic',
    parentId: 'tact_s6800',
    level: 'ITEM',
    tactCode: '6858',
    label: 'Plastic Weather Shields, Automotive Parts',
    description: 'MACHINE PARTSAUTOMOBILE WEATHER SHIELDS MADE OF PLASTIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s6900',
    parentId: 'tact_g6',
    level: 'SUB_GROUP',
    range: [6900, 6999],
    label: 'Cleansing and polishing materials',
    description: 'Cleansing and polishing materials',
    isSelectable: false,
  },
  {
    id: 'cmd_detergents_soaps_soap_flakes_powder_other_cleaning_mtrls',
    parentId: 'tact_s6900',
    level: 'ITEM',
    tactCode: '6900',
    label: 'Detergents, Soaps, Soap Flakes and Powders, Cleaning Supplies',
    description: 'DETERGENTS,SOAPS,SOAP FLAKES/POWDER OTHER CLEANING MTRLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_polishes_paste_powders_preparations_ised_for_polishing_prese',
    parentId: 'tact_s6900',
    level: 'ITEM',
    tactCode: '6901',
    label: 'Polishes, Pastes, Powders, and Preparations Used for Polishing',
    description: 'POLISHES,PASTE,POWDERS,PREPARATIONS-ISED FOR POLISHING,PRESE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cleansing_polishing_products',
    parentId: 'tact_s6900',
    level: 'ITEM',
    tactCode: '6902',
    label: 'Cleansing, Polishing Products',
    description: 'CLEANSING, POLISHING PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_anti_corrosionsealing_prods_for_protctn_of_metalc_structure',
    parentId: 'tact_s6900',
    level: 'ITEM',
    tactCode: '6910',
    label: 'Anti-Corrosion and Sealing Products for Metal Structures',
    description: 'ANTI CORROSIONSEALING PRODS FOR PROTCTN OF METALC STRUCTURE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g7',
    level: 'GROUP',
    range: [7000, 7999],
    label: 'Paper, reed, rubber and wood manufactures',
    description: 'Paper, reed, rubber and wood manufactures',
    isSelectable: false,
  },
  {
    id: 'tact_s7000',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7000, 7099],
    label: 'Paper and paper products',
    description: 'Paper and paper products',
    isSelectable: false,
  },
  {
    id: 'cmd_paper_cardboard_in_sheets_rolls_excl_prntd_matter_photo_pape',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7003',
    label: 'Paper, Cardboard in Sheets, Rolls',
    description: 'PAPER,CARDBOARD-IN SHEETS,ROLLS/EXCL PRNTD MATTER,PHOTO PAPE',
    aliases: ['PAPER  SHEETS,ROLLS/EXCL PRNTD MATTER, PHOTOGRAPHIC PAPER'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_paper_packaging_material',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7021',
    label: 'Paper and Cardboard Packaging Material',
    description: 'PAPER PACKAGING MATERIAL',
    aliases: ['CARDBOARD PACKAGING MATERIAL'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_packaging',
    parentId: 'cmd_paper_packaging_material',
    level: 'ITEM',
    label: 'Packaging',
    description: 'PACKAGING',
  }, // added from the TK list
  {
    id: 'cmd_carbon_paperhot_stamping_paper',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7022',
    label: 'Carbon Paper, Hot Stamping Paper',
    description: 'CARBON PAPERHOT STAMPING PAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cigarette_papers_filters_tips_packaging_material',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7025',
    label: 'Cigarette Papers, Filters, Tips, Packaging Material',
    description: 'CIGARETTE PAPERS, FILTERS, TIPS, PACKAGING MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_security_paper',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7026',
    label: 'Security Paper',
    description: 'SECURITY PAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_suface_coated_paper_in_rolls_sheets',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7031',
    label: 'Surface Coated Paper in Rolls, Sheets',
    description: 'SUFACE COATED PAPER  IN ROLLS, SHEETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cable_insulation_paper_tape',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7032',
    label: 'Cable Insulation Paper Tape',
    description: 'CABLE INSULATION PAPER TAPE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_adhesive_tape_adhesive_tape_dispensers',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7039',
    label: 'Adhesive Tape, Adhesive Tape Dispensers',
    description: 'ADHESIVE TAPE, ADHESIVE TAPE DISPENSERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_photographic_sensitized_paper',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7046',
    label: 'Photographic Sensitized Paper',
    description: 'PHOTOGRAPHIC SENSITIZED PAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_transfers_decalcomania',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7047',
    label: 'Transfers (Decalcomania)',
    description: 'TRANSFERS DECALCOMANIA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tissue_paper',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7050',
    label: 'Tissue Paper',
    description: 'TISSUE PAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_paperplastic_impregnatd_with_dyeg_pigmts_for_ind_txtl_prntg',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7053',
    label: 'Paper and Plastic Impregnated with Dye Pigments',
    description: 'PAPERPLASTIC IMPREGNATD WITH DYEG PIGMTS FOR IND TXTL PRNTG',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wallpaper',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7056',
    label: 'Wallpaper',
    description: 'WALLPAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cards_checks_tkts_tktrolls_lbls_of_paper_metal_plastic',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7067',
    label: 'Paper Cards, Checks, Tickets, Ticket Rolls, Labels',
    description: 'CARDS/CHECKS/TKTS/TKTROLLS/LBLS OF PAPER,METAL,PLASTIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_paper_paterns',
    parentId: 'tact_s7000',
    level: 'ITEM',
    tactCode: '7080',
    label: 'Paper Patterns',
    description: 'PAPER PATERNS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7100',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7100, 7199],
    label: 'Printed matter',
    description: 'Printed matter',
    isSelectable: false,
  },
  {
    id: 'cmd_administrative_reports',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7108',
    label: 'Administrative Reports',
    description: 'ADMINISTRATIVE REPORTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_newspapers_magazines_periodicals',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7110',
    label: 'Newspapers, Magazines, Periodicals',
    description: 'NEWSPAPERS,MAGAZINES,PERIODICALS',
    aliases: [
      'MAGS, PERIODICALS EXCLUDING NEWSPAPERS',
      'MAGAZINES,PERIODCALS PUBLISHED ONCE WEEKLY OR LONGER INTERVALS - BOOKS EXCLUDING PHOTOS',
      'MAGAZINES, PERIODICALS, NEWSPAPERS - PUBLISHED ONCE WEEKLY OR AT LONGER INTERVALS',
      'NEWSPAPERS',
      'NEWSPAPERS AND MAGAZINES',
      'NEWSPAPERS  DAILY',
      'NEWSPAPERS, MAGAZINES',
      'NEWSPPR/MAGAZINES/PERIODCLS EXCL STAMPS,SHARECPNS,PRESS PHOT',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_football_pool_coupons',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7111',
    label: 'Football Pool Coupons',
    description: 'FOOTBALL POOL COUPONS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_newspeprs_magazines_periodicals_books',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7112',
    label: 'Newspapers, Magazines, Periodicals, Books',
    description: 'NEWSPEPRS,MAGAZINES,PERIODICALS,BOOKS',
    aliases: [
      'NEWSPAPER,MAGS,PERIODICALS,BOOKS PRNTD MATTER,PHOTOGRAPHS',
      'NEWP/MAG/PERS/BOOKS',
      'NWP.,MAGS,PERIODICALS,BOOKS,PRINTED MATTER EXCL.POSTAGE STAM',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_books',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7119',
    label: 'Books, Catalogs, Pamphlets',
    description: 'BOOKS',
    aliases: ['BOOKS CATALOGUES, PAMPHLETS', 'BOOKS, MAGAZINES, CATALOGUES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_religious_books',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7123',
    label: 'Religious Books',
    description: 'RELIGIOUS BOOKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_books_postcards_greeting_cards_calendars_credit_card_blanks',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7127',
    label:
      'Books, Postcards, Greeting Cards, Calendars, Credit Card Blanks, Paper Envelopes, Paper Bags, Table Decorations (Made of Paper), Advertising Posters, Picture Reproductions, Phonograph Record Covers',
    description:
      'BOOKS, POSTCARDS, GREETING CARDS, CALENDARS, CREDIT CARD BLANKS, PAPER ENVELOPES, PAPER BAGS, TABLE DECORATIONS (MADE OF PAPER), ADVERTISING POSTERS, PICTURE REPRODUCTIONS, PHONOGRAPH RECORD COVERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'cmd_textbooks_instrumts_visual_teachg_aids_for_use_educatnl_inst',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7159',
    label: 'Textbooks, Instruments, Visual Teaching Aids for Educational Institutions',
    description: 'TEXTBOOKS,INSTRUMTS,VISUAL TEACHG AIDS-FOR USE EDUCATNL INST',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_record_covers',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7163',
    label: 'Record Covers',
    description: '***** RECORD COVERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_self_adhesive_labels',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7179',
    label: 'Self-Adhesive Labels',
    description: 'SELF-ADHESIVE LABELS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_labels_adhesive_tape_dispenser_cardboard_carton_unassembled',
    parentId: 'tact_s7100',
    level: 'ITEM',
    tactCode: '7181',
    label: 'Labels, Adhesive Tape, Cardboard Cartons',
    description: 'LABELS,ADHESIVE TAPE,DISPENSER,CARDBOARD CARTON UNASSEMBLED',
    aliases: ['LABELS ,CARBOARD CARTONS UNASSEMBLED ,OFC STATIONERY,PROMOTIO'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7300',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7300, 7399],
    label: 'Reeds and reed products',
    description: 'Reeds and reed products',
    isSelectable: false,
  },
  {
    id: 'cmd_willow_articles_woven',
    parentId: 'tact_s7300',
    level: 'ITEM',
    tactCode: '7381',
    label: 'Woven Willow Articles',
    description: 'WILLOW ARTICLES  WOVEN',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_basketware',
    parentId: 'tact_s7300',
    level: 'ITEM',
    tactCode: '7382',
    label: 'Basketware',
    description: 'BASKETWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7400',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7400, 7499],
    label: 'Rubber and rubber products',
    description: 'Rubber and rubber products',
    isSelectable: false,
  },
  {
    id: 'cmd_rubber_rubber_products',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7400',
    label: 'Rubber, Rubber Products',
    description: 'RUBBER, RUBBER PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rolls_sheets_slabs_unfinished_forms_made_of_rubber',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7415',
    label: 'Rubber Rolls, Sheets, Slabs, Unfinished Forms',
    description: 'ROLLS,SHEETS,SLABS,UNFINISHED FORMS-MADE OF RUBBER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rubber_goods',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7420',
    label: 'Rubber Goods',
    description: 'RUBBER GOODS',
    sortPosition: 18,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rubber_belts_rubber_belting',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7425',
    label: 'Rubber Belts, Rubber Belting',
    description: 'RUBBER BELTS,RUBBER BELTING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_druggists_sundries_of_rubber',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7430',
    label: "Druggists' Sundries of Rubber",
    description: 'DRUGGISTS SUNDRIES OF RUBBER',
    aliases: ['RUBBER DOUCHE BAG SETS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rubber_boots',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7437',
    label: 'Rubber Boots',
    description: 'RUBBER BOOTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rubber_gloves',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7440',
    label: 'Rubber Gloves',
    description: 'RUBBER GLOVES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_rubber_seals',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7448',
    label: 'Rubber Seals',
    description: 'RUBBER SEALS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  { id: 'cmd_gaskets', parentId: 'cmd_rubber_seals', level: 'ITEM', label: 'Gaskets', description: 'GASKETS' }, // added from the TK list
  {
    id: 'cmd_rubber_tyres_rubber_tubes',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7481',
    label: 'Rubber Tires, Rubber Tubes',
    description: 'RUBBER TYRES, RUBBER TUBES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sealing_rings_made_of_artificial_rubber',
    parentId: 'tact_s7400',
    level: 'ITEM',
    tactCode: '7483',
    label: 'Artificial Rubber Sealing Rings',
    description: 'SEALING RINGS  MADE OF ARTIFICIAL RUBBER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7500',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7500, 7599],
    label: 'Straw and grass products',
    description: 'Straw and grass products',
    isSelectable: false,
  },
  {
    id: 'cmd_straw_products_grass_products',
    parentId: 'tact_s7500',
    level: 'ITEM',
    tactCode: '7500',
    label: 'Straw Products, Grass Products',
    description: 'STRAW PRODUCTS, GRASS PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7600',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7600, 7699],
    label: 'Wood and wood products; cork',
    description: 'Wood and wood products; cork',
    isSelectable: false,
  },
  {
    id: 'cmd_wood_wood_products',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7600',
    label: 'Wood, Wood Products',
    description: 'WOOD, WOOD PRODUCTS',
    aliases: ['*****WOOD MANUFACTURES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plywood_veneerwood',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7601',
    label: 'Plywood, Veneers',
    description: 'PLYWOOD, VENEERWOOD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_handles_made_of_wood',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7603',
    label: 'Wooden Handles',
    description: 'HANDLES MADE OF WOOD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sandalwood',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7606',
    label: 'Sandalwood',
    description: 'SANDALWOOD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wood_parquet_flooring',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7608',
    label: 'Wood Parquet Flooring',
    description: 'WOOD PARQUET FLOORING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_furniture_furniture_components_artifacts_curios_made_of_wood',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7625',
    label: 'Furniture Components, Artifacts, Curios (Wood)',
    description: 'FURNITURE,FURNITURE COMPONENTS,ARTIFACTS,CURIOS-MADE OF WOOD',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wooden_furniture_excl_objects_of_art',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7627',
    label: 'Wooden Furniture',
    description: 'WOODEN FURNITURE  EXCL OBJECTS OF ART',
    aliases: ['***** WOODEN FURNITURE  NOT ASSMBLED/EXCL OBJECTS OF ART'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wooden_cabinets_for_radios_tvs_phonographs_sewing_machines',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7630',
    label: 'Wooden Cabinets for Radios, TVs, Phonographs, Sewing Machines',
    description: 'WOODEN CABINETS FOR RADIOS,TVS,PHONOGRAPHS,SEWING MACHINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_household_woodenware',
    parentId: 'tact_s7600',
    level: 'ITEM',
    tactCode: '7685',
    label: 'Household Woodenware',
    description: 'HOUSEHOLD WOODENWARE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s7900',
    parentId: 'tact_g7',
    level: 'SUB_GROUP',
    range: [7900, 7999],
    label: 'Miscellaneous',
    description: 'Miscellaneous',
    isSelectable: false,
  },
  {
    id: 'cmd_paperrubber_mfrs_excl_newspapermagsbookbraille_type_eqpt',
    parentId: 'tact_s7900',
    level: 'ITEM',
    tactCode: '7900',
    label: 'Paper and Rubber Manufactures, Braille Equipment',
    description: 'PAPERRUBBER MFRS EXCL NEWSPAPERMAGSBOOKBRAILLE TYPE EQPT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g8',
    level: 'GROUP',
    range: [8000, 8999],
    label:
      'Scientific, professional and precision instruments, apparatus and supplies, musical instruments and accessories',
    description:
      'Scientific, professional and precision instruments, apparatus and supplies, musical instruments and accessories',
    isSelectable: false,
  },
  {
    id: 'tact_s8000',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8000, 8099],
    label:
      'Scientific, precision, dental and surgical instruments, apparatus and supplies; one generic item also including optical, photographic and projection equipment and supplies',
    description:
      'Scientific, precision, dental and surgical instruments, apparatus and supplies; one generic item also including optical, photographic and projection equipment and supplies',
    isSelectable: false,
  },
  {
    id: 'cmd_hotel_supplies_bed_sheetssoaptoysgolf_clubsbronzewar',
    parentId: 'tact_s8000',
    level: 'ITEM',
    tactCode: '8000',
    label: 'Hotel Supplies (Bed Sheets, Soap, Toys, Golf Clubs, Bronzeware)',
    description: 'HOTEL SUPPLIES BED SHEETSSOAPTOYSGOLF CLUBSBRONZEWAR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_scientific_precision_instruments_excl_watches_clocks',
    parentId: 'tact_s8000',
    level: 'ITEM',
    tactCode: '8001',
    label: 'Scientific and Precision Instruments',
    description: 'SCIENTIFIC/PRECISION INSTRUMENTS EXCL WATCHES/CLOCKS',
    aliases: ['SCI/PRECISION INSTRMTS,LAB EQPT(INCL SUPPL), WATCHES, CLOCKS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_scientific_precision_dental_surgical_inst_watches_clocks',
    parentId: 'tact_s8000',
    level: 'ITEM',
    tactCode: '8002',
    label: 'Scientific, Precision, Dental, Surgical Instruments, Watches, Clocks',
    description: 'SCIENTIFIC/PRECISION/DENTAL/SURGICAL INST.,WATCHES,CLOCKS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_instruments_for_dentistry_surgery_measuring_calibrating_x_ra',
    parentId: 'tact_s8000',
    level: 'ITEM',
    tactCode: '8003',
    label: 'Instruments for Dentistry, Surgery, Measuring, X-Ray Equipment, Analytical Scales',
    description: 'INSTRUMENTS FOR DENTISTRY, SURGERY, MEASURING, CALIBRATING, X-RAY EQUIPMENT, ANALYSING SCALES, MICRO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_geophysical_equipment',
    parentId: 'tact_s8000',
    level: 'ITEM',
    tactCode: '8005',
    label: 'Geophysical Equipment',
    description: 'GEOPHYSICAL EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8100',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8100, 8199],
    label: 'Dental, medical and surgical instruments, apparatus and supplies',
    description: 'Dental, medical and surgical instruments, apparatus and supplies',
    isSelectable: false,
  },
  {
    id: 'cmd_dental_surgical_medical_eqpt',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8100',
    label: 'Dental, Surgical and Medical Equipment and Instruments',
    description: 'DENTAL/SURGICAL/MEDICAL EQPT',
    aliases: ['DENTAL/SURGCL/MEDICL INSTRMTS INCL APPARATUS,SUPPLIES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_surgical_beds',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8103',
    label: 'Surgical Beds',
    description: 'SURGICAL BEDS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_injection_instruments_infusion_sets',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8112',
    label: 'Injection Instruments, Infusion Sets',
    description: 'INJECTION INSTRUMENTS, INFUSION SETS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hypodermic_surgical_needles',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8114',
    label: 'Hypodermic Needles (Surgical)',
    description: 'HYPODERMIC (SURGICAL) NEEDLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_drain_tubes_plastic_articles_rubber_articles_for_hospital_us',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8167',
    label: 'Drain Tubes, Plastic and Rubber Articles (Hospital Use)',
    description: 'DRAIN TUBES, PLASTIC ARTICLES, RUBBER ARTICLES - FOR HOSPITAL USE -',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_artificial_teeth_raw_mtrl_for_artfcl_teeth_mfd_feldspar',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8181',
    label: 'Artificial Teeth, Raw Materials for Artificial Teeth',
    description: 'ARTIFICIAL TEETH,RAW MTRL FOR ARTFCL TEETH, MFD FELDSPAR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_prostheses',
    parentId: 'tact_s8100',
    level: 'ITEM',
    tactCode: '8183',
    label: 'Prostheses',
    description: 'PROSTHESES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8200',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8200, 8299],
    label: 'Musical instruments; gramophones and gramophone records, recording tapes; music boxes; juke boxes',
    description: 'Musical instruments; gramophones and gramophone records, recording tapes; music boxes; juke boxes',
    isSelectable: false,
  },
  {
    id: 'cmd_musical_inst_phonographs_phonograph_records_cassette_tapes',
    parentId: 'tact_s8200',
    level: 'ITEM',
    tactCode: '8200',
    label: 'Musical Instruments, Phonographs, Records',
    description: 'MUSICAL INST,PHONOGRAPHS,PHONOGRAPH RECORDS,CASSETTE TAPES',
    aliases: [
      'MUSIC BOXES RECORDING TAPEPHONOGRAPH RECORDS INCL ACCESSOR',
      'MUSICAL INSTRUMENTS',
      'MUSICL INSTRMTS,MUSIC BOX,PHONOGRAPHS EXCL COMB WITH CLOCKS',
      'MUSICL INSTRMTS,PHONOGRAPH RCRDS,TAPE RECORDGS,SHEET MUSIC',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_phonograph_records_recording_tape',
    parentId: 'tact_s8200',
    level: 'ITEM',
    tactCode: '8280',
    label: 'Phonograph Records, Recording Tapes, Cassettes',
    description: 'PHONOGRAPH RECORDS,RECORDING TAPE',
    aliases: [
      'PHONOGRAPH RECORDS',
      'RECORDING TAPES,CASSETTES,PHONOGRAPH RECORDS,FILMS',
      'RECORDS, RECORDING TAPE, RECORDING WIRE',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_video_cassettes',
    parentId: 'tact_s8200',
    level: 'ITEM',
    tactCode: '8284',
    label: 'Video Cassettes',
    description: 'VIDEO CASSETTES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cassette_tapes',
    parentId: 'tact_s8200',
    level: 'ITEM',
    tactCode: '8285',
    label: 'Cassette Tapes',
    description: 'CASSETTE TAPES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8300',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8300, 8399],
    label:
      'Optical instruments, apparatus and supplies; photographic and projection equipment in some generic items, even including darkroom supplies',
    description:
      'Optical instruments, apparatus and supplies; photographic and projection equipment in some generic items, even including darkroom supplies',
    isSelectable: false,
  },
  {
    id: 'cmd_optical_instrumtsapparatusgoods_and_supplies',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8301',
    label: 'Optical Instruments, Apparatus and Supplies',
    description: 'OPTICAL INSTRUMTSAPPARATUSGOODS AND SUPPLIES',
    aliases: ['OPTICL INSTRUMTSAPPARATUSANDSUPPLIES CONSGND TO CERRO TO'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_contact_lenses',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8303',
    label: 'Contact Lenses',
    description: 'CONTACT LENSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_lenses_frames_sunglasses',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8370',
    label: 'Lenses, Frames, Sunglasses',
    description: 'LENSES FRAMES, SUNGLASSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_spectacle_frames',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8371',
    label: 'Spectacle Frames',
    description: 'SPECTACLE FRAMES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_spectacles_sunglasses_incl_accessories',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8372',
    label: 'Spectacles, Sunglasses (incl. Accessories)',
    description: 'SPECTACLES,SUNGLASSES - INCL ACCESSORIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sunglasses_goggles',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8380',
    label: 'Sunglasses, Goggles',
    description: 'SUNGLASSES,GOGGLES',
    aliases: ['SUNGLASSES,GOGGLES-INCL SPECTACLE CASES WHEN SHPD WITH MAIN'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cameras_spectacles_binoculars_microscopes_telescopes_photogr',
    parentId: 'tact_s8300',
    level: 'ITEM',
    tactCode: '8396',
    label: 'Cameras, Spectacles, Binoculars, Microscopes, Telescopes, Photographic and Projection Instruments',
    description: 'CAMERAS, SPECTACLES, BINOCULARS, MICROSCOPES, TELESCOPES, PHOTOGRAPHIC INSTRUCTIONS, PROJECTION INST',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8400',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8400, 8499],
    label:
      'Photographic and projection instruments, apparatus, supplies and accessories; films, exposed and unexposed; film sound tracks; sound equipment',
    description:
      'Photographic and projection instruments, apparatus, supplies and accessories; films, exposed and unexposed; film sound tracks; sound equipment',
    isSelectable: false,
  },
  {
    id: 'cmd_cameras_optical_goods_photographic_instruments_projectionins',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8401',
    label: 'Cameras, Photographic and Projection Equipment, Optical Goods',
    description: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC INSTRUMENTS, PROJECTIONINSTRUMENTS - INCLUDING APPARATUS, SUPPL',
    aliases: [
      'CAMERAS (INCLUDING SUPPLIES, ACCESSORIES)',
      'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES -EXCLUDIN',
      'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES - FILMS EX',
      'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES -, PUBLICI',
      'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES, ACCESSORI',
      'CAMERAS, PHOTOGRAPHIC EQUIPMENT (INCLUDING ACCESSORIES)',
      'CAMERAS, PHOTOGRAPHIC INSTRUMENTS, PROJECTION INSTRUMENTS - INCLUDING APPARATUS, SUPPLIES, ACCESSORI',
      'OPTICAL GOODSPHOTO AND PROJ EQPT',
      'PROJN EQPT INCL SUPP/ACCS EXCL NEWSREEL,35MM FILM EXPOSED',
      'PROJECTORS ASSEMBLED',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_camera_supplies_namely_flash_bulbs',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8406',
    label: 'Camera Supplies (Flash Bulbs)',
    description: 'CAMERA SUPPLIES NAMELY: FLASH BULBS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_films',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8420',
    label: 'Films',
    description: 'FILMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_entertainment_parts',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8424',
    label: 'Entertainment Parts',
    description: 'ENTERTAINMENT PARTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_motion_picture_films',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8425',
    label: 'Motion Picture Films',
    description: 'MOTION PICTURE FILMS',
    aliases: ['MOTION PICTURE FILMS EXCL NEWSFEEL FILMS', 'MOTION PICTURE FILMS (INCL ADVTG MTRLS)'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_overhead_track_trolley_carrier_andor_parts_for_mtrl_hndlg',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8427',
    label: 'Overhead Track Trolleys and Parts (Material Handling)',
    description: 'OVERHEAD TRACK TROLLEY CARRIER ANDOR PARTS FOR MTRL HNDLG',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_photo_films_paper_sensitized_unexpsd_incl_photo_chmcs_unsens',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8430',
    label: 'Photographic Films and Paper (Unexposed)',
    description: 'PHOTO FILMS/PAPER-SENSITIZED,UNEXPSD/INCL PHOTO CHMCS,UNSENS',
    aliases: ['FILMS  UNEXPOSED , SENSITIVE PAPER', 'UNEXPOSED FILMS, UNRECORDED TAPES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_developed_films_paper_prints',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8438',
    label: 'Developed Films, Photo Prints',
    description: 'DEVELOPED FILMS PAPER PRINTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_lenses',
    parentId: 'tact_s8400',
    level: 'ITEM',
    tactCode: '8440',
    label: 'Lenses',
    description: 'LENSES',
    aliases: ['LENSES, PRISMS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8500',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8500, 8599],
    label: 'Safety equipment and apparatus; one generic item including 8000-8199, 8300-8399 items',
    description: 'Safety equipment and apparatus; one generic item including 8000-8199, 8300-8399 items',
    isSelectable: false,
  },
  {
    id: 'cmd_cameras_instruments_for_dentistry_surgery_measuring_calibrat',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8549',
    label: 'Cameras, Instruments for Dentistry, Surgery, Measuring, Testing, Drawing',
    description: 'CAMERAS, INSTRUMENTS FOR DENTISTRY, SURGERY, MEASURING, CALIBRATING, TESTING, DRAWING, ANALYSING SCA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_safety_eqpt_and_apparatus_generic_item_of_dental_medi_optica',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8550',
    label: 'Safety Equipment and Apparatus',
    description: 'SAFETY EQPT AND APPARATUS,GENERIC ITEM OF DENTAL/MEDI/OPTICAL',
    aliases: ['SAFETY EQPT SAFETY APPARATUS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_automatic_control_for_measurg_regulatg_flow_temp_humidity_ti',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8551',
    label: 'Automatic Controls for Measuring or Regulating Flow, Temperature, Humidity, Time',
    description: 'AUTOMATIC CONTROL FOR MEASURG/REGULATG FLOW TEMP,HUMIDITY,TI',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electro_medical_apparatus_oper_tables_diathermic_machns_dent',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8553',
    label: 'Electro-Medical Apparatus, Operating Tables, Diathermic Machines',
    description: 'ELECTRO MEDICAL APPARATUS/OPER.TABLES/DIATHERMIC MACHNS/DENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_testing_eqpt_usd_to_chk_for_poisonous_fumes_in_mines',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8554',
    label: 'Mine Gas Testing Equipment',
    description: 'TESTING EQPT USD TO CHK FOR POISONOUS FUMES IN MINES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_process_control_equipment',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8557',
    label: 'Process Control Equipment',
    description: 'PROCESS CONTROL EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_medical_equipment_including_supplies',
    parentId: 'tact_s8500',
    level: 'ITEM',
    tactCode: '8575',
    label: 'Medical Equipment (incl. Supplies)',
    description: 'MEDICAL EQUIPMENT  INCLUDING SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8600',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8600, 8699],
    label: 'Scientific and laboratory equipment',
    description: 'Scientific and laboratory equipment',
    isSelectable: false,
  },
  {
    id: 'cmd_scientific_lab_instrmts_incl_apparatus_supplies',
    parentId: 'tact_s8600',
    level: 'ITEM',
    tactCode: '8600',
    label: 'Scientific and Laboratory Instruments, Apparatus, Supplies',
    description: 'SCIENTIFIC/LAB INSTRMTS INCL APPARATUS, SUPPLIES',
    aliases: ['SCIENTIFIC/LAB INSTRMTS INCL APPARATUS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_electric_medical_equipment',
    parentId: 'tact_s8600',
    level: 'ITEM',
    tactCode: '8603',
    label: 'Electric Medical Equipment',
    description: 'ELECTRIC MEDICAL EQUIPMENT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_analytical_measuring_instrmts_for_med_scientific_purposes',
    parentId: 'tact_s8600',
    level: 'ITEM',
    tactCode: '8610',
    label: 'Analytical Measuring Instruments for Medical/Scientific Purposes',
    description: 'ANALYTICAL MEASURING INSTRMTS FOR MED/SCIENTIFIC PURPOSES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8800',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8800, 8899],
    label: 'Surveying and engineering instruments',
    description: 'Surveying and engineering instruments',
    isSelectable: false,
  },
  {
    id: 'cmd_drawingdrafting_instruments',
    parentId: 'tact_s8800',
    level: 'ITEM',
    tactCode: '8820',
    label: 'Drawing and Drafting Instruments',
    description: 'DRAWINGDRAFTING INSTRUMENTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s8900',
    parentId: 'tact_g8',
    level: 'SUB_GROUP',
    range: [8900, 8999],
    label:
      'Miscellaneous precision instruments which could not be classified between 8000-8899, such as watches and clocks; hearing devices; taximeters; fuel flow meters',
    description:
      'Miscellaneous precision instruments which could not be classified between 8000-8899, such as watches and clocks; hearing devices; taximeters; fuel flow meters',
    isSelectable: false,
  },
  {
    id: 'cmd_instruments_for_regulating_measuring_testing',
    parentId: 'tact_s8900',
    level: 'ITEM',
    tactCode: '8909',
    label: 'Instruments for Regulating, Measuring, Testing',
    description: 'INSTRUMENTS FOR REGULATING, MEASURING, TESTING',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_watches_clocks',
    parentId: 'tact_s8900',
    level: 'ITEM',
    tactCode: '8911',
    label: 'Watches, Clocks',
    description: 'WATCHES, CLOCKS',
    aliases: ['WATCHES', 'CLOCKS', 'WRIST WATCHES  WITH BAND OR BRACELETS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_watch_parts',
    parentId: 'tact_s8900',
    level: 'ITEM',
    tactCode: '8912',
    label: 'Watch Parts',
    description: 'WATCH PARTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_g9',
    level: 'GROUP',
    range: [9000, 9999],
    label: 'Miscellaneous',
    description: 'Miscellaneous',
    isSelectable: false,
  },
  {
    id: 'tact_s9000',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9000, 9010],
    label: 'Jewellery, costume and imitation jewellery',
    description: 'Jewellery, costume and imitation jewellery',
    isSelectable: false,
  },
  {
    id: 'cmd_jewellery',
    parentId: 'tact_s9000',
    level: 'ITEM',
    label: 'Jewelry',
    description: 'JEWELLERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  { id: 'cmd_pearls', parentId: 'cmd_jewellery', level: 'ITEM', label: 'Pearls', description: 'PEARLS' }, // added from the TK list
  {
    id: 'cmd_imitation_jewelry_personal_ornaments_artificial_glass_stones',
    parentId: 'tact_s9000',
    level: 'ITEM',
    tactCode: '9001',
    label: 'Imitation Jewelry, Fancy Novelties, Personal Ornaments',
    description: 'IMITATION JEWELRY,PERSONAL ORNAMENTS,ARTIFICIAL/GLASS STONES',
    aliases: [
      'IMITATION JEWELRY,FANCY NOVELTIES EXCL CLOTH,WATCHES WHICH C',
      'IMITATION JEWELRY,NOVELTIES,ORNAMTS EXCL WATCHES,CLOCKS',
      'IMITATION JEWELRYFANCY NOVELTIESPERSONAL ORNAMENTS EX',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_valuable_cargo',
    parentId: 'tact_s9000',
    level: 'ITEM',
    tactCode: '9002',
    label: 'Valuable Cargo',
    description: 'VALUABLE CARGO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  }, // added from the TK list
  {
    id: 'tact_s9011',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9011, 9049],
    label: 'Codes 9011–9049',
    description: 'Codes 9011–9049',
    isSelectable: false,
  },
  {
    id: 'cmd_bone_andor_beads_jewellery',
    parentId: 'tact_s9011',
    level: 'ITEM',
    tactCode: '9015',
    label: 'Bone and Bead Jewelry',
    description: 'BONE ANDOR BEADS JEWELLERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9050',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9050, 9085],
    label: 'Packing materials, empty containers',
    description: 'Packing materials, empty containers',
    isSelectable: false,
  },
  {
    id: 'cmd_empty_containers',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9050',
    label: 'Empty Containers',
    description: 'EMPTY CONTAINERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_beer_kegs',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9051',
    label: 'Empty Beer Kegs',
    description: 'EMPTY BEER KEGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_ammonia_cylinders',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9052',
    label: 'Empty Ammonia Cylinders',
    description: 'EMPTY AMMONIA CYLINDERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_transparent_cellulosic_wrapping_material',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9056',
    label: 'Transparent Cellulose Wrapping Material',
    description: 'TRANSPARENT CELLULOSIC WRAPPING MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_cases_fr_clockssciprecision_instrmtsmade_of_leather',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9064',
    label: 'Empty Leather Cases (for Clocks, Instruments)',
    description: 'EMPTY CASES FR CLOCKSSCIPRECISION INSTRMTSMADE OF LEATHER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_boxes_of_cardboard_non_precious_metal_for_jewelry_watc',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9072',
    label: 'Empty Cardboard and Metal Boxes (for Jewelry, Watches)',
    description: 'EMPTY BOXES OF CARDBOARD,NON-PRECIOUS METAL/FOR JEWELRY,WATC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_metal_cans',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9078',
    label: 'Empty Metal Cans',
    description: 'EMPTY METAL CANS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_empty_watch_cases_metal',
    parentId: 'tact_s9050',
    level: 'ITEM',
    tactCode: '9085',
    label: 'Empty Metal Watch Cases',
    description: 'EMPTY WATCH CASES METAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9090',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9090, 9092],
    label: 'Human hair, human remains (uncremated), human eyes, tissues and dehydrated corneas',
    description: 'Human hair, human remains (uncremated), human eyes, tissues and dehydrated corneas',
    isSelectable: false,
  },
  {
    id: 'cmd_human_hair',
    parentId: 'tact_s9090',
    level: 'ITEM',
    tactCode: '9090',
    label: 'Human Hair',
    description: 'HUMAN HAIR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_human_remains_uncremated',
    parentId: 'tact_s9090',
    level: 'ITEM',
    tactCode: '9091',
    label: 'Human Remains, Uncremated',
    description: 'HUMAN REMAINS, UNCREMATED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_human_eyes_tissues_dehydrated_corneas',
    parentId: 'tact_s9090',
    level: 'ITEM',
    tactCode: '9092',
    label: 'Human Eyes, Tissues, Dehydrated Corneas',
    description: 'HUMAN EYES,TISSUES,DEHYDRATED CORNEAS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9093',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9093, 9099],
    label: 'Codes 9093–9099',
    description: 'Codes 9093–9099',
    isSelectable: false,
  },
  {
    id: 'cmd_cremated_human_remains_ashes',
    parentId: 'tact_s9093',
    level: 'ITEM',
    tactCode: '9095',
    label: 'Cremated Human Remains, Ashes',
    description: 'CREMATED HUMAN REMAINS, ASHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9100',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9100, 9105],
    label: 'Gift parcels',
    description: 'Gift parcels',
    isSelectable: false,
  },
  {
    id: 'cmd_gift_parcels_cont_food_clothg_shoes_medi_cosmetcs_toilet_ar',
    parentId: 'tact_s9100',
    level: 'ITEM',
    tactCode: '9104',
    label: 'Gift Parcels (Food, Clothing, Shoes, Medicines, Cosmetics)',
    description: 'GIFT PARCELS  CONT FOOD,CLOTHG,SHOES,MEDI,COSMETCS,TOILET AR',
    aliases: ['GIFT PARCELS  CONTAINING FOOD, CLOTHING', 'GIFT SHIPMENTS  CONTAINING FOOD,MEDICAL SUPPLIES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9106',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9106, 9199],
    label: 'Codes 9106–9199',
    description: 'Codes 9106–9199',
    isSelectable: false,
  },
  {
    id: 'cmd_foodstuffs_beverages_fr_consum_household_use_not_for_resale',
    parentId: 'tact_s9106',
    level: 'ITEM',
    tactCode: '9106',
    label: 'Foodstuffs, Beverages for Household Use (Not for Resale)',
    description: 'FOODSTUFFS,BEVERAGES FR CONSUM/HOUSEHOLD USE-NOT FOR RESALE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9200',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9200, 9299],
    label: 'Toys, athletic and sporting goods',
    description: 'Toys, athletic and sporting goods',
    isSelectable: false,
  },
  {
    id: 'cmd_athletic_equipment_sporting_goods',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9200',
    label: 'Athletic Equipment, Sporting Goods',
    description: 'ATHLETIC EQUIPMENT,SPORTING GOODS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_camping_equipment',
    parentId: 'cmd_athletic_equipment_sporting_goods',
    level: 'ITEM',
    label: 'Camping Equipment',
    description: 'CAMPING EQUIPMENT',
  }, // added from the TK list
  {
    id: 'cmd_fitness_equipment',
    parentId: 'cmd_athletic_equipment_sporting_goods',
    level: 'ITEM',
    label: 'Fitness Equipment',
    description: 'FITNESS EQUIPMENT',
  }, // added from the TK list
  {
    id: 'cmd_toys_games_athletic_goods_sporting_goods',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9202',
    label: 'Toys, Games, Athletic and Sporting Goods',
    description: 'TOYS,GAMES,ATHLETIC GOODS,SPORTING GOODS',
    aliases: [
      'TOYS EXCL TOY TRAINS UMBRELLASATHLETIC EQPT MADE PRIMARILY',
      'TOYS,GAMES,ATHELTIC GOODS INCL SPORTG/HUNTG GUNS EXCL WATCHE',
      'TOYS GAMES',
      'TOYS,GAMES,ATHLETIC GOODS,SPORTG GOODS EXCL WEARING APPAREL',
      'TOYS GAMES-INCL PACKAGING MTRL WHEN HPD WITH MAIN ARTCL',
      'TOYS,GAMES,SPORTING EQUIPMENT',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_wooden_toys',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9204',
    label: 'Wooden Toys',
    description: 'WOODEN TOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_saddles_riding_eqpt',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9208',
    label: 'Saddles, Riding Equipment',
    description: 'SADDLES, RIDING EQPT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_manufacturing_materials_for_plush_toys',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9209',
    label: 'Plush Toy Manufacturing Materials',
    description: 'MANUFACTURING MATERIALS FOR PLUSH TOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_footballs',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9211',
    label: 'Footballs',
    description: 'FOOTBALLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_baseballs',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9213',
    label: 'Baseballs',
    description: 'BASEBALLS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_model_electric_railways_electric_toys',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9214',
    label: 'Model Electric Railways, Electric Toys',
    description: 'MODEL ELECTRIC RAILWAYS ELECTRIC TOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_scale_models_ships',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9215',
    label: 'Scale Models of Ships',
    description: 'SCALE MODELS  SHIPS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_skateboard_incl_helmets_protective_pads',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9229',
    label: 'Skateboards (incl. Helmets, Protective Pads)',
    description: 'SKATEBOARD  INCL HELMETS, PROTECTIVE PADS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_canoes',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9231',
    label: 'Canoes',
    description: 'CANOES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_roller_skate_wheels',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9235',
    label: 'Roller Skate Wheels',
    description: 'ROLLER SKATE WHEELS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fishing_equipment_including_accessories',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9241',
    label: 'Fishing Equipment (incl. Accessories)',
    description: 'FISHING EQUIPMENT  INCLUDING ACCESSORIES',
    aliases: ['FISHING EQPT NAMELY:LINESHOOKSLURESREELS AND RODS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_golfbags',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9247',
    label: 'Golf Bags',
    description: 'GOLFBAGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_skis_skipoles_ski_bindings',
    parentId: 'tact_s9200',
    level: 'ITEM',
    tactCode: '9274',
    label: 'Skis, Ski Poles, Ski Bindings',
    description: 'SKIS,SKIPOLES,SKI BINDINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9300',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9300, 9350],
    label: 'Firearms and ammunitions, fireworks',
    description: 'Firearms and ammunitions, fireworks',
    isSelectable: false,
  },
  {
    id: 'cmd_guns_firearms_ammunition',
    parentId: 'tact_s9300',
    level: 'ITEM',
    tactCode: '9300',
    label: 'Guns, Firearms, Ammunition',
    description: 'GUNS, FIREARMS, AMMUNITION',
    aliases: ['GUNS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_sporting_guns_hunting_guns',
    parentId: 'tact_s9300',
    level: 'ITEM',
    tactCode: '9309',
    label: 'Sporting and Hunting Guns',
    description: 'SPORTING GUNS HUNTING GUNS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_munitions_of_war',
    parentId: 'tact_s9300',
    level: 'ITEM',
    tactCode: '9310',
    label: 'Munitions of War',
    description: 'MUNITIONS OF WAR',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_ammunition_for_sporting_guns',
    parentId: 'tact_s9300',
    level: 'ITEM',
    tactCode: '9311',
    label: 'Ammunition for Sporting Guns',
    description: 'AMMUNITION FOR SPORTING GUNS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9400',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9400, 9473],
    label: 'Office supplies, excluding machinery',
    description: 'Office supplies, excluding machinery',
    isSelectable: false,
  },
  {
    id: 'cmd_pens_pencils',
    parentId: 'tact_s9400',
    level: 'ITEM',
    tactCode: '9470',
    label: 'Pens, Pencils (incl. Holders, Cases, Display Material)',
    description: 'PENS,PENCILS',
    aliases: ['PENS, PENCILS  INCL DISPLAY MATERIAL', 'PENS, PENCILS  INCL STANDS, HOLDERS, CASES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_display_material',
    parentId: 'tact_s9400',
    level: 'ITEM',
    tactCode: '9472',
    label: 'Display Material',
    description: 'DISPLAY MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9500',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9500, 9599],
    label: 'Handicraft products, souvenirs, paintings, buttons, brushes, candles, fire extinguishers (empty)',
    description: 'Handicraft products, souvenirs, paintings, buttons, brushes, candles, fire extinguishers (empty)',
    isSelectable: false,
  },
  {
    id: 'cmd_paintings_etchings_engravings',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9500',
    label: 'Paintings, Etchings, Engravings',
    description: 'PAINTINGS, ETCHINGS,ENGRAVINGS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_antiques',
    parentId: 'cmd_paintings_etchings_engravings',
    level: 'ITEM',
    label: 'Antiques',
    description: 'ANTIQUES',
  }, // added from the TK list
  {
    id: 'cmd_art_and_exhibition_pieces',
    parentId: 'cmd_paintings_etchings_engravings',
    level: 'ITEM',
    label: 'Art and Exhibition Pieces',
    description: 'ART AND EXHIBITION PIECES',
  }, // added from the TK list
  {
    id: 'cmd_souvenirs_incl_leather_goods_silverware_wood_copper_mfrs_bon',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9505',
    label: 'Souvenirs (Leather, Silverware, Wood, Copper, Bone)',
    description: 'SOUVENIRS INCL LEATHER GOODS,SILVERWARE,WOOD/COPPER MFRS,BON',
    aliases: ['SOUVENIRS LEATHER/WOOD/COPPER/IVORY/BONE/HORN GDS.'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_gift_items',
    parentId: 'cmd_souvenirs_incl_leather_goods_silverware_wood_copper_mfrs_bon',
    level: 'ITEM',
    label: 'Gift Items',
    description: 'GIFT ITEMS',
  }, // added from the TK list
  {
    id: 'cmd_curios_carved_wooden_bone_articles_horn_articles',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9506',
    label: 'Carved Curios (Wood, Bone, Horn)',
    description: 'CURIOS CARVED/WOODEN ,BONE ARTICLES,HORN ARTICLES',
    aliases: ['CURIOS CARVED/WOODEN ,BONE/HORN ARTCLS EXCL IVORY ARTCLS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_damascened_articles_reproductions_of_old_steel_swords',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9514',
    label: 'Damascened Articles, Reproduction Steel Swords',
    description: 'DAMASCENED ARTICLES REPRODUCTIONS OF OLD STEEL SWORDS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_buttons_buckles',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9526',
    label: 'Buttons, Buckles',
    description: 'BUTTONS, BUCKLES',
    aliases: ['BUTTONS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_handicrafts',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9527',
    label: 'Handicrafts',
    description: 'HANDICRAFTS',
    aliases: [
      'HANDICRAFT ENTIRELY OF ONYX',
      'HANDICRAFT PRODS MADE OF METAL,WOOD,STRAW,LEATHER,EARTH,ONYX',
      'HANDICRAFT PRODS MADE OF TEXTILES,METAL,LEATHER EXCL DIAMOND',
      'HANDICRAFT PRODS MADE OF TEXTLS,METAL,WOOD,CORK,STRAW,WICKER',
      'HANDICRAFT PDCTS TXTLS,METAL,WOOD,CORK,STRAW,WICKER,LEATHER,',
      'HANDICRAFT PRODUCTS MADE OF TEXTILES,METAL,WOOD,STRAW,LEATHE',
      'HANDICRAFT ARTICLES MADE OF WOODMETALCORKSTRAWBAMBOO',
      'HANDICRAFT PRODS TXTLS,METAL,WOOD,CLAY EXCL WEARG AAPAREL',
      'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,BAMBOO,WICKER,PAP',
      'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,CLAY,HANDLOOM TXT',
      'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,STONE,BAMBOO,WICK',
      'HANDICRAFTS(MADE OF METAL,WOOD,CORK,STRAW,BAMBOO,WICKER,CLAY',
      'HANDICRAFTS METAL,WOOD,CORK,STRAW EXCL TXTL,LEATHER,DIAMONDS',
      'HANDICRAFTS MADE OF SISAL,FIBRE,BANANA FIBRE,BAMBOO,LEATHER,',
      'HANDICRAFTSHANDLOOM TEXTILESSILVERBRASSCOPPERIRONWOOD',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fasteners_zippers_buckles_handles_handbag_frames',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9529',
    label: 'Fasteners, Zippers, Buckles, Handles, Handbags',
    description: 'FASTENERS,ZIPPERS,BUCKLES,HANDLES,HANDBAG FRAMES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_watch_bands',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9531',
    label: 'Watch Bands',
    description: 'WATCH BANDS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_christmas_tree_ornaments',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9532',
    label: 'Christmas Tree Ornaments',
    description: 'CHRISTMAS TREE ORNAMENTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cake_decorations_made_of_sugar_plastic',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9533',
    label: 'Cake Decorations (Sugar, Plastic)',
    description: 'CAKE DECORATIONS  MADE OF SUGAR,PLASTIC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_brushes',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9540',
    label: 'Brushes',
    description: 'BRUSHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_paint_brushes_paint_rollers',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9541',
    label: 'Paint Brushes, Paint Rollers',
    description: 'PAINT BRUSHES PAINT ROLLERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_brushware_including_accessories',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9544',
    label: 'Brushware (incl. Accessories)',
    description: 'BRUSHWARE INCLUDING ACCESSORIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_handbags',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9552',
    label: 'Handbags, Purses, Wallets',
    description: 'HANDBAGS',
    aliases: [
      'BAGSHANDBAGSBILLFOLDSPURSESWALLETS MADE OF CANVASLEATHE',
      'HANDBAGS GLOVES',
      'HANDBAGS SHOES INCLUDING DISPLAY MATERIAL',
      'HANDBAGS, BELTS - MADE OF LEATHER',
      'HANDBAGS, POCKETBOOKS, WALLETS, PURSES',
      'HANDBAGS,PURSES,WALLETS INCL ACCESSORIES OTHER THAN LEATHER',
      'HANDBAGSPURSESWALLETS OTHER THAN LEATHER',
      'PURSES,HANDBAGS,WALLETS,BRIEFCASES,SCHOOL BAGS-OF LEATHER',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_suitcases_overnight_bags_briefcases_attache_cases_handbags_w',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9558',
    label: 'Suitcases, Overnight Bags, Briefcases, Attache Cases',
    description: 'SUITCASES,OVERNIGHT BAGS,BRIEFCASES,ATTACHE CASES,HANDBAGS,W',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_religious_articles_excl_printed_matter_pictures_vestments',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9560',
    label: 'Religious Articles (excl. Printed Matter, Pictures, Vestments)',
    description: 'RELIGIOUS ARTICLES EXCL.PRINTED MATTER PICTURES,VESTMENTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_advtg_artcles_display_eqpt_not_for_sale',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9576',
    label: 'Advertising Articles, Display Equipment (Not for Sale)',
    description: 'ADVTG ARTCLES, DISPLAY EQPT - NOT FOR SALE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_fire_extinguishers_empty',
    parentId: 'tact_s9500',
    level: 'ITEM',
    tactCode: '9592',
    label: 'Fire Extinguishers (Empty)',
    description: 'FIRE EXTINGUISHERS EMPTY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9600',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9600, 9699],
    label: 'Tobacco and tobacco related products, batteries for chicken farms, animal identification ear tags',
    description: 'Tobacco and tobacco related products, batteries for chicken farms, animal identification ear tags',
    isSelectable: false,
  },
  {
    id: 'cmd_tobacco_cigars_cigarettes_pipes_humidors_cigar_cigarette_hol',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9602',
    label: 'Tobacco, Cigars, Cigarettes, Pipes, Humidors, Cigar/Cigarette Holders',
    description: 'TOBACCO,CIGARS,CIGARETTES,PIPES,HUMIDORS,CIGAR/CIGARETTE HOL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_indian_beedies',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9613',
    label: 'Indian Beedies',
    description: 'INDIAN BEEDIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_pipes_smoking',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9615',
    label: "Smoking Pipes, Smokers' Articles",
    description: 'PIPES  SMOKING',
    aliases: ['PIPES AND SMOKERS ARTICLES'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_accessories',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9623',
    label: 'Accessories',
    description: 'ACCESSORIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_accessories_high_value',
    parentId: 'cmd_accessories',
    level: 'ITEM',
    label: 'Accessories (High Value)',
    description: 'ACCESSORIES (HIGH VALUE)',
  }, // added from the TK list
  {
    id: 'cmd_lighters',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9625',
    label: 'Lighters',
    description: 'LIGHTERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_feeders_waterers_egg_trays_egg_graders_chicken_crates_parts',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9660',
    label: 'Feeders, Waterers, Egg Trays, Egg Graders, Chicken Crates, Parts',
    description: 'FEEDERS,WATERERS,EGG TRAYS,EGG GRADERS,CHICKEN CRATES,PARTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_animal_identification_ear_tags_marking_dyes',
    parentId: 'tact_s9600',
    level: 'ITEM',
    tactCode: '9661',
    label: 'Animal Identification Ear Tags, Marking Dyes',
    description: 'ANIMAL IDENTIFICATION EAR TAGS MARKING DYES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'tact_s9700',
    parentId: 'tact_g9',
    level: 'SUB_GROUP',
    range: [9700, 9999],
    label: 'Miscellaneous items',
    description: 'Miscellaneous items',
    isSelectable: false,
  },
  {
    id: 'cmd_consolidation',
    parentId: 'tact_s9700',
    level: 'ITEM',
    label: 'Consolidation',
    description: 'CONSOLIDATION',
    isGeneric: true,
    sortPosition: 7,
    allowedCargo: CARGO_PROFILES.GENERAL_AND_PERISHABLE,
  },
  {
    id: 'cmd_general_goods',
    parentId: 'tact_s9700',
    level: 'ITEM',
    label: 'General Goods',
    description: 'GENERAL GOODS',
    isGeneric: true,
    sortPosition: 1,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_military_airmail',
    parentId: 'tact_s9700',
    level: 'ITEM',
    label: 'Military Airmail',
    description: 'Military AirMail',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_military_sal_mail',
    parentId: 'tact_s9700',
    level: 'ITEM',
    label: 'Military SAL Mail',
    description: 'Military SAL Mail',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_garments_yarn_thread_fibres_textiles_wearing_apparel_leather',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9703',
    label: 'Garments, Yarn, Thread, Fibers, Textiles, Leather',
    description: 'GARMENTS, YARN, THREAD, FIBRES, TEXTILES, WEARING APPAREL, LEATHER, LEATHER MANUFACTURES',
    aliases: ['GARMENTS, YARN, THREAD, FIBRES, TEXTILES, WEARING APPAREL, LEATHER, LEATHER MANUFACTURES EXCLUDING F'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_data_processg_eqpt_ofc_machnry_incl_accessories_supplies',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9705',
    label: 'Data Processing Equipment, Office Machinery (incl. Accessories, Supplies)',
    description: 'DATA PROCESSG EQPT,OFC MACHNRY INCL ACCESSORIES,SUPPLIES',
    aliases: [
      'DATA PROCESSG EQPT,OFC MACHNRY-INCL ACCESSORIES,SUPPLIES-ELE',
      'DATA PROCSSG EQPT,OFC MACHNRY,INCL ACCESSORIES,SUPPLIES',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elec_eqpt_appl_tapes_cassettes_phonograph_records_wire',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9706',
    label: 'Electrical Equipment, Appliances, Tapes, Cassettes, Records',
    description: 'ELEC EQPT/APPL.,TAPES,CASSETTES,PHONOGRAPH RECORDS,WIRE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_tools_including_razor_blades_cutlery',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9707',
    label: 'Tools (incl. Razor Blades, Cutlery)',
    description: 'TOOLS INCLUDING RAZOR BLADES , CUTLERY',
    aliases: ['TOOLS INCLUDING RAZOR BLADES CUTLERY'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_printed_matter_paper_including_photographic_sensitized_paper',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9710',
    label: 'Printed Matter',
    description: 'PRINTED MATTER,PAPER-INCLUDING PHOTOGRAPHIC/SENSITIZED PAPER',
    aliases: [
      'PRINTER MATTER',
      'PRINTED MATTER........',
      'PRINTED MATTER',
      'PRINTED MATTER  EXCL NEWSPAPERS',
      'PRINTED MATTER  EXCLUDING NEWSPAPERS',
      'PRINTED MATTER,PAPER-INCL PHOTO SENSITIZED PAPER EXCL NEWSPAPER',
      'PRINTED MATTER, PAPER-INCL PHOTOGRAPHIC,SENSITIZED PAPER-EXC',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_documents',
    parentId: 'cmd_printed_matter_paper_including_photographic_sensitized_paper',
    level: 'ITEM',
    label: 'Documents',
    description: 'DOCUMENTS',
  }, // added from the TK list
  {
    id: 'cmd_passports',
    parentId: 'cmd_printed_matter_paper_including_photographic_sensitized_paper',
    level: 'ITEM',
    label: 'Passports',
    description: 'PASSPORTS',
  }, // added from the TK list
  {
    id: 'cmd_instrumts_eqpt_designed_for_sci_surgcl_lab_indl_radio_or_tv',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9719',
    label: 'Scientific, Surgical and Laboratory Instruments and Equipment',
    description: 'INSTRUMTS,EQPT-DESIGNED FOR SCI SURGCL LAB,INDL RADIO OR TV',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_paper_paper_products',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9721',
    label: 'Paper, Paper Products',
    description: 'PAPER, PAPER PRODUCTS',
    aliases: ['PAPER PRODUCTS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metals_minerals_excl_gold_silver_platinum_alloys',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9722',
    label: 'Metals, Minerals (excl. Gold, Silver, Platinum, Alloys)',
    description: 'METALS,MINERALS-EXCL GOLD,SILVER,PLATINUM,ALLOYS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plastic_articles_wood_wood_products',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9723',
    label: 'Plastic Articles, Wood, Wood Products',
    description: 'PLASTIC ARTICLES,WOOD,WOOD PRODUCTS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_foodstuffs_chemicals_elec_eqpt_toilet_articles_plastic_paper',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9732',
    label: 'Foodstuffs, Chemicals, Electrical Equipment, Toilet Articles, Plastic, Paper',
    description: 'FOODSTUFFS,CHEMICALS,ELEC EQPT,TOILET ARTICLES,PLASTIC,PAPER',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_textiles_apparel_machinery_cutler_elec_eqpt_surface_vehicles',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9733',
    label: 'Textiles, Apparel, Machinery, Cutlery, Electrical Equipment, Surface Vehicles',
    description: 'TEXTILES,APPAREL,MACHINERY,CUTLER,ELEC EQPT,SURFACE VEHICLES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_miscellaneous_items',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9735',
    label: 'Miscellaneous Items',
    description: 'MISCELLANEOUS ITEMS',
    aliases: ['MISCELLANEOUS ITEMS .......', 'MISCELLANEOUS ITEMS........'],
    isGeneric: true,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_construction_eqpt_building_material',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9743',
    label: 'Construction Equipment, Building Material',
    description: 'CONSTRUCTION EQPT, BUILDING MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_doors',
    parentId: 'cmd_construction_eqpt_building_material',
    level: 'ITEM',
    label: 'Doors',
    description: 'DOORS',
  }, // added from the TK list
  {
    id: 'cmd_stationery_office_supplies',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9744',
    label: 'Stationery, Office Supplies',
    description: 'STATIONERY/OFFICE SUPPLIES',
    aliases: ['OFFICE SUPPLIES  EXCLUDING MACHINERY', 'STATIONERY'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hair_wigs_radios_tvs_miscellaneous_items',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9751',
    label: 'Hair, Wigs, Radios, Televisions (Miscellaneous)',
    description: 'HAIR,WIGS,RADIOS,TVS  MISCELLANEOUS ITEMS.....',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_textile_fibresandmfrs_metalandmfrs_non_metlc_misc_items',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9752',
    label: 'Textile Fibers and Manufactures, Metals and Manufactures (Miscellaneous)',
    description: 'TEXTILE FIBRESANDMFRS,METALANDMFRS,NON-METLC  MISC.ITEMS.......',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_abrasivesadhesive_artclsrubberplastic_miscitems',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9790',
    label: 'Abrasives, Adhesive Articles, Rubber and Plastic Items',
    description: 'ABRASIVESADHESIVE ARTCLSRUBBERPLASTIC MISCITEMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_outsized_cargo',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9794',
    label: 'Outsized Cargo',
    description: 'OUTSIZED CARGO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_abrasivesammunitioncigars_misc_items',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9795',
    label: 'Abrasives, Ammunition, Cigars (Miscellaneous)',
    description: 'ABRASIVESAMMUNITIONCIGARS MISC ITEMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_adhesives_small_arms_etchings_miscellaneous_items',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9796',
    label: 'Adhesives, Small Arms, Etchings, Miscellaneous Items',
    description: 'ADHESIVES,SMALL ARMS,ETCHINGS  MISCELLANEOUS ITEMS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_household_electronic_igniters',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9900',
    label: 'Household Electronic Igniters',
    description: 'HOUSEHOLD ELECTRONIC IGNITERS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_furniture',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9910',
    label: 'Furniture',
    description: 'FURNITURE',
    aliases: ['FURNITURE  EXCL OBJECTS OF ART', 'FURNITURE  EXCL PAINTINGS/ENGRAVINGS/OBJECTS OF ART'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_furniture_safes_excl_paintingsengravingsantiquitiesobjec',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9913',
    label: 'Furniture, Safes',
    description: 'FURNITURE SAFES EXCL PAINTINGSENGRAVINGSANTIQUITIESOBJEC',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_metal_safes',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9915',
    label: 'Metal Safes',
    description: 'METAL SAFES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_indl_prods_textilesfibresandmfrsmetalsdrugs_frm_are3_trans',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9922',
    label: 'Industrial Products (Textiles, Fibers, Metals, Drugs)',
    description: 'INDL PRODS:TEXTILESFIBRESANDMFRSMETALSDRUGS FRM ARE3 TRANS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_nuclear_reactor_parts_incl_heat_exchangrs_control_rods_valve',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9930',
    label: 'Nuclear Reactor Parts (incl. Heat Exchangers, Control Rods, Valves)',
    description: 'NUCLEAR REACTOR PARTS INCL HEAT EXCHANGRS,CONTROL RODS,VALVE',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_nuclear_reactor_fuel_containers_fuel_raw_mtrl_cntrs_empty',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9935',
    label: 'Nuclear Reactor Fuel Containers (Empty)',
    description: 'NUCLEAR REACTOR FUEL CONTAINERS,FUEL RAW MTRL CNTRS EMPTY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_government_owned_stores_including_supplies',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9942',
    label: 'Government-Owned Stores (incl. Supplies)',
    description: 'GOVERNMENT OWNED STORES  INCLUDING SUPPLIES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_diplomatic_cargo_diplomatic_mail',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9945',
    label: 'Diplomatic Cargo, Diplomatic Mail',
    description: 'DIPLOMATIC CARGO, DIPLOMATIC MAIL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_australian_govt_diplomatic_pouches',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9946',
    label: 'Australian Government Diplomatic Pouches',
    description: 'AUSTRALIAN GOVT DIPLOMATIC POUCHES',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_us_diplomatic_pouches_moving_on_us_govt_b_l',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9947',
    label: 'US Diplomatic Pouches, Moving on US Government Bill of Lading',
    description: 'US DIPLOMATIC POUCHES, MOVING ON US GOVT. B/L',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_military_storesimpedimenta',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9948',
    label: 'Military Stores, Impedimenta',
    description: 'MILITARY STORESIMPEDIMENTA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_prefabricated_houses_related_outfit',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9955',
    label: 'Prefabricated Houses and Fittings',
    description: 'PREFABRICATED HOUSES RELATED OUTFIT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_shpmts_of_ici_its_subsidiaries_associate_cos',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9960',
    label: 'ICI Group Shipments (incl. Subsidiaries, Associate Companies)',
    description: 'SHPMTS OF ICI, ITS SUBSIDIARIES & ASSOCIATE COS.',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_haberdashery',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9961',
    label: 'Haberdashery',
    description: 'HABERDASHERY',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_elastic_cord_with_metal_ends',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9964',
    label: 'Elastic Cord with Metal Ends',
    description: 'ELASTIC CORD WITH METAL ENDS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_plant_hangers_knitted',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9966',
    label: 'Knitted Plant Hangers',
    description: 'PLANT HANGERS KNITTED',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bldg_eqpt_engg_construction_eqpt',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9970',
    label: 'Building, Engineering and Construction Equipment',
    description: 'BLDG EQPT ENGG CONSTRUCTION EQPT',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_hosp_supplies_clothg_dressg_medicine_lab_tools_pharma_medica',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9971',
    label: 'Hospital Supplies, Clothing, Dressings, Medicines, Lab Tools, Pharmaceuticals',
    description: 'HOSP SUPPLIES CLOTHG,DRESSG,MEDICINE,LAB TOOLS,PHARMA,MEDICA',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_expedition_material',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9974',
    label: 'Expedition Material',
    description: 'EXPEDITION MATERIAL',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_goods_consigned_to_ambassadors_legations_consuls_or_other_of',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9981',
    label: 'Goods Consigned to Ambassadors, Legations, Consuls, or Other Officials',
    description: 'GOODS CONSIGNED TO AMBASSADORS,LEGATIONS,CONSULS OR OTHER OF',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_household_furn_kitchen_eqpt_used_excl_per_effects',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9984',
    label: 'Household Furniture, Kitchen Equipment (Used)',
    description: 'HOUSEHOLD FURN,KITCHEN EQPT USED  EXCL PER.EFFECTS',
    aliases: ['HOUSEHOLD FURNITUREKITCHEN EQPTCUTLER EXCL PAINTINGSOBJEC'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_unaccompanied_baggage_personal_effects_not_fore_resale',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9986',
    label: 'Unaccompanied Baggage, Personal Effects (Not for Resale)',
    description: 'UNACCOMPANIED BAGGAGE, PERSONAL EFFECTS  NOT FORE RESALE',
    aliases: ['ARTICLES OF BAGGAGE USEDOWNED BY INDIVCONSIGNED BY TRANSPO'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_cargo_aircraft_only_cargo',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9990',
    label: 'Cargo Aircraft Only (CAO) Cargo',
    description: 'CARGO AIRCRAFT ONLY CARGO',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_bldg_mtrl_display_mtrl_for_exhibition_trade_fairs',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9991',
    label: 'Building and Display Material for Exhibitions, Trade Fairs',
    description: 'BLDG MTRL,DISPLAY MTRL-FOR EXHIBITION/TRADE FAIRS',
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_dgr_cargo',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9992',
    label: 'Dangerous Goods',
    description: 'DGR CARGO',
    aliases: ['DANGEROUS GOODS'],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_household_goods_used_wearing_apparel_cosmetics_used',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9994',
    label: 'Used Household Goods, Wearing Apparel, Cosmetics',
    description: 'HOUSEHOLD GOODS USED ,WEARING APPAREL/COSMETICS  USED',
    aliases: [
      'HOUSEHOLD/WEARING GD,COSMETICS,TOILETRIES(USED,NO RESALE)',
      'WEARING APPAREL,COSEMTICS,TOILET ARTCLS USED NOT FOR RESALE',
    ],
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_household_goods_personal_effects',
    parentId: 'tact_s9700',
    level: 'ITEM',
    tactCode: '9998',
    label: 'Household Goods, Personal Effects',
    description: 'HOUSEHOLD GOODS, PERSONAL EFFECTS',
    aliases: [
      'PERSONAL EFFECTSHOUSEHOLD EFFECTS OF GOVT OFFICERS TRAVELLI',
      'PERSONAL EFF./HOUSEHOLD GDS. -NOT FOR RESALE',
    ],
    sortPosition: 12,
    allowedCargo: CARGO_PROFILES.GENERAL,
  },
  {
    id: 'cmd_used_goods',
    parentId: 'cmd_household_goods_personal_effects',
    level: 'ITEM',
    label: 'Used Goods',
    description: 'USED GOODS',
  }, // added from the TK list
];
